import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleMap, useLoadScript, InfoWindow, Polygon, Polyline, OverlayView, useGoogleMap } from '@react-google-maps/api';
import { 
  Paper, Typography, Box, Alert, CircularProgress, Chip, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, 
  TextField, FormControl, InputLabel, FormControlLabel, Checkbox,
  RadioGroup, Radio,
  Select, Menu, MenuItem, Card, CardContent,
  List, ListItem, ListItemButton, ListItemText, Collapse, useTheme,
  Autocomplete, Tooltip, Grid, Link, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSnackbar } from 'notistack';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import TerminalIcon from '@mui/icons-material/Terminal';
import DevicesIcon from '@mui/icons-material/Devices';
import HubIcon from '@mui/icons-material/Hub';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MapIcon from '@mui/icons-material/Map';
import LayersIcon from '@mui/icons-material/Layers';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SpeedIcon from '@mui/icons-material/Speed';
import HearingIcon from '@mui/icons-material/Hearing';
import RadarIcon from '@mui/icons-material/Radar';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { api } from '../services/api';
import NeighborScanBatchField, { DEFAULT_NEIGHBOR_SCAN_BATCH, describeNeighborScanBatch } from './NeighborScanBatchField';
import { auth } from '../services/auth';
import {
  deviceSupportsApFailsafe,
  deviceNotes,
  deviceHasNotes,
  truncateNote,
  apFailsafeRadioChoices,
} from '../utils/deviceLampActions';
import DeviceNotesEditor from './DeviceNotesEditor';
import { AcceptAllClientsConfirmDialog } from './ApFailsafeButton';
import { formatSsidProfileOptionLabel } from '../utils/ssidProfileLabel';
import { countNeighborsScanFailures } from '../utils/neighborsScanUtils';
import {
  getDeviceDisplayName,
  getEffectiveGps,
  getReportedDeviceGps,
  gpsSourceLabel,
  hasManualGpsOverride,
  canLockCurrentDeviceGps,
} from '../utils/deviceDisplayUtils';
import { isLastKnownGoodEligible } from '../utils/lkgUtils';
import { persistOptimizeScope } from '../utils/optimizeScopeUtils';
import {
  extractCapSerialsFromSelection,
  extractFocusSerialsFromSelection,
  persistIperfCapScope,
} from '../utils/iperfScopeUtils';
import {
  extractSegmentCohortSerials,
  selectionHasCap,
  persistSegmentCohortScope,
} from '../utils/segmentScopeUtils';
import LastKnownGoodSelectionPanel from './LastKnownGoodSelectionPanel';
import DeviceLocationSelectionPanel from './DeviceLocationSelectionPanel';
import DeviceNicknameSelectionPanel from './DeviceNicknameSelectionPanel';
import DeviceMapGroupDialog from './DeviceMapGroupDialog';
import BackgroundStaScanBulkPanel from './BackgroundStaScanBulkPanel';
import { ReportsConfigFields } from './ReportsSection';
import {
  EMPTY_BG_SCAN_BULK_FORM,
  buildBackgroundStaScanBulkPatch,
} from '../utils/bgScanPatchUtils';
import {
  buildConnectivityChannelGroups,
  defaultChannelsForBandAndHtmode,
  formatTreemgmtChannelList,
} from '../utils/treemgmtChannelUtils';
import { getBackhaulConnectionRssi } from '../utils/backhaulRssiUtils';
import { isTmCandidateSelected, tmSelectedRankId } from '../utils/tmCandidates';
import { device5GHzRadioUsedForPublicWifi, collectUnassignedSegmentSerials, NO_SEGMENT_OVERLAY_ID } from '../utils/backhaulMapFilterUtils';
import {
  classifyConfigPushResults,
  formatConfigPushSummaryMessage,
} from '../utils/configPushResultUtils';
import {
  buildSubtreeSegmentLists,
  countBlockedAPSegmentSlots,
  hasConnectedChild,
} from '../utils/subtreeSegmentUtils';
import {
  nodeDevicesFromHeardSet,
  resolveCapDevicesForHeardSet,
  rssiSignalColor,
  buildHeardByLinksFromResponse,
} from '../utils/heardByMapUtils';
import {
  MapSelectionDialogBody,
  MapSelectionDialogTabs,
  MapSelectionDialogTitle,
  MapSelectionSummaryStrip,
  mapSelectionDialogPaperProps,
} from './mapSelectionDialogUi';
import { MAP_UI, statusDotSx } from '../theme/mapViewUi';
import {
  CAP_BADGE_SIZE,
  CAP_MARKER_CENTER_TRANSFORM,
  buildCapBadgeSvg,
  capAzimuthMapFromResponse,
  capAzimuthTitle,
  withCapAzimuthEntry,
} from '../utils/capMarkerUtils';
import CapDirectionWedges from './map/CapDirectionWedges';
import CapAzimuthEditor from './map/CapAzimuthEditor';
import useInfoWindowFit from './map/useInfoWindowFit';
import {
  PopupButtonGrid,
  PopupInfoRows,
  PopupSection,
  popupButtonSx,
  popupInlineButtonSx,
} from './map/devicePopupUi';
import { hasPublicWifi, isCapDevice } from '../utils/publicWifiRrmPlanUi';
import {
  TRAFFIC_LAYER_CATEGORIES,
  TRAFFIC_RATE_LEGEND,
  getCategoryThroughputMbps,
  getCategoryTrafficMB,
  getDeviceTrafficColor,
  getTrafficLayerCategoryLabel,
} from '../utils/deviceDataCounters';

import { readStoredPlacesPreset, resolvePlacesPreset, storePlacesPreset } from '../utils/mapPlacesPresets';
import MapPlacesControl from './MapPlacesControl';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDBzNIMvmobitJ-vi8Z-kzVfK99qSb7jRw';
const GOOGLE_MAPS_LIBRARIES = ['geometry', 'marker'];

// Colours of custom trees (segments) on the map, by list position.
const SEGMENT_COLORS = [
  '#2196F3', '#9C27B0', '#FF9800', '#00BCD4', '#E91E63',
  '#3F51B5', '#FF6F00', '#00ACC1', '#8E24AA', '#FFA726',
];
const segmentColorAt = (index) => SEGMENT_COLORS[index % SEGMENT_COLORS.length];

const NO_SEGMENT_OVERLAY = { id: NO_SEGMENT_OVERLAY_ID, name: 'No segment' };
const NO_SEGMENT_OVERLAY_COLOR = '#757575';

const mapContainerStyle = {
  width: '100%',
  height: 'calc(100vh - 200px)',
  minHeight: '600px'
};

const defaultCenter = {
  lat: 32.0853, // Tel Aviv, Israel (default center)
  lng: 34.7818
};

const mapLayerListItemButtonSx = (accentColor) => ({
  py: 1,
  px: 1.5,
  '&.Mui-selected': {
    backgroundColor: `${accentColor}25`,
    borderLeft: `3px solid ${accentColor}`,
    '&:hover': {
      backgroundColor: `${accentColor}35`,
    },
  },
  '&:hover': {
    backgroundColor: `${accentColor}15`,
  },
});

const MapOverlayListRow = ({ label, color, selected, disabled, onToggle, showBottomBorder = true }) => (
  <ListItem disablePadding sx={{ borderBottom: showBottomBorder ? '1px solid #e0e0e0' : 'none' }}>
    <ListItemButton
      selected={selected}
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      sx={mapLayerListItemButtonSx(color)}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: selected ? 700 : 600,
          fontSize: '0.8rem',
          width: '100%',
        }}
      >
        {label}
      </Typography>
    </ListItemButton>
  </ListItem>
);

const SELECTION_DIALOG_TABS = [
  { label: 'Segment' },
  { label: 'RRM' },
  { label: 'Config' },
  { label: 'Commands' },
  { label: 'Group' },
  { label: 'Location' },
  { label: 'Nick Name' },
  { label: 'Whitelist' },
  { label: 'Blacklist' },
  { label: 'Band (STA)' },
  { label: 'BG scan' },
  { label: 'Last known good' },
];

function ssidsAssignedToDevice(ssidProfiles, device) {
  const serial = device?.serial_number?.toLowerCase?.();
  if (!serial || !Array.isArray(ssidProfiles)) return [];
  return ssidProfiles.filter((profile) =>
    (profile.device_serials || []).some((ds) => String(ds).toLowerCase() === serial),
  );
}

function normalizeProfileSsidKey(ssid) {
  if (!ssid || typeof ssid !== 'string') return '';
  const trimmed = ssid.trim().toLowerCase();
  if (trimmed === 'n/a' || trimmed === '-' || trimmed === '') return '';
  return trimmed;
}

/** Count station associations on public WiFi AP interfaces matching a profile SSID. */
function countPublicWifiClientsForProfileSsid(device, profileSsid) {
  if (!device?.connected) return 0;
  const want = normalizeProfileSsidKey(profileSsid);
  if (!want) return 0;
  const interfaces = device?.public_wifi?.interfaces || device?.public_wifi?.Interfaces;
  if (!Array.isArray(interfaces)) return 0;
  let total = 0;
  interfaces.forEach((iface) => {
    const mode = (iface?.Mode || iface?.mode || '').toString().trim().toLowerCase();
    if (mode !== 'ap') return;
    const ifaceSsid = normalizeProfileSsidKey(iface.SSID || iface.ssid);
    if (ifaceSsid !== want) return;
    const associations = iface.associations || iface.Associations;
    if (Array.isArray(associations)) {
      total += associations.length;
    }
  });
  return total;
}

function assignedSsidClientSummaries(device, assignedProfiles) {
  return assignedProfiles.map((profile) => {
    const label = profile.ssid || profile.name || profile.id;
    const count = countPublicWifiClientsForProfileSsid(device, profile.ssid || profile.name);
    return { profile, label, count };
  });
}

function summarizeSsidApplyResults(enqueueSnackbar, applyResults, serial) {
  const status = applyResults?.[serial?.toLowerCase?.() ? serial.toLowerCase() : serial];
  if (typeof status === 'string') {
    if (status === 'success') {
      enqueueSnackbar('SSID profile applied to device', { variant: 'success' });
      return;
    }
    if (status.startsWith('failed')) {
      enqueueSnackbar(`SSID apply failed: ${status}`, { variant: 'error' });
      return;
    }
    enqueueSnackbar(`SSID apply: ${status}`, { variant: 'info' });
    return;
  }
  enqueueSnackbar('SSID assigned; check device config if apply did not complete', { variant: 'info' });
}
const BAND_OPTIONS = ['', '5GHz', '6GHz'];
const HTMODE_OPTIONS = ['', 'HT20', 'HT40', 'HT80', 'HT160'];

function normalizeReportedStaBand(raw) {
  if (raw == null || raw === '') return null;
  // Human labels from API / radios (e.g. "5 GHz", "6GHz") and numeric uplink codes.
  const s = String(raw).trim().toLowerCase().replace(/\s/g, '');
  if (s === '6ghz' || s === '6' || s === '3') return '6GHz';
  if (s === '5ghz' || s === '5' || s === '2') return '5GHz';
  if (s.includes('6') && s.includes('ghz')) return '6GHz';
  if (s.includes('5') && s.includes('ghz')) return '5GHz';
  if (s.includes('2.4') || s === '2.4ghz') return '2.4GHz';
  return null;
}

function getDeviceStaBand(device) {
  const fromUl = normalizeReportedStaBand(device?.ul_band);
  if (fromUl) return fromUl;

  const privateInterfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces;
  if (!Array.isArray(privateInterfaces)) return null;

  const staIface = privateInterfaces.find((iface) => {
    const mode = (iface.Mode || iface.mode || '').toLowerCase();
    return mode === 'station' || mode === 'sta';
  });
  if (!staIface) return null;

  const radioName = staIface.Radio?.Name || staIface.radio?.name || staIface.radio?.Ref;
  let radioInfo = device.radios?.find((r) => r.name === radioName);
  const ifaceListIdx = privateInterfaces.indexOf(staIface);
  if (!radioInfo && device.radios?.length > 0) {
    const ifaceIdx =
      parseInt((staIface.Name || staIface.name || '').replace(/\D/g, ''), 10) || ifaceListIdx + 1;
    radioInfo =
      device.radios.find((r) => {
        const rIdx = parseInt((r.name || '').replace(/\D/g, ''), 10);
        return rIdx === ifaceIdx;
      }) || device.radios[Math.min(ifaceListIdx, device.radios.length - 1)];
  }
  if (radioInfo?.band) {
    return normalizeReportedStaBand(radioInfo.band) || radioInfo.band;
  }
  return null;
}

function resolveStaRadioInfo(device) {
  const privateInterfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces;
  if (!Array.isArray(privateInterfaces)) return null;

  const staIface = privateInterfaces.find((iface) => {
    const mode = (iface.Mode || iface.mode || '').toLowerCase();
    return mode === 'station' || mode === 'sta';
  });
  if (!staIface) return null;

  const radioName = staIface.Radio?.Name || staIface.radio?.name || staIface.radio?.Ref;
  let radioInfo = device.radios?.find((r) => r.name === radioName);
  const ifaceListIdx = privateInterfaces.indexOf(staIface);
  if (!radioInfo && device.radios?.length > 0) {
    const ifaceIdx =
      parseInt((staIface.Name || staIface.name || '').replace(/\D/g, ''), 10) || ifaceListIdx + 1;
    radioInfo =
      device.radios.find((r) => {
        const rIdx = parseInt((r.name || '').replace(/\D/g, ''), 10);
        return rIdx === ifaceIdx;
      }) || device.radios[Math.min(ifaceListIdx, device.radios.length - 1)];
  }
  return radioInfo || null;
}

function resolveRadioName(iface) {
  return iface?.Radio?.Name || iface?.radio?.name || iface?.radio?.Ref || iface?.Name || iface?.name || '';
}

function resolveRadioInfoForIface(device, iface) {
  const radioName = resolveRadioName(iface);
  if (!radioName || !Array.isArray(device?.radios)) return null;

  let radioInfo = device.radios.find((r) => r.name === radioName);
  const privateInterfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces;
  const ifaceListIdx = Array.isArray(privateInterfaces) ? privateInterfaces.indexOf(iface) : -1;
  if (!radioInfo && device.radios.length > 0 && ifaceListIdx >= 0) {
    const ifaceIdx =
      parseInt(String(iface?.Name || iface?.name || '').replace(/\D/g, ''), 10) || ifaceListIdx + 1;
    radioInfo =
      device.radios.find((r) => {
        const rIdx = parseInt(String(r.name || '').replace(/\D/g, ''), 10);
        return rIdx === ifaceIdx;
      }) || device.radios[Math.min(ifaceListIdx, device.radios.length - 1)];
  }
  return radioInfo || null;
}

function formatTxPowerDbm(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (Number.isFinite(n)) return `${n} dBm`;
  const s = String(value).trim();
  if (!s) return null;
  return s.toLowerCase().includes('dbm') ? s : `${s} dBm`;
}

function resolveIfaceTxPower(device, iface) {
  const direct = iface?.TxPower ?? iface?.tx_power;
  const fromIface = formatTxPowerDbm(direct);
  if (fromIface) return fromIface;

  const radio = resolveRadioInfoForIface(device, iface);
  return formatTxPowerDbm(radio?.TxPower ?? radio?.tx_power) || null;
}

/** Builds the optional traffic lines for the marker title. */
function buildTrafficTitleLines(device, category = 'total') {
  const lines = [];
  const label = getTrafficLayerCategoryLabel(category);
  const totalMB = getCategoryTrafficMB(device, category);
  if (totalMB != null) {
    lines.push(`${label}: ${totalMB.toFixed(1)} MB`);
  }
  const mbps = getCategoryThroughputMbps(device, category);
  if (mbps != null) {
    lines.push(`${label} rate: ${mbps.toFixed(2)} Mb/s`);
  }
  return lines;
}

function appendChildCountLines(lines, childCounts) {
  if (!childCounts) return lines;
  lines.push(`Children in tree: ${childCounts.tree}`);
  lines.push(`Direct children: ${childCounts.direct}`);
  return lines;
}

/** Nvidia / Jetson / QRB boards hang off a lamp; they are not mesh children. */
function isAiBoardDevice(device) {
  const model = (device?.capabilities?.model || '').toString().toLowerCase();
  return model.includes('nvidia') || model.includes('jetson') || model.includes('qrb5165');
}

/** Descendants under each device, from parent_serial. tree includes direct children. */
function buildTreeChildCounts(devices) {
  const counts = new Map();
  const childrenOf = new Map();
  (devices || []).forEach((device) => {
    const serial = device?.serial_number?.toLowerCase();
    if (!serial) return;
    counts.set(serial, { direct: 0, tree: 0 });
    if (isAiBoardDevice(device)) return;
    const parent = String(device.parent_serial || '').toLowerCase();
    if (!parent || parent === serial) return;
    if (!childrenOf.has(parent)) childrenOf.set(parent, []);
    childrenOf.get(parent).push(serial);
  });

  const memo = new Map();
  const visiting = new Set();
  const subtreeSize = (serial) => {
    if (memo.has(serial)) return memo.get(serial);
    if (visiting.has(serial)) return 0;
    visiting.add(serial);
    const kids = childrenOf.get(serial) || [];
    let total = kids.length;
    kids.forEach((child) => {
      total += subtreeSize(child);
    });
    visiting.delete(serial);
    memo.set(serial, total);
    return total;
  };

  counts.forEach((_, serial) => {
    const kids = childrenOf.get(serial) || [];
    counts.set(serial, { direct: kids.length, tree: subtreeSize(serial) });
  });
  return counts;
}

/** Native AdvancedMarker title — serial plus STA/AP TX power used on mesh links. */
function buildMapMarkerTitle(device, includeTraffic = false, trafficCategory = 'total', childCounts = null) {
  const serial = device?.serial_number || 'Unknown';
  const displayName = getDeviceDisplayName(device);
  const nameHeader = displayName && displayName.toLowerCase() !== serial.toLowerCase()
    ? `${displayName}\n${serial}`
    : serial;
  const header = deviceHasNotes(device)
    ? `${nameHeader}\nNote: ${truncateNote(deviceNotes(device))}`
    : nameHeader;

  const trafficLines = includeTraffic ? buildTrafficTitleLines(device, trafficCategory) : [];

  const privateInterfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces;
  if (!Array.isArray(privateInterfaces) || privateInterfaces.length === 0) {
    const lines = trafficLines.length ? [header, ...trafficLines] : [header];
    return appendMarkerRelationLines(lines, device, childCounts).join('\n');
  }

  const apByRadio = new Map();
  let staRadio = '';
  let staTx = null;

  privateInterfaces.forEach((iface) => {
    const mode = String(iface?.Mode || iface?.mode || '').toLowerCase();
    const radioName = resolveRadioName(iface);
    const tx = resolveIfaceTxPower(device, iface);

    if (mode === 'station' || mode === 'sta') {
      if (!staRadio || tx) {
        staRadio = radioName;
        staTx = tx;
      }
      return;
    }

    if (mode !== 'ap') return;

    const key = radioName.toLowerCase() || `ap-${apByRadio.size}`;
    const existing = apByRadio.get(key);
    if (!existing || (!existing.tx && tx)) {
      apByRadio.set(key, { radioName, tx });
    }
  });

  const lines = [header, ...trafficLines];
  if (staRadio || staTx) {
    lines.push(`STA TX${staRadio ? ` (${staRadio})` : ''}: ${staTx || '—'}`);
  }
  Array.from(apByRadio.values())
    .sort((a, b) => String(a.radioName).localeCompare(String(b.radioName)))
    .forEach(({ radioName, tx }) => {
      lines.push(`AP TX${radioName ? ` (${radioName})` : ''}: ${tx || '—'}`);
    });

  return appendMarkerRelationLines(lines, device, childCounts).join('\n');
}

/** AI boards show their parent instead of mesh child counts. */
function appendMarkerRelationLines(lines, device, childCounts) {
  if (isAiBoardDevice(device)) {
    const parentId = String(device?.parent_serial || '').trim();
    if (parentId) lines.push(`Parent: ${parentId}`);
    return lines;
  }
  return appendChildCountLines(lines, childCounts);
}

function getDeviceUplinkChannelInfo(device) {
  const rawChannel = device?.ul_channel;
  const numChannel = typeof rawChannel === 'number' ? rawChannel : parseInt(rawChannel, 10);
  let band = normalizeReportedStaBand(device?.ul_band);

  if (Number.isFinite(numChannel) && numChannel > 0) {
    if (!band) band = getDeviceStaBand(device);
    return { channel: numChannel, band: band || null };
  }

  const radioInfo = resolveStaRadioInfo(device);
  const ch = radioInfo?.channel != null ? Number(radioInfo.channel) : NaN;
  if (!Number.isFinite(ch) || ch <= 0) return null;

  if (!band && radioInfo?.band) {
    band = normalizeReportedStaBand(radioInfo.band) || radioInfo.band;
  }
  if (!band) band = getDeviceStaBand(device);

  return { channel: ch, band: band || null };
}

function channelModeArrowColor(band) {
  if (band === '6GHz') return '#4caf50';
  if (band === '5GHz') return '#2196f3';
  return '#9e9e9e';
}

// Custom AdvancedMarker component using native Google Maps AdvancedMarkerElement
const AdvancedMarker = ({ position, content, onClick, onDblClick, onRightClick, title, zIndex }) => {
  const markerRef = useRef(null);
  const map = useGoogleMap();

  useEffect(() => {
    if (!window.google?.maps?.marker?.AdvancedMarkerElement || !map) {
      return;
    }

    // Create the advanced marker
    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      title,
      map,
      zIndex,
    });

    // Add event listeners
    if (onClick) {
      marker.addListener('click', onClick);
    }
    if (onDblClick) {
      marker.addListener('dblclick', onDblClick);
    }
    if (onRightClick) {
      marker.addListener('rightclick', onRightClick);
    }

    markerRef.current = marker;

    // Cleanup
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
      }
    };
  }, [position, content, onClick, onDblClick, onRightClick, title, zIndex, map]);

  return null;
};

function buildDevicesWithLocation(devices, getDeviceType, findParentDevice, tempGPSUpdates) {
  if (!devices || !Array.isArray(devices)) return [];

  const devicesWithGPS = [];

  devices.forEach((device) => {
    let gps = null;
    let gpsSource = '';
    const effective = getEffectiveGps(device);
    if (effective) {
      gps = { lat: effective.lat, lng: effective.lng, alt: effective.alt };
      gpsSource = effective.source;
    }
    let isInheritedGPS = false;
    let parentDevice = null;
    let isTempGPS = false;

    const deviceType = getDeviceType(device.capabilities);

    if (device.parent_serial) {
      parentDevice = findParentDevice(device);
    }

    if (tempGPSUpdates[device.serial_number]) {
      gps = {
        lat: tempGPSUpdates[device.serial_number].lat,
        lng: tempGPSUpdates[device.serial_number].lng,
      };
      gpsSource = 'override';
      isTempGPS = true;
    } else if (
      !gps &&
      deviceType === 'AI' &&
      parentDevice?.compatible === 'jnet2' &&
      parentDevice?.gps &&
      parentDevice.gps.lat != null &&
      parentDevice.gps.lng != null
    ) {
      gps = parentDevice.gps;
      isInheritedGPS = true;
      gpsSource = 'inherited';
    }

    const rawLat = gps?.lat ?? gps?.latitude;
    const rawLng = gps?.lng ?? gps?.longitude;

    if (rawLat == null || rawLng == null) {
      return;
    }

    const lat = typeof rawLat === 'number' ? rawLat : parseFloat(rawLat);
    const lng = typeof rawLng === 'number' ? rawLng : parseFloat(rawLng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
      return;
    }

    let altitude = null;
    if (gps.alt != null && gps.alt !== '') {
      altitude = typeof gps.alt === 'number' ? gps.alt : parseFloat(gps.alt);
      if (!Number.isFinite(altitude)) {
        altitude = null;
      }
    }

    devicesWithGPS.push({
      ...device,
      lat,
      lng,
      alt: altitude,
      isInheritedGPS,
      isTempGPS,
      isManualGPS: gpsSource === 'override',
      gpsSource,
      parentDevice,
    });
  });

  return devicesWithGPS;
}

function centerAndZoomForDevices(devices) {
  const avgLat = devices.reduce((sum, device) => sum + device.lat, 0) / devices.length;
  const avgLng = devices.reduce((sum, device) => sum + device.lng, 0) / devices.length;
  const latitudes = devices.map((device) => device.lat);
  const longitudes = devices.map((device) => device.lng);
  const maxSpread = Math.max(
    Math.max(...latitudes) - Math.min(...latitudes),
    Math.max(...longitudes) - Math.min(...longitudes),
  );

  let zoom = 15;
  if (maxSpread > 1) zoom = 8;
  else if (maxSpread > 0.5) zoom = 10;
  else if (maxSpread > 0.1) zoom = 12;
  else if (maxSpread > 0.01) zoom = 14;

  return { center: { lat: avgLat, lng: avgLng }, zoom };
}

function locatedDeviceSetKey(devices) {
  return devices
    .map((device) => device.serial_number?.toLowerCase() || '')
    .sort()
    .join('\n');
}

function anyLocatedDeviceInView(map, devices) {
  const bounds = map?.getBounds?.();
  if (!bounds) return false;
  const LatLng = window.google?.maps?.LatLng;
  return devices.some((device) => {
    const point = LatLng ? new LatLng(device.lat, device.lng) : { lat: device.lat, lng: device.lng };
    return bounds.contains(point);
  });
}

function deviceIsInMapView(map, device) {
  const bounds = map?.getBounds?.();
  if (!bounds || device?.lat == null || device?.lng == null) return true;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const latPad = Math.abs(ne.lat() - sw.lat()) * 0.08;
  const lngPad = Math.abs(ne.lng() - sw.lng()) * 0.08;
  return device.lat <= ne.lat() + latPad
    && device.lat >= sw.lat() - latPad
    && device.lng <= ne.lng() + lngPad
    && device.lng >= sw.lng() - lngPad;
}

// Provisioning identifiers shown in the device popup, as [label, value]
// rows; jnet1 numeric ids also show their colon-separated hex form.
function provisioningRows(prov) {
  if (!prov) return [];
  const formatJnet1 = (value) => {
    if (!value) return value;
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    if (!Number.isNaN(numValue) && Number.isFinite(numValue)) {
      let hex = numValue.toString(16).toUpperCase();
      if (hex.length % 2 !== 0) hex = `0${hex}`;
      return `${value} (${hex.match(/.{2}/g).join(':')})`;
    }
    return value;
  };
  const aiMac = prov.AiDeviceMac || prov.ai_device_mac;
  const jnet1Embedded = prov.Jnet1EmbeddedMac || prov.jnet1_embedded_mac;
  const dimmerEmbedded = prov.DimmerEmbeddedSerial || prov.dimmer_embedded_serial;
  const jnet1Nema = prov.Jnet1NemaMac || prov.jnet1_nema_mac;
  const dimmerNema = prov.DimmerNemaSerial || prov.dimmer_nema_serial;
  const fixtureSerial = prov.FixtureSerial || prov.fixture_serial;
  return [
    aiMac && ['ai', aiMac],
    jnet1Embedded && ['jnet1', formatJnet1(jnet1Embedded)],
    dimmerEmbedded && ['dimmer', dimmerEmbedded],
    jnet1Nema && ['jnet1 (nema)', formatJnet1(jnet1Nema)],
    dimmerNema && ['dimmer (nema)', dimmerNema],
    fixtureSerial && ['fixture', fixtureSerial],
  ].filter(Boolean);
}

function deviceIsJnet2Cap(device, getDeviceType) {
  if (!device || getDeviceType(device.capabilities) !== 'CAP') return false;
  const compatible = device.compatible || device?.capabilities?.compatible;
  return String(compatible || '').toLowerCase() === 'jnet2';
}

function ssidBandNeedsCapTreeSelect(band) {
  const normalized = String(band || '2.4GHz,5GHz').toLowerCase();
  return normalized.includes('5ghz') || normalized.includes('6ghz');
}

function capUpperTreeIdsFromConfig(configuration) {
  const treemgmt = Array.isArray(configuration?.treemgmt) ? configuration.treemgmt : [];
  const fromTreemgmt = treemgmt
    .map((entry) => String(entry?.tree_id || '').trim())
    .filter((treeId) => treeId.toLowerCase().startsWith('5ghz'));
  if (fromTreemgmt.length > 0) {
    return [...new Set(fromTreemgmt)];
  }
  const radios = Array.isArray(configuration?.radio) ? configuration.radio : [];
  return [...new Set(
    radios
      .map((entry) => String(entry?.radio || '').trim())
      .filter((radioName) => radioName.toLowerCase().startsWith('5ghz')),
  )];
}

function deviceDetailsHref(serial) {
  return `/device/${encodeURIComponent(String(serial || '').trim())}`;
}

function DeviceDetailsLink({ serial }) {
  const navigate = useNavigate();
  const value = String(serial || '').trim();
  if (!value) return null;
  const href = deviceDetailsHref(value);
  return (
    <Link
      href={href}
      underline="hover"
      color="inherit"
      title="Open device details"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        navigate(href);
      }}
      sx={{ fontWeight: 600 }}
    >
      {value}
    </Link>
  );
}

function segmentDeviceName(option) {
  const serial = option?.serial_number || '';
  const name = option?.name || '';
  return name && name !== serial ? name : '';
}

function segmentDeviceChipLabel(option) {
  const name = segmentDeviceName(option);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
      {name ? <span>{name}</span> : null}
      <DeviceDetailsLink serial={option?.serial_number} />
    </Box>
  );
}

function renderSegmentDeviceOption(props, option) {
  const name = segmentDeviceName(option);
  return (
    <Box component="li" {...props}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="body2">
          {name || <DeviceDetailsLink serial={option?.serial_number} />}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {name ? <DeviceDetailsLink serial={option?.serial_number} /> : null}
          {option?.connected ? (name ? ' • Online' : 'Online') : (name ? ' • Offline' : 'Offline')}
        </Typography>
      </Box>
    </Box>
  );
}

const EMPTY_CAP_AZIMUTHS = new Map();

const DeviceMapView = ({
  devices,
  allDevices,
  onRefresh,
  highlightedGroupSerials,
  canWriteBackhaul = false,
}) => {
  const allowBackhaulWrite = canWriteBackhaul === true;
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const theme = useTheme();
  const mapColorScheme = React.useMemo(() => {
    if (!isLoaded || !window.google?.maps?.ColorScheme) {
      return undefined;
    }
    return theme.palette.mode === 'dark'
      ? window.google.maps.ColorScheme.DARK
      : window.google.maps.ColorScheme.LIGHT;
  }, [isLoaded, theme.palette.mode]);
  const infoWindowTextColor = theme.palette.mode === 'dark' ? '#000' : undefined;
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [outlinedSerial, setOutlinedSerial] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(8);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [customTreeName, setCustomTreeName] = useState('');
  const [segmentsDialogOpen, setSegmentsDialogOpen] = useState(false);
  const [manageSegmentFocus, setManageSegmentFocus] = useState(null);
  const [selectedCaps, setSelectedCaps] = useState([]);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [connectivityParams, setConnectivityParams] = useState({
    txPowerDown: '',
    txPowerUp: '',
    band: '',
    maxChildren: '',
    htmode: '',
    channels: '',
  });
  const connectivityChannelsTouchedRef = useRef(false);
  const [commandText, setCommandText] = useState('');
  const [commandLoading, setCommandLoading] = useState(false);
  const [creatingCustomTree, setCreatingCustomTree] = useState(false);
  const [connectivityLoading, setConnectivityLoading] = useState(false);
  const [bgScanForm, setBgScanForm] = useState(EMPTY_BG_SCAN_BULK_FORM);
  const [bgScanLoading, setBgScanLoading] = useState(false);
  const [deleteSegmentsLoading, setDeleteSegmentsLoading] = useState(false);
  const [deleteSegmentsConfirmOpen, setDeleteSegmentsConfirmOpen] = useState(false);
  const [customTrees, setCustomTrees] = useState([]);
  const [customTreesLoading, setCustomTreesLoading] = useState(false);
  const [selectedTree, setSelectedTree] = useState(null);
  const [addToCustomTreeLoading, setAddToCustomTreeLoading] = useState(false);
  const [removeFromCustomTreeLoading, setRemoveFromCustomTreeLoading] = useState(false);
  const [deleteCustomTreeLoading, setDeleteCustomTreeLoading] = useState(false);
  const [segmentEditNodes, setSegmentEditNodes] = useState([]);
  const [segmentEditCaps, setSegmentEditCaps] = useState([]);
  const [addToExistingTree, setAddToExistingTree] = useState(null);
  const [addToExistingNodes, setAddToExistingNodes] = useState([]);
  const [addToExistingCaps, setAddToExistingCaps] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [highlightedTree, setHighlightedTree] = useState(null);
  const [highlightedTreeRootSerial, setHighlightedTreeRootSerial] = useState(null);
  const [highlightedTreeMode, setHighlightedTreeMode] = useState(null); // 'subtree' | 'children'
  const [treeHighlightMenuAnchor, setTreeHighlightMenuAnchor] = useState(null);
  const [acceptAllMenuAnchor, setAcceptAllMenuAnchor] = useState(null);
  const [acceptAllSaving, setAcceptAllSaving] = useState(false);
  const [acceptAllConfirm, setAcceptAllConfirm] = useState(null);
  const [drawingMode, setDrawingMode] = useState(null);
  const [showAllConnections, setShowAllConnections] = useState(false);
  const [showChannelMode, setShowChannelMode] = useState(false);
  const [showSegmentsOnMap, setShowSegmentsOnMap] = useState(false);
  const [selectedSegmentForHighlight, setSelectedSegmentForHighlight] = useState(null);
  const [isLoadingWifiDetails, setIsLoadingWifiDetails] = useState(false);
  const [iperfDialogOpen, setIperfDialogOpen] = useState(false);
  const [iperfSelectionMode, setIperfSelectionMode] = useState(false);
  const [iperfFromDevice, setIperfFromDevice] = useState(null);
  const [iperfToDevice, setIperfToDevice] = useState(null);
  const [iperfRunning, setIperfRunning] = useState(false);
  const [iperfResults, setIperfResults] = useState(null);
  const [iperfConfirmDialogOpen, setIperfConfirmDialogOpen] = useState(false);
  const [layersMenuAnchor, setLayersMenuAnchor] = useState(null);
  const [iperfViewDialogOpen, setIperfViewDialogOpen] = useState(false);
  const [iperfTestRuns, setIperfTestRuns] = useState([]);
  const [iperfTestRunsLoading, setIperfTestRunsLoading] = useState(false);
  const [iperfViewRun, setIperfViewRun] = useState(null); // full run detail shown as overlay
  const [iperfViewRunLoading, setIperfViewRunLoading] = useState(false);
  const [assignSsidDialogOpen, setAssignSsidDialogOpen] = useState(false);
  const [assignSsidDevice, setAssignSsidDevice] = useState(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDialogDevice, setGroupDialogDevice] = useState(null);
  const [selectedDeviceMemberGroups, setSelectedDeviceMemberGroups] = useState([]);
  const [ssidProfiles, setSsidProfiles] = useState([]);
  const [vlanProfiles, setVlanProfiles] = useState([]);
  const [ssidProfilesLoading, setSsidProfilesLoading] = useState(false);
  const [assignSsidId, setAssignSsidId] = useState('');
  const [assignSsidTreeId, setAssignSsidTreeId] = useState('');
  const [assignSsidCapTreeIds, setAssignSsidCapTreeIds] = useState([]);
  const [assignSsidConfigLoading, setAssignSsidConfigLoading] = useState(false);
  const [assignSsidSaving, setAssignSsidSaving] = useState(false);
  const [ssidUnassignInProgress, setSsidUnassignInProgress] = useState(false);
  const [showWhitelistOverlay, setShowWhitelistOverlay] = useState(false);
  const [showBlacklistOverlay, setShowBlacklistOverlay] = useState(false);
  const [showPublicWifiLampsLayer, setShowPublicWifiLampsLayer] = useState(false);
  const [showDataTrafficLayer, setShowDataTrafficLayer] = useState(false);
  const [dataTrafficCategory, setDataTrafficCategory] = useState('total');
  const [selectionWhitelistData, setSelectionWhitelistData] = useState({});
  const [selectionWhitelistLoading, setSelectionWhitelistLoading] = useState(false);
  const [staBandFilter5G, setStaBandFilter5G] = useState(true);
  const [staBandFilter6G, setStaBandFilter6G] = useState(true);
  const [staBandSwitchSelected, setStaBandSwitchSelected] = useState(() => new Set());
  const [staBandTarget, setStaBandTarget] = useState('6GHz');
  const [staBandSwitchLoading, setStaBandSwitchLoading] = useState(false);
  const [clearingWhitelistSerials, setClearingWhitelistSerials] = useState(new Set());
  const [clearingBlacklistSerials, setClearingBlacklistSerials] = useState(new Set());
  const [selectedDeviceForGPS, setSelectedDeviceForGPS] = useState(null);
  const [tempGPSUpdates, setTempGPSUpdates] = useState({});
  const [metadataEditName, setMetadataEditName] = useState('');
  const [metadataSaving, setMetadataSaving] = useState(false);
  const [devicesWithoutGPSExpanded, setDevicesWithoutGPSExpanded] = useState(false);
  const [showNeighborsForDevice, setShowNeighborsForDevice] = useState(null);
  const [neighborsData, setNeighborsData] = useState(null);
  const [loadingNeighbors, setLoadingNeighbors] = useState(false);
  const [heardByTarget, setHeardByTarget] = useState(null);
  const [heardByMinRssi, setHeardByMinRssi] = useState(-75);
  const [heardByLinks, setHeardByLinks] = useState([]);
  const [heardByDialogOpen, setHeardByDialogOpen] = useState(false);
  const [heardByTreeName, setHeardByTreeName] = useState('');
  const [heardBySegmentDialogOpen, setHeardBySegmentDialogOpen] = useState(false);
  const [heardBySegmentNodes, setHeardBySegmentNodes] = useState([]);
  const [heardBySegmentCaps, setHeardBySegmentCaps] = useState([]);
  const [loadingHeardBy, setLoadingHeardBy] = useState(false);
  const [creatingHeardBySegment, setCreatingHeardBySegment] = useState(false);
  const [subtreeSegmentDialogOpen, setSubtreeSegmentDialogOpen] = useState(false);
  const [subtreeSegmentTarget, setSubtreeSegmentTarget] = useState(null);
  const [subtreeSegmentName, setSubtreeSegmentName] = useState('');
  const [subtreeSegmentConnectedOnly, setSubtreeSegmentConnectedOnly] = useState(true);
  const [subtreeSegmentSignalFilter, setSubtreeSegmentSignalFilter] = useState('all');
  const [subtreeSegmentMinRssi, setSubtreeSegmentMinRssi] = useState('-75');
  const [subtreeSegmentNodes, setSubtreeSegmentNodes] = useState([]);
  const [subtreeSegmentCaps, setSubtreeSegmentCaps] = useState([]);
  const [subtreeSegmentConfigBySerial, setSubtreeSegmentConfigBySerial] = useState(() => new Map());
  const [loadingSubtreeSegmentConfig, setLoadingSubtreeSegmentConfig] = useState(false);
  const [creatingSubtreeSegment, setCreatingSubtreeSegment] = useState(false);
  const [selectionNeighborsScanInterval, setSelectionNeighborsScanInterval] = useState(15);
  const [selectionNeighborsScanAllFreq, setSelectionNeighborsScanAllFreq] = useState(true);
  const [selectionNeighborsScanBatch, setSelectionNeighborsScanBatch] = useState(DEFAULT_NEIGHBOR_SCAN_BATCH);
  const [selectionNeighborsScanLoading, setSelectionNeighborsScanLoading] = useState(false);
  const [selectionNeighborsScanConfirmOpen, setSelectionNeighborsScanConfirmOpen] = useState(false);
  const [showTmCandidatesForDevice, setShowTmCandidatesForDevice] = useState(null);
  const [tmCandidatesData, setTmCandidatesData] = useState(null);
  const [loadingTmCandidates, setLoadingTmCandidates] = useState(false);
  const [parentSelectionMode, setParentSelectionMode] = useState(false);
  const [deviceSelectingParent, setDeviceSelectingParent] = useState(null);
  const [accessListDialogOpen, setAccessListDialogOpen] = useState(false);
  const [deviceForAccessList, setDeviceForAccessList] = useState(null);
  const [selectedWhitelist, setSelectedWhitelist] = useState([]);
  const [selectedBlacklist, setSelectedBlacklist] = useState([]);
  const [accessListUpdateStatus, setAccessListUpdateStatus] = useState({ type: '', message: '' });
  const [accessListLoading, setAccessListLoading] = useState(false);
  const [mapSelectionMode, setMapSelectionMode] = useState(null); // 'whitelist' | 'blacklist' | null
  const [highlightedAccessList, setHighlightedAccessList] = useState({ whitelist: [], blacklist: [] }); // For visual marking on map
  // Group management state
  const [groupCreationName, setGroupCreationName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [syslogConfig, setSyslogConfig] = useState({ ip: '', port: '', proto: 'udp', log_remote: '' });
  const [syslogSaving, setSyslogSaving] = useState(false);
  const [reportsConfig, setReportsConfig] = useState({
    backhaul_clients_report: '1',
    public_wifi_clients_report: '1',
  });
  const [reportsSaving, setReportsSaving] = useState(false);
  const [showGroupsOnMap, setShowGroupsOnMap] = useState(false);
  const [mapGroups, setMapGroups] = useState([]);
  const [mapGroupsLoading, setMapGroupsLoading] = useState(false);
  const [selectedGroupForHighlight, setSelectedGroupForHighlight] = useState(null);

  const mapRef = useRef(null);
  const locatedDeviceKeyRef = useRef(null);

  const filteredDeviceKeyRef = useRef(null);
  const mapFrameRef = useRef({ token: 0, serials: null });
  const seenMapFrameTokenRef = useRef(0);
  const hasAutoFittedMapRef = useRef(false);
  // Google POI preset; each is its own map ID, so changing it re-creates the
  // map, which then keeps the current view instead of re-fitting devices.
  const [placesPreset, setPlacesPreset] = useState(readStoredPlacesPreset);
  const keepViewOnMapLoadRef = useRef(false);
  const mapShellRef = useRef(null);
  const assignSsidDialogSuppressCloseRef = useRef(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const drawingPolygonRef = useRef(null);
  const drawingPolylineRef = useRef(null);
  const drawingListenersRef = useRef([]);
  const isDrawingRef = useRef(false);
  const currentPathRef = useRef([]);
  const wifiDetailsPromiseRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !mapColorScheme) {
      return;
    }
    mapRef.current.setOptions({ colorScheme: mapColorScheme });
  }, [mapColorScheme]);

  // Helper function to get device type
  const getDeviceType = useCallback((capabilities) => {
    if (!capabilities) return 'Unknown';
    const model = (capabilities.model || '').toString();
    const compatible = (capabilities.compatible || '').toString().toLowerCase();

    // EdgeCore APs should use the CAP icon on the map
    if (compatible === 'edgecore_eap102' || compatible.includes('edgecore')) {
      return 'CAP';
    }

    if (model.toLowerCase().includes('nvidia') || model.toLowerCase().includes('jetson') || model.toLowerCase().includes('qrb5165')) {
      return 'AI';
    }
    if (model.toLowerCase().includes('cap')) return 'CAP';
    if (model.toLowerCase().includes('node')) return 'Node';
    return model || 'Unknown';
  }, []);

  // Get all CAP devices from the devices list
  const allCapDevices = React.useMemo(() => {
    if (!devices || !Array.isArray(devices)) return [];
    return devices.filter(device => getDeviceType(device.capabilities) === 'CAP');
  }, [devices, getDeviceType]);

  // Estimated CAP antenna directions: one request per map load (cached in
  // api.js and on the backend), never part of the per-device payload.
  // Results are tagged with their project so a project switch never shows
  // another project's directions. allDevices only changes on a real
  // device refresh (devices is rebuilt every render), so it marks a
  // refresh: then both caches are bypassed and the backend recomputes.
  const capAzimuthProjectId = auth.getProjectId() || '';
  const [capAzimuthState, setCapAzimuthState] = useState(() => ({ projectId: null, bySerial: new Map() }));
  const capAzimuthFetchedForRef = useRef(null);
  const capAzimuthRefreshSignal = allDevices ?? null;
  const hasCapDevices = allCapDevices.length > 0;
  // Bumped by every set / reset. A list fetch that started before one is
  // stale (it may predate the change), so it is dropped and re-read.
  const capAzimuthWriteSeqRef = useRef(0);
  const [capAzimuthReload, setCapAzimuthReload] = useState(0);
  const capAzimuthReloadSeenRef = useRef(0);
  useEffect(() => {
    if (!hasCapDevices) return undefined;
    let cancelled = false;
    const isReload = capAzimuthReload !== capAzimuthReloadSeenRef.current;
    capAzimuthReloadSeenRef.current = capAzimuthReload;
    // A reload only needs fresh overrides (read live by the backend), not
    // a recomputed estimate.
    const isRefresh = !isReload && capAzimuthFetchedForRef.current === capAzimuthProjectId;
    capAzimuthFetchedForRef.current = capAzimuthProjectId;
    const seqAtStart = capAzimuthWriteSeqRef.current;
    api.getCapAzimuths({ refresh: isRefresh, force: isReload })
      .then((resp) => {
        if (cancelled) return;
        if (capAzimuthWriteSeqRef.current !== seqAtStart) {
          setCapAzimuthReload((n) => n + 1);
          return;
        }
        setCapAzimuthState({ projectId: capAzimuthProjectId, bySerial: capAzimuthMapFromResponse(resp) });
      })
      .catch((error) => {
        console.warn('CAP azimuths unavailable; drawing CAPs without direction', error);
      });
    return () => {
      cancelled = true;
    };
  }, [hasCapDevices, capAzimuthProjectId, capAzimuthRefreshSignal, capAzimuthReload]);
  const capAzimuthBySerial = capAzimuthState.projectId === capAzimuthProjectId
    ? capAzimuthState.bySerial
    : EMPTY_CAP_AZIMUTHS;

  // User-set CAP direction: typed in the CAP popup, or aimed by clicking
  // the map (capAim holds the CAP waiting for that click). Reset goes back
  // to the estimate.
  const [capAim, setCapAim] = useState(null);
  const [capAzimuthSaving, setCapAzimuthSaving] = useState(false);
  const applyCapAzimuthResponse = useCallback((resp) => {
    setCapAzimuthState((prev) => ({
      projectId: capAzimuthProjectId,
      bySerial: withCapAzimuthEntry(prev.projectId === capAzimuthProjectId ? prev.bySerial : EMPTY_CAP_AZIMUTHS, resp),
    }));
  }, [capAzimuthProjectId]);
  const handleSaveCapAzimuth = useCallback(async (serial, azimuthDeg) => {
    capAzimuthWriteSeqRef.current += 1;
    setCapAzimuthSaving(true);
    try {
      applyCapAzimuthResponse(await api.setCapAzimuth(serial, azimuthDeg));
      enqueueSnackbar(`Antenna direction set to ${Math.round(azimuthDeg)}°`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Could not set antenna direction: ${error.message}`, { variant: 'error' });
    } finally {
      setCapAzimuthSaving(false);
    }
  }, [applyCapAzimuthResponse, enqueueSnackbar]);
  const handleResetCapAzimuth = useCallback(async (serial) => {
    capAzimuthWriteSeqRef.current += 1;
    setCapAzimuthSaving(true);
    try {
      applyCapAzimuthResponse(await api.resetCapAzimuth(serial));
      enqueueSnackbar('Antenna direction reset to the estimate', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Could not reset antenna direction: ${error.message}`, { variant: 'error' });
    } finally {
      setCapAzimuthSaving(false);
    }
  }, [applyCapAzimuthResponse, enqueueSnackbar]);
  // Aiming hides the device popup; Esc cancels.
  useEffect(() => {
    if (!capAim) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setCapAim(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [capAim]);
  const [devicePopupRef, devicePopupMaxHeight] = useInfoWindowFit(420);
  // Finish aiming at a map point or a clicked device: the direction is the
  // compass heading from the CAP to it.
  const aimCapAt = useCallback((target) => {
    const computeHeading = window.google?.maps?.geometry?.spherical?.computeHeading;
    if (!capAim || !computeHeading || !Number.isFinite(target?.lat) || !Number.isFinite(target?.lng)) return;
    const heading = computeHeading(
      new window.google.maps.LatLng(capAim.lat, capAim.lng),
      new window.google.maps.LatLng(target.lat, target.lng),
    );
    setCapAim(null);
    handleSaveCapAzimuth(capAim.serial, ((heading % 360) + 360) % 360);
  }, [capAim, handleSaveCapAzimuth]);
  const handleToggleCapAim = useCallback((device) => {
    setCapAim((prev) => {
      if (prev && prev.serial === device.serial_number) return null;
      return { serial: device.serial_number, lat: device.lat, lng: device.lng };
    });
  }, []);

  // Get all Node devices from the devices list
  const allNodeDevices = React.useMemo(() => {
    if (!devices || !Array.isArray(devices)) return [];
    return devices.filter(device => getDeviceType(device.capabilities) === 'Node');
  }, [devices, getDeviceType]);

  const deviceCatalog = allDevices ?? devices;
  const treeChildCounts = React.useMemo(
    () => buildTreeChildCounts(deviceCatalog),
    [deviceCatalog],
  );

  const findDeviceInCatalog = useCallback((serial) => {
    if (!serial || !deviceCatalog) return null;
    const key = String(serial).toLowerCase();
    return deviceCatalog.find((d) => d.serial_number?.toLowerCase() === key) ?? null;
  }, [deviceCatalog]);

  // Helper function to find parent device (full catalog so subtrees span filters)
  const findParentDevice = useCallback((device) => {
    if (!device?.parent_serial) return null;
    return findDeviceInCatalog(device.parent_serial);
  }, [findDeviceInCatalog]);

  const getSelectedSerials = useCallback(() => {
    const serialSet = new Set();
    selectedDevices.forEach(device => {
      if (device.serial_number) {
        serialSet.add(device.serial_number.toLowerCase());
      }
    });
    return Array.from(serialSet);
  }, [selectedDevices]);

  const hasWifiInterfaceData = React.useMemo(() => {
    if (!devices || !Array.isArray(devices)) return false;
    return devices.some(device => {
      const privateInterfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces;
      const publicInterfaces = device?.public_wifi?.interfaces || device?.public_wifi?.Interfaces;
      return (privateInterfaces && privateInterfaces.length > 0) ||
             (publicInterfaces && publicInterfaces.length > 0);
    });
  }, [devices]);

  const ensureWifiInterfaces = useCallback(async () => {
    if (hasWifiInterfaceData) {
      return true;
    }
    if (!onRefresh) {
      enqueueSnackbar('WiFi interface details are not available', { variant: 'warning' });
      return false;
    }
    if (wifiDetailsPromiseRef.current) {
      return wifiDetailsPromiseRef.current;
    }

    const fetchPromise = (async () => {
      setIsLoadingWifiDetails(true);
      try {
        await onRefresh({
          addFields: ['wifi_interfaces'],
          force: true,
        });
        return true;
      } catch (error) {
        console.error('Failed to load WiFi interface data', error);
        enqueueSnackbar('Failed to load WiFi interface data', { variant: 'error' });
        return false;
      } finally {
        setIsLoadingWifiDetails(false);
        wifiDetailsPromiseRef.current = null;
      }
    })();

    wifiDetailsPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [hasWifiInterfaceData, onRefresh, enqueueSnackbar]);

  const staBandFilteredDevices = React.useMemo(() => {
    if (!staBandFilter5G && !staBandFilter6G) return [];
    return selectedDevices.filter((device) => {
      const band = getDeviceStaBand(device);
      if (band !== '5GHz' && band !== '6GHz') return false;
      if (staBandFilter5G && band === '5GHz') return true;
      if (staBandFilter6G && band === '6GHz') return true;
      return false;
    });
  }, [selectedDevices, staBandFilter5G, staBandFilter6G]);

  useEffect(() => {
    if (dialogTab !== 9 || !selectionDialogOpen) return;
    ensureWifiInterfaces();
  }, [dialogTab, selectionDialogOpen, ensureWifiInterfaces]);

  useEffect(() => {
    if (dialogTab !== 9) return;
    const allowed = new Set(
      staBandFilteredDevices.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
    );
    setStaBandSwitchSelected((prev) => {
      const next = new Set();
      prev.forEach((s) => {
        const k = String(s).toLowerCase();
        if (allowed.has(k)) next.add(k);
      });
      return next;
    });
  }, [dialogTab, staBandFilteredDevices]);

  const summarizeOperationResults = useCallback((results, successMessage) => {
    if (!Array.isArray(results) || results.length === 0) {
      enqueueSnackbar(successMessage, { variant: 'success' });
      return;
    }
    const successCount = results.filter(result => result.success).length;
    const failedResults = results.filter(result => !result.success);
    
    if (successCount === results.length) {
      enqueueSnackbar(successMessage, { variant: 'success' });
    } else if (successCount === 0) {
      // All failed - show detailed error message
      const failedDevices = failedResults.map(r => r.serial).join(', ');
      const errorMessage = `Operation failed for all devices: ${failedDevices}. ${failedResults[0]?.message || ''}`;
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 8000 });
    } else {
      // Partial success - show detailed warning
      const failedDevices = failedResults.map(r => r.serial).join(', ');
      const warningMessage = `${successMessage} (${successCount}/${results.length} succeeded). Failed devices: ${failedDevices}`;
      enqueueSnackbar(warningMessage, { variant: 'warning', autoHideDuration: 8000 });
    }
  }, [enqueueSnackbar]);

  // Filter devices that have valid GPS coordinates or can inherit from parent
  const devicesWithLocation = React.useMemo(() => (
    buildDevicesWithLocation(devices, getDeviceType, findParentDevice, tempGPSUpdates)
  ), [devices, getDeviceType, findParentDevice, tempGPSUpdates]);

  const catalogDevicesWithLocation = React.useMemo(() => (
    buildDevicesWithLocation(deviceCatalog, getDeviceType, findParentDevice, tempGPSUpdates)
  ), [deviceCatalog, getDeviceType, findParentDevice, tempGPSUpdates]);

  /** When the Public WiFi lamps layer is on: CAPs always + NODEs with active Public WiFi only. */
  const mapDisplayedDevices = React.useMemo(() => {
    let base;
    if (!showPublicWifiLampsLayer) {
      base = devicesWithLocation;
    } else {
      base = devicesWithLocation.filter((device) => {
        if (isCapDevice(device) || getDeviceType(device.capabilities) === 'CAP') return true;
        if (getDeviceType(device.capabilities) === 'AI') return false;
        return hasPublicWifi(device);
      });
    }

    // Sub-tree view overrides active filters: always show every device in the highlighted tree.
    if (!highlightedTree || highlightedTree.size === 0) {
      return base;
    }

    const baseSerials = new Set(base.map((d) => d.serial_number?.toLowerCase()).filter(Boolean));
    const subtreeExtras = catalogDevicesWithLocation.filter((device) => {
      const serial = device.serial_number?.toLowerCase();
      return serial && highlightedTree.has(serial) && !baseSerials.has(serial);
    });

    return subtreeExtras.length > 0 ? [...base, ...subtreeExtras] : base;
  }, [
    devicesWithLocation,
    catalogDevicesWithLocation,
    highlightedTree,
    showPublicWifiLampsLayer,
    getDeviceType,
  ]);

  const mapDisplayedSerialSet = React.useMemo(() => (
    new Set(mapDisplayedDevices.map((d) => d.serial_number?.toLowerCase()).filter(Boolean))
  ), [mapDisplayedDevices]);

  // Displayed CAPs that have an estimated direction, for CapDirectionWedges.
  const capDirectionWedges = React.useMemo(() => {
    if (capAzimuthBySerial.size === 0) return [];
    return mapDisplayedDevices
      .filter((d) => getDeviceType(d.capabilities) === 'CAP')
      .map((d) => {
        const serial = d.serial_number?.toLowerCase();
        const azimuth = serial ? capAzimuthBySerial.get(serial) : null;
        return azimuth && azimuth.azimuthDeg != null ? { serial, lat: d.lat, lng: d.lng, azimuth } : null;
      })
      .filter(Boolean);
  }, [mapDisplayedDevices, capAzimuthBySerial, getDeviceType]);

  const devicesBySerial = React.useMemo(() => {
    const map = new Map();
    (devicesWithLocation || []).forEach((device) => {
      const serial = device?.serial_number?.toLowerCase();
      if (serial) map.set(serial, device);
    });
    return map;
  }, [devicesWithLocation]);

  const heardByHearerSerials = React.useMemo(() => (
    new Set((heardByLinks || []).map((link) => link.scannerSerial))
  ), [heardByLinks]);

  const heardByOverlayActive = Boolean(heardByTarget && heardByLinks.length > 0);

  const selectedDeviceHasConnectedChild = React.useMemo(() => (
    selectedDevice ? hasConnectedChild(selectedDevice, deviceCatalog) : false
  ), [selectedDevice, deviceCatalog]);

  const subtreeSegmentPreview = React.useMemo(() => {
    if (!subtreeSegmentTarget) {
      return {
        nodes: [],
        caps: [],
        descendantCount: 0,
        includedDescendantCount: 0,
      };
    }
    const minRssi = parseInt(subtreeSegmentMinRssi, 10);
    return buildSubtreeSegmentLists(
      subtreeSegmentTarget,
      deviceCatalog,
      {
        connectedOnly: subtreeSegmentConnectedOnly,
        signalFilter: subtreeSegmentSignalFilter,
        minRssi,
      },
      getDeviceType,
    );
  }, [
    subtreeSegmentTarget,
    deviceCatalog,
    subtreeSegmentConnectedOnly,
    subtreeSegmentSignalFilter,
    subtreeSegmentMinRssi,
    getDeviceType,
  ]);

  // Get devices without GPS location
  const devicesWithoutGPS = React.useMemo(() => {
    if (!devices || !Array.isArray(devices)) return [];
    
    return devices.filter(device => {
      // Skip devices with temporary GPS updates
      if (tempGPSUpdates[device.serial_number]) {
        return false;
      }
      
      const gpsEffective = getEffectiveGps(device);
      const deviceType = getDeviceType(device.capabilities);
      
      // Skip AI devices only when they can inherit GPS from a jnet2 parent.
      if (deviceType === 'AI') {
        if (!gpsEffective && device?.parent_serial) {
          const parent = findParentDevice(device);
          const canInheritFromJnet2 =
            parent?.compatible === 'jnet2' &&
            parent?.gps?.lat != null &&
            parent?.gps?.lng != null;
          if (canInheritFromJnet2) return false;
        }
      }
      
      if (!gpsEffective) {
        return true;
      }

      return false;
    });
  }, [devices, getDeviceType, tempGPSUpdates, findParentDevice]);

  // Move the camera when the filtered device set changes only if that set is
  // non-empty and none of its members are already inside the current view.
  // The first non-empty set still frames the map so the initial load is useful.
  useEffect(() => {
    const key = locatedDeviceSetKey(devicesWithLocation);
    if (key === locatedDeviceKeyRef.current) return;
    locatedDeviceKeyRef.current = key;

    const noteFrame = (serials) => {
      mapFrameRef.current = { token: mapFrameRef.current.token + 1, serials };
    };

    if (devicesWithLocation.length === 0) {
      noteFrame(null);
      return;
    }

    const map = mapRef.current;
    if (hasAutoFittedMapRef.current && anyLocatedDeviceInView(map, devicesWithLocation)) {
      noteFrame(null);
      return;
    }

    noteFrame(new Set(
      devicesWithLocation
        .map((device) => device.serial_number?.toLowerCase())
        .filter(Boolean),
    ));

    const { center, zoom } = centerAndZoomForDevices(devicesWithLocation);
    setMapCenter(center);
    setMapZoom(zoom);
    hasAutoFittedMapRef.current = true;

    // Google Maps doesn't always react to center prop changes.
    if (map) {
      map.panTo(center);
      map.setZoom(zoom);
    }
  }, [devicesWithLocation]);

  // Auto-expand devices without GPS sidebar when no devices have GPS location
  useEffect(() => {
    if (devicesWithLocation.length === 0 && devicesWithoutGPS.length > 0) {
      setDevicesWithoutGPSExpanded(true);
    }
  }, [devicesWithLocation.length, devicesWithoutGPS.length]);

  // Calculate distance between two GPS coordinates in meters using Haversine formula
  const calculateDistance = useCallback((lat1, lng1, lat2, lng2) => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Build device tree for a given device (only the path from device to root and its descendants)
  const buildDeviceTree = useCallback((device) => {
    if (!device || !deviceCatalog) return new Set();

    const treeDevices = new Set();

    const collectAncestors = (startDevice) => {
      const visited = new Set();
      let current = startDevice;

      treeDevices.add(current.serial_number.toLowerCase());

      while (current.parent_serial) {
        const parent = findDeviceInCatalog(current.parent_serial);
        if (!parent || visited.has(parent.serial_number)) break;
        visited.add(parent.serial_number);
        treeDevices.add(parent.serial_number.toLowerCase());
        current = parent;
      }
    };

    collectAncestors(device);

    const traverseDescendants = (currentDevice) => {
      if (!currentDevice?.serial_number) return;

      deviceCatalog.forEach((d) => {
        if (isAiBoardDevice(d)) return;
        if (
          d.parent_serial &&
          d.parent_serial.toLowerCase() === currentDevice.serial_number.toLowerCase() &&
          !treeDevices.has(d.serial_number.toLowerCase())
        ) {
          treeDevices.add(d.serial_number.toLowerCase());
          traverseDescendants(d);
        }
      });
    };

    traverseDescendants(device);

    return treeDevices;
  }, [deviceCatalog, findDeviceInCatalog]);

  const buildDirectChildrenHighlight = useCallback((device) => {
    if (!device?.serial_number || !deviceCatalog) return new Set();

    const treeDevices = new Set();
    const rootSerial = device.serial_number.toLowerCase();
    treeDevices.add(rootSerial);

    deviceCatalog.forEach((d) => {
      if (isAiBoardDevice(d)) return;
      if (
        d.parent_serial &&
        d.parent_serial.toLowerCase() === rootSerial &&
        d.serial_number
      ) {
        treeDevices.add(d.serial_number.toLowerCase());
      }
    });

    return treeDevices;
  }, [deviceCatalog]);

  const clearHighlightedTree = useCallback(() => {
    setHighlightedTree(null);
    setHighlightedTreeRootSerial(null);
    setHighlightedTreeMode(null);
  }, []);

  // Handle parent device selection
  const handleSelectParent = useCallback(async (parentDevice) => {
    if (!deviceSelectingParent) return;
    
    try {
      await api.setParent(deviceSelectingParent.serial_number, parentDevice.serial_number);
      enqueueSnackbar(`Successfully set ${parentDevice.name || parentDevice.serial_number} as parent for ${deviceSelectingParent.name || deviceSelectingParent.serial_number}`, { variant: 'success' });
      setParentSelectionMode(false);
      setDeviceSelectingParent(null);
      
      // Refresh devices after a short delay
      setTimeout(() => {
        if (onRefresh) {
          onRefresh();
        }
      }, 1000);
    } catch (error) {
      enqueueSnackbar(`Failed to set parent: ${error.message}`, { variant: 'error' });
      setParentSelectionMode(false);
      setDeviceSelectingParent(null);
    }
  }, [deviceSelectingParent, enqueueSnackbar, onRefresh]);

  const handleMarkerClick = useCallback((device) => {
    // Aiming a CAP antenna: a clicked lamp (the usual target) sets the
    // direction toward it; clicking the CAP itself does nothing.
    if (capAim) {
      if (device.serial_number?.toLowerCase() !== capAim.serial?.toLowerCase()) {
        aimCapAt({ lat: device.lat, lng: device.lng });
      }
      return;
    }

    // If map selection mode for access list is active
    if (mapSelectionMode && deviceForAccessList) {
      // Don't allow selecting the device itself
      if (device.serial_number.toLowerCase() === deviceForAccessList.serial_number.toLowerCase()) {
        enqueueSnackbar('Cannot add device to its own access list', { variant: 'warning' });
        return;
      }
      
      // Only allow jnet2 compatible devices
      if (device.compatible !== 'jnet2') {
        enqueueSnackbar('Only jnet2 compatible devices can be added to access lists', { variant: 'warning' });
        return;
      }
      
      // Add to the appropriate list
      if (mapSelectionMode === 'whitelist') {
        if (selectedWhitelist.includes(device.serial_number)) {
          enqueueSnackbar('Device already in whitelist', { variant: 'info' });
        } else {
          // Remove from blacklist if present (device cannot be in both lists)
          const updatedBlacklist = selectedBlacklist.filter(s => s !== device.serial_number);
          const removedFromBlacklist = updatedBlacklist.length < selectedBlacklist.length;
          
          const newWhitelist = [...selectedWhitelist, device.serial_number];
          
          setSelectedWhitelist(newWhitelist);
          if (removedFromBlacklist) {
            setSelectedBlacklist(updatedBlacklist);
          }
          
          setHighlightedAccessList({
            whitelist: newWhitelist,
            blacklist: updatedBlacklist
          });
          
          if (removedFromBlacklist) {
            enqueueSnackbar(`Moved ${device.name || device.serial_number} from blacklist to whitelist`, { variant: 'success' });
          } else {
            enqueueSnackbar(`Added ${device.name || device.serial_number} to whitelist`, { variant: 'success' });
          }
        }
      } else if (mapSelectionMode === 'blacklist') {
        if (selectedBlacklist.includes(device.serial_number)) {
          enqueueSnackbar('Device already in blacklist', { variant: 'info' });
        } else {
          // Remove from whitelist if present (device cannot be in both lists)
          const updatedWhitelist = selectedWhitelist.filter(s => s !== device.serial_number);
          const removedFromWhitelist = updatedWhitelist.length < selectedWhitelist.length;
          
          const newBlacklist = [...selectedBlacklist, device.serial_number];
          
          setSelectedBlacklist(newBlacklist);
          if (removedFromWhitelist) {
            setSelectedWhitelist(updatedWhitelist);
          }
          
          setHighlightedAccessList({
            whitelist: updatedWhitelist,
            blacklist: newBlacklist
          });
          
          if (removedFromWhitelist) {
            enqueueSnackbar(`Moved ${device.name || device.serial_number} from whitelist to blacklist`, { variant: 'success' });
          } else {
            enqueueSnackbar(`Added ${device.name || device.serial_number} to blacklist`, { variant: 'success' });
          }
        }
      }
      return;
    }
    
    // If parent selection mode is active, set the clicked device as parent
    if (parentSelectionMode && deviceSelectingParent) {
      if (device.serial_number === deviceSelectingParent.serial_number) {
        enqueueSnackbar('Cannot set a device as its own parent', { variant: 'warning' });
      } else {
        handleSelectParent(device);
      }
      return;
    }
    
    // If iPerf selection mode is active, use click to select devices
    if (iperfSelectionMode) {
      // If no "from" device is selected, set it
      if (!iperfFromDevice) {
        setIperfFromDevice(device);
        enqueueSnackbar(`Selected "${device.name || device.serial_number}" as source device`, { variant: 'info' });
      }
      // If "from" is selected but not "to", set "to"
      else if (!iperfToDevice) {
        if (device.serial_number === iperfFromDevice.serial_number) {
          enqueueSnackbar('Please select a different device for destination', { variant: 'warning' });
        } else {
          setIperfToDevice(device);
          enqueueSnackbar(`Selected "${device.name || device.serial_number}" as destination device`, { variant: 'info' });
          // Once both devices are selected, show confirmation dialog
          setIperfConfirmDialogOpen(true);
        }
      }
      // Both are selected, allow changing selection
      else {
        // Toggle between from and to
        if (device.serial_number === iperfFromDevice.serial_number) {
          setIperfFromDevice(null);
          setIperfConfirmDialogOpen(false);
          enqueueSnackbar('Cleared source device selection', { variant: 'info' });
        } else if (device.serial_number === iperfToDevice.serial_number) {
          setIperfToDevice(null);
          setIperfConfirmDialogOpen(false);
          enqueueSnackbar('Cleared destination device selection', { variant: 'info' });
        } else {
          // Replace "from" device with new selection
          setIperfFromDevice(device);
          enqueueSnackbar(`Changed source device to "${device.name || device.serial_number}"`, { variant: 'info' });
        }
      }
    } else {
      // Normal behavior: show info window
    setSelectedDevice(device);
    }
  }, [capAim, aimCapAt, iperfSelectionMode, iperfFromDevice, iperfToDevice, parentSelectionMode, deviceSelectingParent, handleSelectParent, mapSelectionMode, deviceForAccessList, selectedWhitelist, selectedBlacklist, enqueueSnackbar]);

  const handleMarkerDoubleClick = useCallback((device) => {
    // Navigate to device details page on double-click
    navigate(`/device/${device.serial_number}`);
  }, [navigate]);

  const handleMarkerRightClick = useCallback(async (device) => {
    const serial = device.serial_number?.toLowerCase();
    if (serial && highlightedTree && highlightedTree.has(serial)) {
      clearHighlightedTree();
      return;
    }

    const hasDetails = await ensureWifiInterfaces();
    if (!hasDetails) {
      return;
    }
    
    // If show all connections / channel mode is active, turn it off to show only this tree
    if (showAllConnections) {
      setShowAllConnections(false);
    }
    if (showChannelMode) {
      setShowChannelMode(false);
    }
    
    // Build and highlight the entire tree on right-click
    const tree = buildDeviceTree(device);
    setHighlightedTree(tree);
    setHighlightedTreeRootSerial(serial);
    setHighlightedTreeMode('subtree');
  }, [buildDeviceTree, highlightedTree, showAllConnections, showChannelMode, ensureWifiInterfaces, clearHighlightedTree]);

  const handleToggleShowConnections = useCallback(async () => {
    if (!showAllConnections && !hasWifiInterfaceData) {
      const hasDetails = await ensureWifiInterfaces();
      if (!hasDetails) {
        return;
      }
    }
    setShowAllConnections((prev) => {
      if (!prev) setShowChannelMode(false);
      return !prev;
    });
  }, [showAllConnections, hasWifiInterfaceData, ensureWifiInterfaces]);

  const handleToggleChannelMode = useCallback(async () => {
    if (!showChannelMode && !hasWifiInterfaceData) {
      const hasDetails = await ensureWifiInterfaces();
      if (!hasDetails) {
        return;
      }
    }
    setShowChannelMode((prev) => {
      if (!prev) setShowAllConnections(false);
      return !prev;
    });
  }, [showChannelMode, hasWifiInterfaceData, ensureWifiInterfaces]);

  const openIperfViewPicker = useCallback(() => {
    setIperfTestRuns([]);
    setIperfTestRunsLoading(true);
    setIperfViewDialogOpen(true);
    api.listIperfTestRuns().then((runs) => {
      setIperfTestRuns(Array.isArray(runs) ? runs : []);
    }).catch(() => {
      setIperfTestRuns([]);
    }).finally(() => setIperfTestRunsLoading(false));
  }, []);

  const handleShowDeviceTree = useCallback(async (device, mode = 'subtree') => {
    const rootSerial = device?.serial_number?.toLowerCase();
    if (!rootSerial) return;

    if (
      highlightedTreeRootSerial === rootSerial
      && highlightedTreeMode === mode
      && highlightedTree?.size > 0
    ) {
      clearHighlightedTree();
      return;
    }

    const hasDetails = await ensureWifiInterfaces();
    if (!hasDetails) {
      return;
    }
    // Close the InfoWindow
    setSelectedDevice(null);
    
    // If show all connections / channel mode is active, turn it off to show only this tree
    if (showAllConnections) {
      setShowAllConnections(false);
    }
    if (showChannelMode) {
      setShowChannelMode(false);
    }
    
    const tree = mode === 'children'
      ? buildDirectChildrenHighlight(device)
      : buildDeviceTree(device);
    setHighlightedTree(tree);
    setHighlightedTreeRootSerial(rootSerial);
    setHighlightedTreeMode(mode);
  }, [
    buildDeviceTree,
    buildDirectChildrenHighlight,
    showAllConnections,
    showChannelMode,
    ensureWifiInterfaces,
    highlightedTree,
    highlightedTreeMode,
    highlightedTreeRootSerial,
    clearHighlightedTree,
  ]);

  // Helper function to create curved path points for arrows
  const createCurvedPath = useCallback((start, end, curveDirection, curveAmount) => {
    const midLat = (start.lat + end.lat) / 2;
    const midLng = (start.lng + end.lng) / 2;
    
    // Calculate perpendicular offset for curve
    const dx = end.lng - start.lng;
    const dy = end.lat - start.lat;
    const len = Math.sqrt(dx * dx + dy * dy);
    
    // Perpendicular unit vector
    const perpX = -dy / len;
    const perpY = dx / len;
    
    // Offset amount (proportional to distance)
    const offset = len * curveAmount * curveDirection;
    
    // Control point for the curve
    const controlLat = midLat + perpY * offset;
    const controlLng = midLng + perpX * offset;
    
    // Generate points along a quadratic bezier curve
    const points = [];
    const numPoints = 20;
    for (let i = 0; i <= numPoints; i++) {
      const t = i / numPoints;
      const oneMinusT = 1 - t;
      // Quadratic bezier: (1-t)²·P0 + 2(1-t)t·P1 + t²·P2
      const lat = oneMinusT * oneMinusT * start.lat + 
                 2 * oneMinusT * t * controlLat + 
                 t * t * end.lat;
      const lng = oneMinusT * oneMinusT * start.lng + 
                 2 * oneMinusT * t * controlLng + 
                 t * t * end.lng;
      points.push({ lat, lng });
    }
    
    return { points, controlLat, controlLng };
  }, []);

  const handleClearNeighbors = useCallback(() => {
    setShowNeighborsForDevice(null);
    setNeighborsData(null);
  }, []);

  const handleClearHeardBy = useCallback(() => {
    setHeardByTarget(null);
    setHeardByLinks([]);
    setHeardByDialogOpen(false);
    setHeardByTreeName('');
    setHeardBySegmentDialogOpen(false);
    setHeardBySegmentNodes([]);
    setHeardBySegmentCaps([]);
  }, []);

  const buildHeardBySegmentDeviceLists = useCallback(() => {
    const isCapDevice = (d) => getDeviceType(d?.capabilities) === 'CAP';
    const memberMap = new Map();
    if (heardByTarget?.serial_number) {
      memberMap.set(heardByTarget.serial_number.toLowerCase(), heardByTarget);
    }
    (heardByLinks || []).forEach((link) => {
      if (link.scannerDevice) {
        memberMap.set(link.scannerSerial, link.scannerDevice);
      }
    });
    const members = [...memberMap.values()];
    return {
      nodes: nodeDevicesFromHeardSet(members, isCapDevice),
      caps: resolveCapDevicesForHeardSet(members, devices, isCapDevice),
    };
  }, [heardByTarget, heardByLinks, devices, getDeviceType]);

  const handleShowNeighbors = useCallback(async (device) => {
    if (!device || !device.serial_number) {
      return;
    }

    // Close the InfoWindow
    setSelectedDevice(null);

    setLoadingNeighbors(true);
    handleClearHeardBy();
    try {
      const serial = device.serial_number.toLowerCase();
      const response = await api.getNeighborsReport(serial);
      
      if (response && response.Neighbors && Array.isArray(response.Neighbors)) {
        setNeighborsData(response);
        setShowNeighborsForDevice(device);
        enqueueSnackbar(`Showing ${response.Neighbors.length} neighbors for ${device.name || device.serial_number}`, { variant: 'success' });
      } else {
        enqueueSnackbar('No neighbors data available for this device', { variant: 'info' });
        setNeighborsData(null);
        setShowNeighborsForDevice(null);
      }
    } catch (error) {
      console.error('Error fetching neighbors:', error);
      enqueueSnackbar('Failed to fetch neighbors data', { variant: 'error' });
      setNeighborsData(null);
      setShowNeighborsForDevice(null);
    } finally {
      setLoadingNeighbors(false);
    }
  }, [enqueueSnackbar, handleClearHeardBy]);

  const handleOpenHeardByDialog = useCallback((device) => {
    if (!device?.serial_number) return;
    setSelectedDevice(null);
    handleClearNeighbors();
    setHeardByTarget(device);
    setHeardByDialogOpen(true);
    if (!heardByTreeName) {
      setHeardByTreeName(`${device.name || device.serial_number} heard-by`);
    }
  }, [handleClearNeighbors, heardByTreeName]);

  const handleApplyHeardBy = useCallback(async () => {
    if (!heardByTarget?.serial_number) return;
    const minRssi = Number(heardByMinRssi);
    if (!Number.isFinite(minRssi)) {
      enqueueSnackbar('Enter a valid minimum STA RSSI (dBm).', { variant: 'warning' });
      return;
    }

    setLoadingHeardBy(true);
    try {
      const response = await api.getHeardBy(heardByTarget.serial_number, minRssi);
      const hearers = response?.hearers ?? response?.Hearers ?? [];
      const { links, hearerSerials } = buildHeardByLinksFromResponse(hearers, devicesBySerial);
      setHeardByLinks(links);
      if (links.length === 0) {
        enqueueSnackbar(
          `No devices heard ${heardByTarget.name || heardByTarget.serial_number} at STA RSSI ≥ ${minRssi} dBm`,
          { variant: 'info' },
        );
      } else {
        enqueueSnackbar(
          `${hearerSerials.size} device(s) heard ${heardByTarget.name || heardByTarget.serial_number} (STA ≥ ${minRssi} dBm)`,
          { variant: 'success' },
        );
      }
    } catch (error) {
      console.error('Error loading heard-by overlay:', error);
      enqueueSnackbar(error?.message || 'Failed to load neighbor scans for heard-by view', { variant: 'error' });
      setHeardByLinks([]);
    } finally {
      setLoadingHeardBy(false);
    }
  }, [heardByTarget, heardByMinRssi, devicesBySerial, enqueueSnackbar]);

  const handleShowTmCandidates = useCallback(async (device) => {
    if (!device || !device.serial_number) {
      return;
    }

    // Close the InfoWindow
    setSelectedDevice(null);

    setLoadingTmCandidates(true);
    try {
      const serial = device.serial_number.toLowerCase();
      const response = await api.getTmReport(serial);
      
      if (response && response.candidates && Array.isArray(response.candidates) && response.candidates.length > 0) {
        setTmCandidatesData(response);
        setShowTmCandidatesForDevice(device);
        enqueueSnackbar(`Showing ${response.candidates.length} TM candidates for ${device.name || device.serial_number}`, { variant: 'success' });
      } else {
        enqueueSnackbar('No TM candidates data available for this device', { variant: 'info' });
        setTmCandidatesData(null);
        setShowTmCandidatesForDevice(null);
      }
    } catch (error) {
      console.error('Error fetching TM candidates:', error);
      enqueueSnackbar('Failed to fetch TM candidates data', { variant: 'error' });
      setTmCandidatesData(null);
      setShowTmCandidatesForDevice(null);
    } finally {
      setLoadingTmCandidates(false);
    }
  }, [enqueueSnackbar]);

  const handleClearTmCandidates = useCallback(() => {
    setShowTmCandidatesForDevice(null);
    setTmCandidatesData(null);
  }, []);

  // Handle Set Parent - enter selection mode
  const handleStartParentSelection = useCallback((device) => {
    setParentSelectionMode(true);
    setDeviceSelectingParent(device);
    setSelectedDevice(null); // Close InfoWindow
    enqueueSnackbar(`Click on another device to set as parent for ${device.name || device.serial_number}`, { variant: 'info' });
  }, [enqueueSnackbar]);

  // Debug effect to log when highlightedAccessList changes
  useEffect(() => {
    window._accessListDevices = new Set(); // Reset logging flag when list changes
    window._markerRenderLogged = false; // Reset marker render flag to log next render
  }, [highlightedAccessList]);

  // Handle Access List Dialog
  const fetchSsidProfiles = useCallback(async () => {
    try {
      const [ssidData, vlanData] = await Promise.all([
        api.getSSIDProfiles(),
        api.getVLANProfiles(),
      ]);
      const list = ssidData?.ssids || [];
      setSsidProfiles(list);
      setVlanProfiles(vlanData?.vlans || []);
      return list;
    } catch {
      setSsidProfiles([]);
      setVlanProfiles([]);
      return [];
    }
  }, []);

  useEffect(() => {
    const serial = selectedDevice?.serial_number;
    if (serial) setOutlinedSerial(serial);
  }, [selectedDevice?.serial_number]);

  useEffect(() => {
    fetchSsidProfiles();
  }, [fetchSsidProfiles]);

  useEffect(() => {
    const key = locatedDeviceSetKey(Array.isArray(devices) ? devices : []);
    const filterChanged = filteredDeviceKeyRef.current !== null && key !== filteredDeviceKeyRef.current;
    filteredDeviceKeyRef.current = key;

    const frame = mapFrameRef.current;
    const framedThisChange = frame.token !== seenMapFrameTokenRef.current;
    seenMapFrameTokenRef.current = frame.token;

    const serial = selectedDevice?.serial_number?.toLowerCase?.();
    if (!serial) return;

    const shown = mapDisplayedDevices.find(
      (device) => device.serial_number?.toLowerCase() === serial,
    );
    const broughtIntoView = framedThisChange && frame.serials?.has(serial);
    if (filterChanged && (!shown || (!broughtIntoView && !deviceIsInMapView(mapRef.current, shown)))) {
      setSelectedDevice(null);
      return;
    }

    if (!Array.isArray(devices)) return;
    const updated = devices.find((d) => d.serial_number?.toLowerCase() === serial);
    if (!updated) return;
    setSelectedDevice((prev) => {
      if (!prev || prev.serial_number?.toLowerCase() !== serial) return prev;
      return { ...prev, ...updated };
    });
  }, [devices, mapDisplayedDevices, selectedDevice?.serial_number]);

  const applyDeviceSnapshotToMapSelection = useCallback((updated) => {
    const serialLower = updated?.serial_number?.toLowerCase?.();
    if (!serialLower) return;
    setSelectedDevice((prev) => (
      prev && prev.serial_number?.toLowerCase() === serialLower ? { ...prev, ...updated } : prev
    ));
    setAssignSsidDevice((prev) => (
      prev && prev.serial_number?.toLowerCase() === serialLower ? { ...prev, ...updated } : prev
    ));
  }, []);

  const refreshDeviceAfterSsidChange = useCallback(async (serial, { light = false } = {}) => {
    const serialLower = serial?.toLowerCase?.();
    await fetchSsidProfiles();

    if (light && serialLower) {
      try {
        const updated = await api.getDevice(serial);
        applyDeviceSnapshotToMapSelection(updated);
      } catch (err) {
        console.error('Failed to refresh device after SSID change', err);
      }
      return;
    }

    let refreshedList = devices;
    if (onRefresh) {
      try {
        const result = await onRefresh({ force: true });
        if (Array.isArray(result)) refreshedList = result;
      } catch (err) {
        console.error('Failed to refresh devices after SSID change', err);
      }
    }
    if (!serialLower || !Array.isArray(refreshedList)) return;
    const updated = refreshedList.find((d) => d.serial_number?.toLowerCase() === serialLower);
    if (updated) {
      applyDeviceSnapshotToMapSelection(updated);
    }
  }, [devices, fetchSsidProfiles, onRefresh, applyDeviceSnapshotToMapSelection]);

  const handleOpenAssignSsidDialog = useCallback((device) => {
    setAssignSsidDevice(device);
    setAssignSsidId('');
    setAssignSsidTreeId('');
    setAssignSsidCapTreeIds([]);
    setAssignSsidDialogOpen(true);
    setSsidProfilesLoading(true);
    const isCap = deviceIsJnet2Cap(device, getDeviceType);
    const configPromise = isCap
      ? api.getDeviceConfig(device.serial_number?.toLowerCase?.() || device.serial_number)
      : Promise.resolve(null);
    setAssignSsidConfigLoading(isCap);
    Promise.all([
      fetchSsidProfiles(),
      configPromise,
    ])
      .then(([list, cfgResp]) => {
        const assigned = ssidsAssignedToDevice(list, device);
        const firstAssigned = assigned[0];
        setAssignSsidId(firstAssigned?.id || '');
        if (isCap) {
          const treeIds = capUpperTreeIdsFromConfig(cfgResp?.configuration);
          setAssignSsidCapTreeIds(treeIds);
          const savedTreeId = firstAssigned?.device_tree_ids?.[device.serial_number?.toLowerCase?.()];
          setAssignSsidTreeId(savedTreeId || treeIds[0] || '');
        }
      })
      .finally(() => {
        setSsidProfilesLoading(false);
        setAssignSsidConfigLoading(false);
      });
  }, [fetchSsidProfiles, getDeviceType]);

  const selectedAssignSsidProfile = useMemo(
    () => ssidProfiles.find((p) => p.id === assignSsidId) || null,
    [ssidProfiles, assignSsidId],
  );
  const assignSsidNeedsCapTreeSelect = useMemo(() => (
    deviceIsJnet2Cap(assignSsidDevice, getDeviceType)
    && ssidBandNeedsCapTreeSelect(selectedAssignSsidProfile?.band)
  ), [assignSsidDevice, getDeviceType, selectedAssignSsidProfile]);

  const handleAssignSsidToDevice = useCallback(async () => {
    const serial = assignSsidDevice?.serial_number?.toLowerCase?.();
    if (!assignSsidId || !serial) return;
    if (assignSsidNeedsCapTreeSelect && !assignSsidTreeId) {
      enqueueSnackbar('Select a CAP radio slot (tree_id) for this SSID', { variant: 'warning' });
      return;
    }
    setAssignSsidSaving(true);
    try {
      const res = await api.assignSSIDToDevice(assignSsidId, serial, {
        treeId: assignSsidNeedsCapTreeSelect ? assignSsidTreeId : undefined,
      });
      summarizeSsidApplyResults(
        enqueueSnackbar,
        res?.apply_results,
        serial,
      );
      setAssignSsidDialogOpen(false);
      setAssignSsidDevice(null);
      setAssignSsidId('');
      setAssignSsidTreeId('');
      setAssignSsidCapTreeIds([]);
      await refreshDeviceAfterSsidChange(serial);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to assign SSID', { variant: 'error' });
    } finally {
      setAssignSsidSaving(false);
    }
  }, [
    assignSsidDevice,
    assignSsidId,
    assignSsidNeedsCapTreeSelect,
    assignSsidTreeId,
    enqueueSnackbar,
    refreshDeviceAfterSsidChange,
  ]);

  const handleUnassignSsidFromDevice = useCallback(async (ssidId, device) => {
    const serial = device?.serial_number?.toLowerCase?.();
    if (!ssidId || !serial || ssidUnassignInProgress) return;
    setSsidUnassignInProgress(true);
    if (assignSsidId === ssidId) {
      setAssignSsidId('');
    }
    try {
      await api.unassignSSIDFromDevice(ssidId, serial);
      enqueueSnackbar('SSID profile unassigned from device', { variant: 'success' });
      await refreshDeviceAfterSsidChange(serial, { light: assignSsidDialogOpen });
      if (assignSsidDialogOpen && assignSsidDevice?.serial_number?.toLowerCase() === serial) {
        const list = await fetchSsidProfiles();
        const remaining = ssidsAssignedToDevice(list, assignSsidDevice);
        setAssignSsidId(remaining[0]?.id || '');
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to unassign SSID', { variant: 'error' });
    } finally {
      setSsidUnassignInProgress(false);
    }
  }, [
    assignSsidDialogOpen,
    assignSsidDevice,
    assignSsidId,
    enqueueSnackbar,
    fetchSsidProfiles,
    refreshDeviceAfterSsidChange,
    ssidUnassignInProgress,
  ]);

  const handleChipUnassignSsid = useCallback((event, ssidId, device) => {
    event.preventDefault();
    event.stopPropagation();
    assignSsidDialogSuppressCloseRef.current = true;
    window.setTimeout(() => {
      handleUnassignSsidFromDevice(ssidId, device).finally(() => {
        window.setTimeout(() => {
          assignSsidDialogSuppressCloseRef.current = false;
        }, 400);
      });
    }, 0);
  }, [handleUnassignSsidFromDevice]);

  const handleOpenAccessListDialog = useCallback(async (device) => {
    setDeviceForAccessList(device);
    setSelectedDevice(null); // Close InfoWindow
    setAccessListDialogOpen(true);
    setAccessListLoading(true);
    window._accessListLogged = false; // Reset debug flag
    
    // Fetch current access list from backend
    try {
      const accessListData = await api.getAccessList(device.serial_number);
      const currentWhitelist = accessListData.whitelist ? accessListData.whitelist.split(',').filter(Boolean) : [];
      const currentBlacklist = accessListData.blacklist ? accessListData.blacklist.split(',').filter(Boolean) : [];
      
      setSelectedWhitelist(currentWhitelist);
      setSelectedBlacklist(currentBlacklist);
      // Set highlighted devices for visual marking on map
      setHighlightedAccessList({
        whitelist: currentWhitelist,
        blacklist: currentBlacklist
      });
    } catch (error) {
      console.error('Error fetching access list:', error);
      // Fallback to tm_config if API call fails
      const tmConfig = device?.tm_config || {};
      const currentWhitelist = tmConfig.white_list ? tmConfig.white_list.split(',').filter(Boolean) : [];
      const currentBlacklist = tmConfig.black_list ? tmConfig.black_list.split(',').filter(Boolean) : [];
      setSelectedWhitelist(currentWhitelist);
      setSelectedBlacklist(currentBlacklist);
      setHighlightedAccessList({
        whitelist: currentWhitelist,
        blacklist: currentBlacklist
      });
      enqueueSnackbar('Failed to load access list from server, showing local data', { variant: 'warning' });
    } finally {
      setAccessListLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleCloseAccessListDialog = useCallback(() => {
    setAccessListDialogOpen(false);
    setDeviceForAccessList(null);
    setSelectedWhitelist([]);
    setSelectedBlacklist([]);
    setAccessListUpdateStatus({ type: '', message: '' });
    setMapSelectionMode(null); // Exit map selection mode
    setHighlightedAccessList({ whitelist: [], blacklist: [] }); // Clear visual markers
  }, []);

  const handleSetAccessList = useCallback(async () => {
    if (!deviceForAccessList) return;
    
    try {
      await api.setAccessList(deviceForAccessList.serial_number, selectedWhitelist, selectedBlacklist);
      setAccessListUpdateStatus({ type: 'success', message: 'Access lists updated successfully' });
      
      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseAccessListDialog();
        if (onRefresh) {
          onRefresh();
        }
      }, 1500);
    } catch (err) {
      console.error('Error updating access lists:', err);
      setAccessListUpdateStatus({
        type: 'error',
        message: err.response?.data?.message || err.message || 'Failed to update access lists'
      });
    }
  }, [deviceForAccessList, selectedWhitelist, selectedBlacklist, handleCloseAccessListDialog, onRefresh]);

  const handleWhitelistChange = useCallback((event) => {
    const { target: { value } } = event;
    const newList = typeof value === 'string' ? value.split(',') : value;
    setSelectedWhitelist(newList);
    // Update visual markers on map
    setHighlightedAccessList(current => ({
      ...current,
      whitelist: newList
    }));
  }, []);

  const handleBlacklistChange = useCallback((event) => {
    const { target: { value } } = event;
    const newList = typeof value === 'string' ? value.split(',') : value;
    setSelectedBlacklist(newList);
    // Update visual markers on map
    setHighlightedAccessList(current => ({
      ...current,
      blacklist: newList
    }));
  }, []);

  const handleDialogClose = useCallback(() => {
    setSelectionDialogOpen(false);
    setPolygons([]);
    setSelectedDevices([]);
    setSelectedCaps([]);
    setSelectedNodes([]);
    setCustomTreeName('');
    setSelectedTree(null);
    setAddToExistingTree(null);
    setAddToExistingNodes([]);
    setAddToExistingCaps([]);
    setBgScanForm(EMPTY_BG_SCAN_BULK_FORM);
  }, []);

  const handleMapClick = useCallback(async (event) => {
    // Don't handle map clicks when in drawing mode (polygon drawing handles its own clicks)
    if (drawingMode) {
      return;
    }

    // Aiming a CAP antenna: the click sets the direction from the CAP to it.
    if (capAim && event.latLng) {
      aimCapAt({ lat: event.latLng.lat(), lng: event.latLng.lng() });
      return;
    }
    
    // Cancel map selection mode for access list if clicking on the map
    if (mapSelectionMode) {
      setMapSelectionMode(null);
      enqueueSnackbar('Map selection cancelled', { variant: 'info' });
      return;
    }
    
    // Cancel parent selection mode if clicking on the map
    if (parentSelectionMode) {
      setParentSelectionMode(false);
      setDeviceSelectingParent(null);
      enqueueSnackbar('Parent selection cancelled', { variant: 'info' });
      return;
    }
    
    // Handle placing device if one is selected for GPS assignment
    if (selectedDeviceForGPS && event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      
      try {
        // Optimistically add the device to the map with temporary GPS
        setTempGPSUpdates(prev => ({
          ...prev,
          [selectedDeviceForGPS.serial_number]: { lat, lng }
        }));
        
        await api.updateDeviceGPS(selectedDeviceForGPS.serial_number, lat, lng);
        enqueueSnackbar(
          `GPS location set for ${getDeviceDisplayName(selectedDeviceForGPS)}`,
          { variant: 'success' },
        );
        setSelectedDeviceForGPS(null);
        if (onRefresh) {
          await onRefresh({ force: true });
        }
      } catch (error) {
        // Remove the temporary GPS on error
        setTempGPSUpdates(prev => {
          const updated = { ...prev };
          delete updated[selectedDeviceForGPS.serial_number];
          return updated;
        });
        enqueueSnackbar(`Failed to set GPS location: ${error.message}`, { variant: 'error' });
        setSelectedDeviceForGPS(null);
      }
      return;
    }
    
    // Keep map popup / manage SSID open while the SSID dialog is active
    if (assignSsidDialogOpen || assignSsidDialogSuppressCloseRef.current) {
      return;
    }

    // Close InfoWindow when clicking on the map
    setSelectedDevice(null);
    
    // Clear highlighted tree
    clearHighlightedTree();
    
    // Clear neighbors visualization
    handleClearNeighbors();
    
    // Clear TM candidates visualization
    handleClearTmCandidates();
    
    // If dialog is open, close it and clear polygons
    if (selectionDialogOpen) {
      handleDialogClose();
    }
  }, [
    drawingMode,
    capAim,
    aimCapAt,
    selectedDeviceForGPS,
    selectionDialogOpen,
    parentSelectionMode,
    mapSelectionMode,
    assignSsidDialogOpen,
    handleDialogClose,
    handleClearNeighbors,
    handleClearTmCandidates,
    clearHighlightedTree,
    enqueueSnackbar,
    onRefresh,
  ]);

  useEffect(() => {
    if (!selectedDevice?.serial_number) {
      setMetadataEditName('');
      return;
    }
    setMetadataEditName(selectedDevice.display_name || selectedDevice.name || '');
  }, [selectedDevice?.serial_number, selectedDevice?.display_name, selectedDevice?.name]);

  const refreshDevicesAfterMetadataChange = useCallback(async (serial) => {
    if (!onRefresh) return null;
    try {
      const result = await onRefresh({ force: true });
      if (Array.isArray(result) && serial) {
        const updated = result.find(
          (d) => d.serial_number?.toLowerCase() === serial.toLowerCase(),
        );
        if (updated) {
          setSelectedDevice((prev) => (
            prev && prev.serial_number?.toLowerCase() === serial.toLowerCase()
              ? { ...prev, ...updated, lat: prev.lat, lng: prev.lng }
              : prev
          ));
        }
      }
      return result;
    } catch (err) {
      console.error('Failed to refresh devices after metadata change', err);
      return null;
    }
  }, [onRefresh]);

  const handleSaveDisplayName = useCallback(async () => {
    if (!selectedDevice?.serial_number || metadataSaving) return;
    const serial = selectedDevice.serial_number;
    setMetadataSaving(true);
    try {
      await api.patchDeviceMetadata(serial, { displayName: metadataEditName.trim() });
      await refreshDevicesAfterMetadataChange(serial);
      enqueueSnackbar('Display name saved', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to save display name', { variant: 'error' });
    } finally {
      setMetadataSaving(false);
    }
  }, [selectedDevice, metadataEditName, metadataSaving, refreshDevicesAfterMetadataChange, enqueueSnackbar]);

  const patchSelectedDevice = useCallback((serial, fields) => {
    setSelectedDevice((prev) => (
      prev && prev.serial_number?.toLowerCase() === serial.toLowerCase()
        ? { ...prev, ...fields }
        : prev
    ));
  }, []);

  // Refresh replaces selectedDevice from GET /devices; re-apply the API result
  // so cleared notes (omitted in the response) and the new segment stick.
  const handleSelectedDeviceFieldsSaved = useCallback(async (serial, fields) => {
    patchSelectedDevice(serial, fields);
    await refreshDevicesAfterMetadataChange(serial);
    patchSelectedDevice(serial, fields);
  }, [patchSelectedDevice, refreshDevicesAfterMetadataChange]);

  const handleToggleApFailsafeRadio = useCallback(async (device, treeId, enabled) => {
    setAcceptAllMenuAnchor(null);
    if (!device?.serial_number || !treeId || acceptAllSaving) return;
    setAcceptAllSaving(true);
    const enable = !enabled;
    try {
      const both = treeId === '*';
      const res = await api.setDeviceApFailsafe(device.serial_number, enable, both ? undefined : treeId);
      const fields = {};
      if (res?.segment) fields.segment = res.segment;
      if (Array.isArray(res?.ap_segments)) fields.ap_segments = res.ap_segments;
      if (Object.keys(fields).length > 0) {
        await handleSelectedDeviceFieldsSaved(device.serial_number, fields);
      }
      const target = both ? 'both AP radios' : treeId;
      let msg = enable
        ? `Accept all clients enabled on ${target}`
        : `Accept all clients disabled on ${target}`;
      if (!res?.config_changed) msg += ' — configuration already matched';
      else if (!res?.pushed && res?.connected) msg += ' — saved; the device did not accept the push';
      else if (!res?.pushed) msg += ' — saved; device offline, applies on reconnect';
      enqueueSnackbar(msg, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to update FEFE', { variant: 'error' });
    } finally {
      setAcceptAllSaving(false);
    }
  }, [acceptAllSaving, enqueueSnackbar, handleSelectedDeviceFieldsSaved]);

  const handleClearManualGps = useCallback(async () => {
    if (!selectedDevice?.serial_number || metadataSaving) return;
    const serial = selectedDevice.serial_number;
    setMetadataSaving(true);
    try {
      await api.patchDeviceMetadata(serial, { clearGpsOverride: true });
      setTempGPSUpdates((prev) => {
        const next = { ...prev };
        delete next[serial];
        return next;
      });
      await refreshDevicesAfterMetadataChange(serial);
      enqueueSnackbar('Manual GPS cleared', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to clear manual GPS', { variant: 'error' });
    } finally {
      setMetadataSaving(false);
    }
  }, [selectedDevice, metadataSaving, refreshDevicesAfterMetadataChange, enqueueSnackbar]);

  const handleLockCurrentDeviceGps = useCallback(async (device) => {
    const target = device || selectedDevice;
    if (!target?.serial_number || metadataSaving) return;
    const reported = getReportedDeviceGps(target);
    if (!reported || !canLockCurrentDeviceGps(target)) {
      enqueueSnackbar('No device-reported GPS available to lock', { variant: 'warning' });
      return;
    }
    const serial = target.serial_number;
    setMetadataSaving(true);
    try {
      await api.patchDeviceMetadata(serial, {
        gpsOverride: {
          lat: reported.lat,
          lng: reported.lng,
          ...(reported.alt != null ? { alt: reported.alt } : {}),
        },
      });
      setTempGPSUpdates((prev) => {
        const next = { ...prev };
        delete next[serial];
        return next;
      });
      await refreshDevicesAfterMetadataChange(serial);
      enqueueSnackbar(
        `Locked current device GPS for ${getDeviceDisplayName(target)}`,
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to lock device GPS', { variant: 'error' });
    } finally {
      setMetadataSaving(false);
    }
  }, [selectedDevice, metadataSaving, refreshDevicesAfterMetadataChange, enqueueSnackbar]);

  // Handle device selection for GPS assignment
  const handleSelectDeviceForGPS = useCallback((device) => {
    setSelectedDeviceForGPS(device);
    // Close any open dialogs/info windows
    setSelectedDevice(null);
    clearHighlightedTree();
  }, [clearHighlightedTree]);

  // Check if a point is inside a polygon using ray casting algorithm
  const isPointInPolygon = useCallback((point, polygon) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].lat, yi = polygon[i].lng;
      const xj = polygon[j].lat, yj = polygon[j].lng;
      
      const intersect = ((yi > point.lng) !== (yj > point.lng))
        && (point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }, []);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    setIsLoading(false);

    // Re-created for a places preset change: the center/zoom props already
    // hold the view the user was on.
    if (keepViewOnMapLoadRef.current) {
      keepViewOnMapLoadRef.current = false;
      return;
    }

    // Center the map on devices when map first loads
    if (devicesWithLocation.length > 0) {
      const { center, zoom } = centerAndZoomForDevices(devicesWithLocation);
      map.panTo(center);
      map.setZoom(zoom);
      hasAutoFittedMapRef.current = true;
    }
  }, [devicesWithLocation]);

  const changePlacesPreset = useCallback((id) => {
    const next = resolvePlacesPreset(id);
    if (next.mapId === placesPreset.mapId) return;
    const map = mapRef.current;
    const center = map?.getCenter?.();
    if (center) {
      setMapCenter({ lat: center.lat(), lng: center.lng() });
      setMapZoom(map.getZoom());
      keepViewOnMapLoadRef.current = true;
    }
    storePlacesPreset(next.id);
    setPlacesPreset(next);
  }, [placesPreset.mapId]);

  const toggleDrawingMode = useCallback(() => {
    setDrawingMode(prev => prev ? null : 'polygon');
  }, []);

  const fetchGroupsForMap = useCallback(async () => {
    setMapGroupsLoading(true);
    try {
      const res = await api.getGroups(false);
      setMapGroups(res?.groups || []);
    } catch (err) {
      enqueueSnackbar('Failed to load groups', { variant: 'error' });
    } finally {
      setMapGroupsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!selectedDevice?.serial_number) {
      setSelectedDeviceMemberGroups([]);
      return undefined;
    }
    const serial = selectedDevice.serial_number.toLowerCase();
    const fromMapGroups = mapGroups.filter((g) =>
      (g.devices || []).some((s) => String(s).toLowerCase() === serial),
    );
    if (mapGroups.length > 0) {
      setSelectedDeviceMemberGroups(fromMapGroups);
      return undefined;
    }
    let cancelled = false;
    api.getGroups(false)
      .then((res) => {
        if (cancelled) return;
        const member = (res?.groups || []).filter((g) =>
          (g.devices || []).some((s) => String(s).toLowerCase() === serial),
        );
        setSelectedDeviceMemberGroups(member);
      })
      .catch(() => {
        if (!cancelled) setSelectedDeviceMemberGroups([]);
      });
    return () => { cancelled = true; };
  }, [selectedDevice?.serial_number, mapGroups]);

  const handleOpenGroupDialog = useCallback((device) => {
    setGroupDialogDevice(device);
    setGroupDialogOpen(true);
  }, []);

  const handleCloseGroupDialog = useCallback(() => {
    setGroupDialogOpen(false);
    setGroupDialogDevice(null);
  }, []);

  const handleGroupDialogChanged = useCallback(() => {
    if (showGroupsOnMap) fetchGroupsForMap();
    if (selectedDevice?.serial_number) {
      const serial = selectedDevice.serial_number.toLowerCase();
      api.getGroups(false)
        .then((res) => {
          const member = (res?.groups || []).filter((g) =>
            (g.devices || []).some((s) => String(s).toLowerCase() === serial),
          );
          setSelectedDeviceMemberGroups(member);
        })
        .catch(() => {});
    }
  }, [showGroupsOnMap, fetchGroupsForMap, selectedDevice?.serial_number]);

  const handleCreateGroupFromMap = useCallback(async () => {
    if (!groupCreationName.trim()) {
      enqueueSnackbar('Please provide a group name', { variant: 'warning' });
      return;
    }
    if (selectedDevices.length === 0) {
      enqueueSnackbar('No devices selected', { variant: 'warning' });
      return;
    }
    setCreatingGroup(true);
    try {
      const serials = selectedDevices.map(d => d.serial_number?.toLowerCase()).filter(Boolean);
      await api.createGroup({ name: groupCreationName.trim(), devices: serials });
      enqueueSnackbar(`Group "${groupCreationName}" created with ${serials.length} device(s)`, { variant: 'success' });
      // Close the selection dialog and reset state
      setSelectionDialogOpen(false);
      setSelectedDevices([]);
      setPolygons([]);
      setDialogTab(0);
      setGroupCreationName('');
      if (showGroupsOnMap) fetchGroupsForMap();
    } catch (err) {
      enqueueSnackbar(err.message || 'Failed to create group', { variant: 'error' });
    } finally {
      setCreatingGroup(false);
    }
  }, [groupCreationName, selectedDevices, enqueueSnackbar, showGroupsOnMap, fetchGroupsForMap]);

  const fetchWhitelistsForSelection = useCallback(async () => {
    setSelectionWhitelistLoading(true);
    const results = {};
    const serials = selectedDevices.map((device) => device.serial_number).filter(Boolean);
    try {
      const response = await api.queryAccessListBatch(serials);
      const bySerial = new Map((response?.results || []).map((row) => [row.serial, row]));
      selectedDevices.forEach((device) => {
        const serial = device.serial_number;
        const row = bySerial.get(serial);
        if (row?.success) {
          results[serial] = {
            whitelist: row.whitelist ? row.whitelist.split(',').filter(Boolean) : [],
            blacklist: row.blacklist ? row.blacklist.split(',').filter(Boolean) : [],
          };
          return;
        }
        const tmConfig = device?.tm_config || {};
        results[serial] = {
          whitelist: tmConfig.white_list ? tmConfig.white_list.split(',').filter(Boolean) : [],
          blacklist: tmConfig.black_list ? tmConfig.black_list.split(',').filter(Boolean) : [],
        };
      });
    } catch (err) {
      selectedDevices.forEach((device) => {
        const tmConfig = device?.tm_config || {};
        results[device.serial_number] = {
          whitelist: tmConfig.white_list ? tmConfig.white_list.split(',').filter(Boolean) : [],
          blacklist: tmConfig.black_list ? tmConfig.black_list.split(',').filter(Boolean) : [],
        };
      });
    } finally {
      setSelectionWhitelistData(results);
      setSelectionWhitelistLoading(false);
    }
  }, [selectedDevices]);

  const handleClearWhitelist = useCallback(async (serial) => {
    setClearingWhitelistSerials(prev => new Set([...prev, serial]));
    try {
      const existing = selectionWhitelistData[serial] || {};
      const response = await api.applyAccessListBatch({
        updates: [{
          serial,
          whitelist: '',
          blacklist: (existing.blacklist || []).join(','),
        }],
      });
      const row = api.mapBatchOperationResults(response)[0];
      if (!row?.success) {
        throw new Error(row?.message || 'Failed to clear whitelist');
      }
      setSelectionWhitelistData(prev => ({
        ...prev,
        [serial]: { ...prev[serial], whitelist: [] }
      }));
      enqueueSnackbar(`Whitelist cleared for ${serial}`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear whitelist for ${serial}`, { variant: 'error' });
    } finally {
      setClearingWhitelistSerials(prev => {
        const next = new Set(prev);
        next.delete(serial);
        return next;
      });
    }
  }, [selectionWhitelistData, enqueueSnackbar]);

  const handleClearAllWhitelists = useCallback(async () => {
    const serials = selectedDevices.map(d => d.serial_number);
    setClearingWhitelistSerials(new Set(serials));
    try {
      const response = await api.applyAccessListBatch({
        updates: serials.map((serial) => {
          const existing = selectionWhitelistData[serial] || {};
          return {
            serial,
            whitelist: '',
            blacklist: (existing.blacklist || []).join(','),
          };
        }),
      });
      const results = api.mapBatchOperationResults(response);
      const failed = results.filter((r) => !r.success).length;
      setSelectionWhitelistData(prev => {
        const next = { ...prev };
        serials.forEach(serial => {
          next[serial] = { ...next[serial], whitelist: [] };
        });
        return next;
      });
      if (failed > 0) {
        enqueueSnackbar(`Cleared whitelists (${failed} failed)`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Whitelists cleared for ${serials.length} devices`, { variant: 'success' });
      }
    } finally {
      setClearingWhitelistSerials(new Set());
    }
  }, [selectedDevices, selectionWhitelistData, enqueueSnackbar]);

  const handleClearBlacklist = useCallback(async (serial) => {
    setClearingBlacklistSerials(prev => new Set([...prev, serial]));
    try {
      const existing = selectionWhitelistData[serial] || {};
      const response = await api.applyAccessListBatch({
        updates: [{
          serial,
          whitelist: (existing.whitelist || []).join(','),
          blacklist: '',
        }],
      });
      const row = api.mapBatchOperationResults(response)[0];
      if (!row?.success) {
        throw new Error(row?.message || 'Failed to clear blacklist');
      }
      setSelectionWhitelistData(prev => ({
        ...prev,
        [serial]: { ...prev[serial], blacklist: [] }
      }));
      enqueueSnackbar(`Blacklist cleared for ${serial}`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear blacklist for ${serial}`, { variant: 'error' });
    } finally {
      setClearingBlacklistSerials(prev => {
        const next = new Set(prev);
        next.delete(serial);
        return next;
      });
    }
  }, [selectionWhitelistData, enqueueSnackbar]);

  const handleClearAllBlacklists = useCallback(async () => {
    const serials = selectedDevices.map(d => d.serial_number);
    setClearingBlacklistSerials(new Set(serials));
    try {
      const response = await api.applyAccessListBatch({
        updates: serials.map((serial) => {
          const existing = selectionWhitelistData[serial] || {};
          return {
            serial,
            whitelist: (existing.whitelist || []).join(','),
            blacklist: '',
          };
        }),
      });
      const results = api.mapBatchOperationResults(response);
      const failed = results.filter((r) => !r.success).length;
      setSelectionWhitelistData(prev => {
        const next = { ...prev };
        serials.forEach(serial => {
          next[serial] = { ...next[serial], blacklist: [] };
        });
        return next;
      });
      if (failed > 0) {
        enqueueSnackbar(`Cleared blacklists (${failed} failed)`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Blacklists cleared for ${serials.length} devices`, { variant: 'success' });
      }
    } finally {
      setClearingBlacklistSerials(new Set());
    }
  }, [selectedDevices, selectionWhitelistData, enqueueSnackbar]);

  useEffect(() => {
    if ((dialogTab === 7 || dialogTab === 8) && selectionDialogOpen && selectedDevices.length > 0) {
      fetchWhitelistsForSelection();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogTab, selectionDialogOpen]);

  const handleRunIperf = useCallback(async () => {
    if (!iperfFromDevice || !iperfToDevice) {
      enqueueSnackbar('Please select both devices', { variant: 'warning' });
      return;
    }

    if (iperfFromDevice.serial_number === iperfToDevice.serial_number) {
      enqueueSnackbar('Please select two different devices', { variant: 'warning' });
      return;
    }

    setIperfRunning(true);
    setIperfResults(null);

    try {
      // Calculate start time (5 seconds from now to allow both commands to be sent)
      const startTime = Math.floor(Date.now() / 1000) + 5;
      const duration = 20; // 20 seconds test duration

      // Run both iPerf commands in parallel
      const [serverResult, clientResult] = await Promise.all([
        // Server on "To" device
        api.runIperf(iperfToDevice.serial_number, {
          serial_number: iperfToDevice.serial_number,
          start_time: startTime,
          duration: duration,
          protocol: 'UDP',
          port: 5001,
          udp_bandwidth: 2000,
          role: 'server'
        }),
        // Client on "From" device
        api.runIperf(iperfFromDevice.serial_number, {
          serial_number: iperfFromDevice.serial_number,
          start_time: startTime,
          duration: duration,
          protocol: 'UDP',
          port: 5001,
          udp_bandwidth: 2000,
          role: 'client',
          ip: iperfToDevice.peer_address || ''
        })
      ]);

      setIperfResults({
        server: serverResult,
        client: clientResult,
        fromDevice: iperfFromDevice,
        toDevice: iperfToDevice
      });

      enqueueSnackbar('iPerf test completed successfully', { variant: 'success' });
    } catch (error) {
      console.error('iPerf test failed:', error);
      enqueueSnackbar(error?.message || 'iPerf test failed', { variant: 'error' });
    } finally {
      setIperfRunning(false);
    }
  }, [iperfFromDevice, iperfToDevice, enqueueSnackbar]);

  const handleFreehandPolygonComplete = useCallback((coordinates) => {
    if (!allowBackhaulWrite) {
      setDrawingMode(null);
      return;
    }
    // Find devices within the polygon
    const devicesInPolygon = mapDisplayedDevices.filter(device => 
      isPointInPolygon({ lat: device.lat, lng: device.lng }, coordinates)
    );

    if (devicesInPolygon.length > 0) {
      setSelectedDevices(devicesInPolygon);
      const capsInPolygon = devicesInPolygon.filter(device => getDeviceType(device.capabilities) === 'CAP');
      setSelectedCaps(capsInPolygon);
      const nodesInPolygon = devicesInPolygon.filter(device => getDeviceType(device.capabilities) === 'Node');
      setSelectedNodes(nodesInPolygon);
      setSelectionDialogOpen(true);
    } else {
      enqueueSnackbar('No devices in the selected area', { variant: 'info' });
    }

    // Store polygon for visualization
    setPolygons(prev => [...prev, { coordinates, id: Date.now() }]);

    // Reset drawing mode
    setDrawingMode(null);
  }, [allowBackhaulWrite, mapDisplayedDevices, isPointInPolygon, getDeviceType, enqueueSnackbar]);

  // Setup polygon drawing listeners (point-by-point with live preview)
  useEffect(() => {
    if (!mapRef.current || !drawingMode) {
      // Clean up any existing listeners
      drawingListenersRef.current.forEach(listener => {
        if (listener && listener.remove) {
          listener.remove();
        }
      });
      drawingListenersRef.current = [];
      
      // Clean up any leftover polylines (both drawn segments and preview)
      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.setMap(null);
        drawingPolygonRef.current = null;
      }
      if (drawingPolylineRef.current) {
        drawingPolylineRef.current.setMap(null);
        drawingPolylineRef.current = null;
      }
      isDrawingRef.current = false;
      currentPathRef.current = [];
      
      return;
    }

    const map = mapRef.current;

    // Helper to check if a point is close to another point (for closing polygon)
    // Using pixel-based distance instead of lat/lng for more consistent behavior
    const isNearPoint = (point1, point2) => {
      if (!map) return false;
      
      const projection = map.getProjection();
      if (!projection) return false;
      
      const point1Pixel = projection.fromLatLngToPoint(new window.google.maps.LatLng(point1.lat, point1.lng));
      const point2Pixel = projection.fromLatLngToPoint(new window.google.maps.LatLng(point2.lat, point2.lng));
      
      if (!point1Pixel || !point2Pixel) return false;
      
      const scale = Math.pow(2, map.getZoom());
      const pixelDistance = Math.sqrt(
        Math.pow((point1Pixel.x - point2Pixel.x) * scale, 2) +
        Math.pow((point1Pixel.y - point2Pixel.y) * scale, 2)
      );
      
      // Close if within 15 pixels
      return pixelDistance < 15;
    };

    // Mouse move - show preview line from last point to cursor
    const mouseMoveListener = map.addListener('mousemove', (e) => {
      if (!e.latLng || currentPathRef.current.length === 0) return;
      
      const currentPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const previewPath = [...currentPathRef.current, currentPoint];
      
      // Update or create preview polyline
      if (window.google && window.google.maps) {
        if (!drawingPolylineRef.current) {
          drawingPolylineRef.current = new window.google.maps.Polyline({
            path: previewPath,
            strokeColor: '#1976d2',
            strokeOpacity: 0.7,
            strokeWeight: 2,
            map: map,
            clickable: false
          });
        } else {
          drawingPolylineRef.current.setPath(previewPath);
        }
      }
    });

    // Click to add points to polygon
    const clickListener = map.addListener('click', (e) => {
      if (!e.latLng) return;
      
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      
      // Check if clicking near the first point to close the polygon
      if (currentPathRef.current.length >= 3 && isNearPoint(point, currentPathRef.current[0])) {
        // Close the polygon
        handleFreehandPolygonComplete(currentPathRef.current);
        
        // Clean up
        currentPathRef.current = [];
        if (drawingPolygonRef.current) {
          drawingPolygonRef.current.setMap(null);
          drawingPolygonRef.current = null;
        }
        if (drawingPolylineRef.current) {
          drawingPolylineRef.current.setMap(null);
          drawingPolylineRef.current = null;
        }
        return;
      }
      
      // Add point to path
      currentPathRef.current.push(point);

      // Update the drawn segments (use polyline, not polygon, so it doesn't auto-close)
      if (window.google && window.google.maps) {
        if (drawingPolygonRef.current) {
          drawingPolygonRef.current.setMap(null);
        }
        
        // Use Polyline for the drawn segments (doesn't auto-close)
        drawingPolygonRef.current = new window.google.maps.Polyline({
          path: currentPathRef.current,
          strokeColor: '#1976d2',
          strokeOpacity: 1.0,
          strokeWeight: 3,
          map: map,
          clickable: false
        });
      }
    });

    drawingListenersRef.current = [clickListener, mouseMoveListener];

    return () => {
      drawingListenersRef.current.forEach(listener => {
        if (listener && listener.remove) {
          listener.remove();
        }
      });
      drawingListenersRef.current = [];
    };
  }, [drawingMode, handleFreehandPolygonComplete]);

  const fetchCustomTrees = useCallback(async () => {
    setCustomTreesLoading(true);
    try {
      const response = await api.getCustomTrees();
      setCustomTrees(response?.trees || []);
    } catch (error) {
      console.error('Failed to fetch custom trees:', error);
      const errorMessage = error?.message || 'Failed to load custom trees';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setCustomTrees([]);
    } finally {
      setCustomTreesLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleOpenHeardBySegmentDialog = useCallback(() => {
    if (!heardByTarget?.serial_number) return;
    if (heardByLinks.length === 0) {
      enqueueSnackbar('Run the heard-by scan first', { variant: 'warning' });
      return;
    }

    const { nodes, caps } = buildHeardBySegmentDeviceLists();
    if (nodes.length === 0) {
      enqueueSnackbar('Heard-by set has no Node devices for a custom tree', { variant: 'warning' });
      return;
    }
    if (caps.length === 0) {
      enqueueSnackbar('Could not resolve CAP device(s) for the heard-by set', { variant: 'warning' });
      return;
    }

    setHeardBySegmentNodes(nodes);
    setHeardBySegmentCaps(caps);
    if (!heardByTreeName.trim()) {
      setHeardByTreeName(`${heardByTarget.name || heardByTarget.serial_number} heard-by`);
    }
    setHeardByDialogOpen(false);
    setHeardBySegmentDialogOpen(true);
  }, [
    heardByTarget,
    heardByLinks,
    heardByTreeName,
    buildHeardBySegmentDeviceLists,
    enqueueSnackbar,
  ]);

  const fetchSubtreeSegmentConfigs = useCallback(async (nodes) => {
    const serials = [...new Set(
      (nodes || []).map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
    )];
    if (serials.length === 0) {
      setSubtreeSegmentConfigBySerial(new Map());
      return;
    }
    setLoadingSubtreeSegmentConfig(true);
    try {
      const resp = await api.queryDeviceConfigBatch(serials);
      const next = new Map();
      (resp?.results || []).forEach((row) => {
        const serial = row.serial?.toLowerCase();
        if (!serial || !row.success) return;
        const configuration = row.configuration;
        if (configuration && typeof configuration === 'object') {
          next.set(serial, configuration);
        }
      });
      setSubtreeSegmentConfigBySerial(next);
    } finally {
      setLoadingSubtreeSegmentConfig(false);
    }
  }, []);

  const handleApplySubtreeSegmentFilters = useCallback(async () => {
    if (!subtreeSegmentTarget) return false;
    if (subtreeSegmentSignalFilter !== 'all') {
      const minRssi = Number(subtreeSegmentMinRssi);
      if (!Number.isFinite(minRssi)) {
        enqueueSnackbar('Enter a valid minimum RSSI (dBm).', { variant: 'warning' });
        return false;
      }
    }
    const minRssi = parseInt(subtreeSegmentMinRssi, 10);
    const preview = buildSubtreeSegmentLists(
      subtreeSegmentTarget,
      deviceCatalog,
      {
        connectedOnly: subtreeSegmentConnectedOnly,
        signalFilter: subtreeSegmentSignalFilter,
        minRssi,
      },
      getDeviceType,
    );
    setSubtreeSegmentNodes(preview.nodes);
    setSubtreeSegmentCaps(preview.caps);
    await fetchSubtreeSegmentConfigs(preview.nodes);
    return true;
  }, [
    subtreeSegmentTarget,
    subtreeSegmentConnectedOnly,
    subtreeSegmentSignalFilter,
    subtreeSegmentMinRssi,
    deviceCatalog,
    getDeviceType,
    enqueueSnackbar,
    fetchSubtreeSegmentConfigs,
  ]);

  const handleOpenSubtreeSegmentDialog = useCallback((device) => {
    if (!device?.serial_number) return;
    setSelectedDevice(null);
    setSubtreeSegmentTarget(device);
    setSubtreeSegmentName(`${device.name || device.serial_number} subtree`);
    setSubtreeSegmentConnectedOnly(true);
    setSubtreeSegmentSignalFilter('all');
    setSubtreeSegmentMinRssi('-75');
    setSubtreeSegmentConfigBySerial(new Map());
    setSubtreeSegmentNodes([]);
    setSubtreeSegmentCaps([]);
    setSubtreeSegmentDialogOpen(true);
  }, []);

  useEffect(() => {
    if (!subtreeSegmentDialogOpen || !subtreeSegmentTarget) return;
    handleApplySubtreeSegmentFilters();
  }, [subtreeSegmentDialogOpen, subtreeSegmentTarget]); // eslint-disable-line react-hooks/exhaustive-deps -- initial apply only on open

  const handleCreateSubtreeSegment = useCallback(async () => {
    if (!subtreeSegmentName.trim()) {
      enqueueSnackbar('Provide a segment name', { variant: 'warning' });
      return;
    }
    if (subtreeSegmentNodes.length === 0) {
      enqueueSnackbar('Custom trees must include at least one Node device', { variant: 'warning' });
      return;
    }
    if (subtreeSegmentCaps.length === 0) {
      enqueueSnackbar('Select at least one CAP device for the custom tree', { variant: 'warning' });
      return;
    }

    setCreatingSubtreeSegment(true);
    try {
      const response = await api.createCustomTree({
        name: subtreeSegmentName.trim(),
        devices: subtreeSegmentNodes.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
        caps: subtreeSegmentCaps.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
      });
      const message = response?.segment_id
        ? `Custom tree "${response.name || subtreeSegmentName}" created (segment ${response.segment_id})`
        : `Custom tree "${response?.name || subtreeSegmentName}" created`;
      enqueueSnackbar(message, { variant: 'success' });
      fetchCustomTrees();
      setSubtreeSegmentDialogOpen(false);
      setSubtreeSegmentTarget(null);
      setSubtreeSegmentName('');
      setSubtreeSegmentNodes([]);
      setSubtreeSegmentCaps([]);
      setSubtreeSegmentConfigBySerial(new Map());
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to create subtree segment', { variant: 'error' });
    } finally {
      setCreatingSubtreeSegment(false);
    }
  }, [
    subtreeSegmentName,
    subtreeSegmentNodes,
    subtreeSegmentCaps,
    enqueueSnackbar,
    fetchCustomTrees,
  ]);

  const handleCreateHeardBySegment = useCallback(async () => {
    if (!heardByTreeName.trim()) {
      enqueueSnackbar('Provide a custom tree name', { variant: 'warning' });
      return;
    }
    if (heardBySegmentNodes.length === 0) {
      enqueueSnackbar('Custom trees must include at least one Node device', { variant: 'warning' });
      return;
    }
    if (heardBySegmentCaps.length === 0) {
      enqueueSnackbar('Select at least one CAP device for the custom tree', { variant: 'warning' });
      return;
    }

    setCreatingHeardBySegment(true);
    try {
      const response = await api.createCustomTree({
        name: heardByTreeName.trim(),
        devices: heardBySegmentNodes.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
        caps: heardBySegmentCaps.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
      });
      const message = response?.segment_id
        ? `Custom tree "${response.name || heardByTreeName}" created (segment ${response.segment_id})`
        : `Custom tree "${response?.name || heardByTreeName}" created`;
      enqueueSnackbar(message, { variant: 'success' });
      fetchCustomTrees();
      handleClearHeardBy();
    } catch (error) {
      enqueueSnackbar(error?.message || 'Failed to create custom tree from heard-by set', { variant: 'error' });
    } finally {
      setCreatingHeardBySegment(false);
    }
  }, [
    heardByTreeName,
    heardBySegmentNodes,
    heardBySegmentCaps,
    enqueueSnackbar,
    fetchCustomTrees,
    handleClearHeardBy,
  ]);

  // Fetch custom trees when segments dialog opens or when showing segments on map
  useEffect(() => {
    if (segmentsDialogOpen || showSegmentsOnMap) {
      fetchCustomTrees();
    }
  }, [segmentsDialogOpen, showSegmentsOnMap, fetchCustomTrees]);

  const segmentFocusDevicesRef = useRef({ nodes: allNodeDevices, caps: allCapDevices });
  segmentFocusDevicesRef.current = { nodes: allNodeDevices, caps: allCapDevices };
  const scrollSegmentToTopRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('manageSegment')) return;
    const segmentId = Number(params.get('manageSegment'));
    params.delete('manageSegment');
    const search = params.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true },
    );
    if (!Number.isFinite(segmentId)) return;
    setManageSegmentFocus(segmentId);
    setSegmentsDialogOpen(true);
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (manageSegmentFocus == null) return undefined;
    const segmentId = manageSegmentFocus;
    let cancelled = false;
    (async () => {
      setCustomTreesLoading(true);
      try {
        const response = await api.getCustomTrees();
        if (cancelled) return;
        const trees = response?.trees || [];
        setCustomTrees(trees);
        const tree = trees.find((entry) => Number(entry.segment_id) === segmentId);
        if (!tree) {
          enqueueSnackbar(`No managed segment with id ${segmentId}`, { variant: 'info' });
          setSelectedTree(null);
          setSegmentEditNodes([]);
          setSegmentEditCaps([]);
          return;
        }
        const { nodes, caps } = segmentFocusDevicesRef.current;
        setSelectedTree(tree);
        scrollSegmentToTopRef.current = true;
        setSegmentEditNodes(
          (tree.devices || [])
            .map((serial) => nodes.find((device) => device.serial_number === serial))
            .filter(Boolean),
        );
        setSegmentEditCaps(
          (tree.caps || [])
            .map((serial) => caps.find((device) => device.serial_number === serial))
            .filter(Boolean),
        );
      } catch (error) {
        if (!cancelled) {
          enqueueSnackbar(error?.message || 'Failed to load segments', { variant: 'error' });
        }
      } finally {
        if (!cancelled) {
          setCustomTreesLoading(false);
          setManageSegmentFocus(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [manageSegmentFocus, enqueueSnackbar]);

  useEffect(() => {
    if (!segmentsDialogOpen || !selectedTree?.id || customTreesLoading || !scrollSegmentToTopRef.current) {
      return undefined;
    }
    const segmentKey = selectedTree.id;
    const timer = setTimeout(() => {
      const row = document.getElementById(`manage-segment-${segmentKey}`);
      const container = row?.closest('.MuiDialogContent-root');
      if (!row || !container) return;
      const paddingTop = parseFloat(window.getComputedStyle(container).paddingTop) || 0;
      container.scrollTop += row.getBoundingClientRect().top - container.getBoundingClientRect().top - paddingTop;
      scrollSegmentToTopRef.current = false;
    }, 300);
    return () => clearTimeout(timer);
  }, [segmentsDialogOpen, selectedTree?.id, customTreesLoading]);

  // Fetch custom trees when the selection dialog Custom Tree tab is open
  useEffect(() => {
    if (selectionDialogOpen && dialogTab === 0) {
      fetchCustomTrees();
    }
  }, [selectionDialogOpen, dialogTab, fetchCustomTrees]);

  const syncAddToExistingDeviceLists = useCallback((tree, nodes, caps) => {
    if (!tree) {
      setAddToExistingNodes([]);
      setAddToExistingCaps([]);
      return;
    }
    const currentNodes = new Set((tree.devices || []).map((s) => String(s).toLowerCase()));
    const currentCaps = new Set((tree.caps || []).map((s) => String(s).toLowerCase()));
    setAddToExistingNodes(
      (nodes || []).filter((d) => !currentNodes.has(String(d.serial_number).toLowerCase())),
    );
    setAddToExistingCaps(
      (caps || []).filter((d) => !currentCaps.has(String(d.serial_number).toLowerCase())),
    );
  }, []);

  useEffect(() => {
    if (!selectionDialogOpen || dialogTab !== 0) return;
    syncAddToExistingDeviceLists(addToExistingTree, selectedNodes, selectedCaps);
  }, [
    selectionDialogOpen,
    dialogTab,
    addToExistingTree,
    selectedNodes,
    selectedCaps,
    syncAddToExistingDeviceLists,
  ]);

  // notifySegmentAddPlan surfaces the plan-based add response (202). Adding
  // devices to an existing segment now starts a tracked, offline-capable
  // segment rollout — identical to creating a segment — instead of an
  // immediate online-only apply.
  const notifySegmentAddPlan = useCallback((response, treeName) => {
    const offline = response?.offline_devices?.length || 0;
    const offlineNote = offline > 0
      ? ` ${offline} offline device${offline === 1 ? '' : 's'} will receive the segment on reconnect.`
      : '';
    enqueueSnackbar(
      `Adding devices to "${response?.name || treeName}" — segment rollout started.${offlineNote} Track it under RRM → Plans → Segment plans.`,
      { variant: 'success', autoHideDuration: 8000 },
    );
  }, [enqueueSnackbar]);

  const handleAddSelectionToExistingTree = useCallback(async () => {
    if (!addToExistingTree?.id) {
      enqueueSnackbar('Select an existing segment', { variant: 'warning' });
      return;
    }
    if (addToExistingNodes.length === 0 && addToExistingCaps.length === 0) {
      enqueueSnackbar('No new devices to add — selected devices are already in this segment', { variant: 'info' });
      return;
    }

    setAddToCustomTreeLoading(true);
    try {
      const response = await api.addDevicesToCustomTree(addToExistingTree.id, {
        devices: addToExistingNodes.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
        caps: addToExistingCaps.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
      });
      notifySegmentAddPlan(response, addToExistingTree.name);
      const updatedTree = {
        ...addToExistingTree,
        devices: response?.devices || addToExistingTree.devices,
        caps: response?.caps || addToExistingTree.caps,
        name: response?.name || addToExistingTree.name,
        status: 'provisioning',
        plan_id: response?.plan_id || addToExistingTree.plan_id,
      };
      setAddToExistingTree(updatedTree);
      setCustomTrees((prev) => prev.map((tree) => (
        tree.id === updatedTree.id ? { ...tree, ...updatedTree } : tree
      )));
      syncAddToExistingDeviceLists(updatedTree, selectedNodes, selectedCaps);
      fetchCustomTrees();
    } catch (error) {
      if (error?.conflicts?.length) {
        enqueueSnackbar(
          'Some devices are already in an in-flight segment plan — wait for it to finish, then retry.',
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar(error?.message || 'Failed to add devices to segment', { variant: 'error' });
      }
    } finally {
      setAddToCustomTreeLoading(false);
    }
  }, [
    addToExistingTree,
    addToExistingNodes,
    addToExistingCaps,
    selectedNodes,
    selectedCaps,
    enqueueSnackbar,
    notifySegmentAddPlan,
    syncAddToExistingDeviceLists,
    fetchCustomTrees,
  ]);

  // Fetch groups when groups panel is toggled on
  useEffect(() => {
    if (showGroupsOnMap) {
      fetchGroupsForMap();
    }
  }, [showGroupsOnMap, fetchGroupsForMap]);


  const clearSelection = useCallback(() => {
    setSelectedDevices([]);
    setPolygons([]);
    setSelectionDialogOpen(false);
    setDialogTab(0);
    setCustomTreeName('');
    setSelectedCaps([]);
    setSelectedNodes([]);
    setSelectedTree(null);
    setAddToExistingTree(null);
    setAddToExistingNodes([]);
    setAddToExistingCaps([]);
    connectivityChannelsTouchedRef.current = false;
    setConnectivityParams({
      txPowerDown: '',
      txPowerUp: '',
      band: '',
      maxChildren: '',
      htmode: '',
      channels: '',
    });
    setCommandText('');
    setGroupCreationName('');
    setStaBandFilter5G(true);
    setStaBandFilter6G(true);
    setStaBandSwitchSelected(new Set());
    setStaBandTarget('6GHz');
  }, []);

  const handleRunOptimizeOnSelection = useCallback(() => {
    const serials = selectedDevices.map((d) => d.serial_number).filter(Boolean);
    if (serials.length === 0) {
      enqueueSnackbar('Select at least one device.', { variant: 'warning' });
      return;
    }
    persistOptimizeScope({ type: 'devices', serials });
    clearSelection();
    setDrawingMode(null);
    navigate('/analysis?scope=devices');
  }, [selectedDevices, enqueueSnackbar, navigate, clearSelection]);

  const selectionHasCapDevice = React.useMemo(
    () => selectionHasCap(selectedDevices, (d) => getDeviceType(d.capabilities) === 'CAP'),
    [selectedDevices, getDeviceType],
  );

  const handleCreateSegmentsOnSelection = useCallback(() => {
    const serials = extractSegmentCohortSerials(selectedDevices);
    if (serials.length === 0) {
      enqueueSnackbar('Select at least one device.', { variant: 'warning' });
      return;
    }
    if (!selectionHasCapDevice) {
      enqueueSnackbar(
        'Segment partitioning needs at least one CAP in the selection to act as a segment root. Add a CAP and try again.',
        { variant: 'warning' },
      );
      return;
    }
    persistSegmentCohortScope({ serials });
    clearSelection();
    setDrawingMode(null);
    navigate('/analysis?scope=segment');
  }, [selectedDevices, selectionHasCapDevice, enqueueSnackbar, navigate, clearSelection]);

  const handleRunIperfOnSelection = useCallback(() => {
    const isCap = (device) => getDeviceType(device.capabilities) === 'CAP';
    const capSerials = extractCapSerialsFromSelection(selectedDevices, isCap);
    if (capSerials.length === 0) {
      enqueueSnackbar('Select at least one CAP (or devices under a CAP).', { variant: 'warning' });
      return;
    }
    const focusSerials = extractFocusSerialsFromSelection(selectedDevices, isCap);
    persistIperfCapScope({ capSerials, focusSerials });
    clearSelection();
    setDrawingMode(null);
    navigate('/analysis?scope=iperf');
  }, [selectedDevices, getDeviceType, enqueueSnackbar, navigate, clearSelection]);

  const selectionNeighborsScanTargets = React.useMemo(
    () => selectedDevices.filter((d) => d.compatible === 'jnet2' && d.connected),
    [selectedDevices],
  );

  const handleOpenNeighborsScanOnSelection = useCallback(() => {
    if (selectionNeighborsScanTargets.length === 0) {
      enqueueSnackbar(
        'No connected jnet2 devices in selection. Neighbors scan requires online jnet2 devices.',
        { variant: 'warning' },
      );
      return;
    }
    setSelectionNeighborsScanConfirmOpen(true);
  }, [selectionNeighborsScanTargets.length, enqueueSnackbar]);

  const handleConfirmNeighborsScanOnSelection = useCallback(async () => {
    const serials = selectionNeighborsScanTargets.map((d) => d.serial_number).filter(Boolean);
    if (serials.length === 0) {
      enqueueSnackbar('No devices to scan.', { variant: 'warning' });
      return;
    }
    const interval = Number(selectionNeighborsScanInterval);
    const scanInterval = Number.isFinite(interval) && interval >= 1 ? interval : 15;

    setSelectionNeighborsScanLoading(true);
    setSelectionNeighborsScanConfirmOpen(false);
    try {
      const result = await api.startNeighborsScanBatch(
        serials,
        scanInterval,
        selectionNeighborsScanAllFreq,
        selectionNeighborsScanBatch,
      );
      const failedCount = countNeighborsScanFailures(result?.results);
      const sent = result?.commands_sent ?? 0;
      if (failedCount > 0) {
        enqueueSnackbar(
          `Neighbors scan: ${sent} started, ${failedCount} failed (${serials.length} selected)`,
          { variant: 'warning' },
        );
      } else {
        enqueueSnackbar(
          `Neighbors scan started for ${sent} device${sent === 1 ? '' : 's'} (${
            selectionNeighborsScanAllFreq ? 'all channels' : 'known channels only'
          })`,
          { variant: 'success' },
        );
      }
    } catch (error) {
      console.error('Neighbors scan on selection failed:', error);
      enqueueSnackbar(error?.message || 'Failed to start neighbors scan', { variant: 'error' });
    } finally {
      setSelectionNeighborsScanLoading(false);
    }
  }, [
    selectionNeighborsScanTargets,
    selectionNeighborsScanInterval,
    selectionNeighborsScanAllFreq,
    selectionNeighborsScanBatch,
    enqueueSnackbar,
  ]);

  const handleCreateCustomTree = useCallback(async () => {
    if (!customTreeName.trim()) {
      enqueueSnackbar('Please provide a custom tree name', { variant: 'warning' });
      return;
    }

    if (selectedNodes.length === 0) {
      enqueueSnackbar('Custom trees must include at least one Node device', { variant: 'warning' });
      return;
    }

    if (selectedCaps.length === 0) {
      enqueueSnackbar('Select at least one CAP device for the custom tree', { variant: 'warning' });
      return;
    }

    // Get serials from selected nodes and CAPs
    const nodeSerials = selectedNodes.map(node => node.serial_number?.toLowerCase()).filter(Boolean);
    const capSerials = selectedCaps.map(cap => cap.serial_number?.toLowerCase()).filter(Boolean);

    setCreatingCustomTree(true);
    try {
      const response = await api.createCustomTree({
        name: customTreeName.trim(),
        devices: nodeSerials,
        caps: capSerials
      });
      
      // Summarize results from device operations
      const allResults = [
        ...(response?.results?.devices || []),
        ...(response?.results?.caps || [])
      ];
      const message = response?.segment_id
        ? `Custom tree "${response.name || customTreeName}" created (segment ${response.segment_id})`
        : `Custom tree "${response?.name || customTreeName}" created`;
      summarizeOperationResults(allResults, message);
      
      // Refresh the list and close the dialog
      fetchCustomTrees();
      
      // Close dialog and clear selection after a short delay to show the success message
      setTimeout(() => {
        clearSelection();
      }, 500);
    } catch (error) {
      // Check if error contains detailed results from backend
      if (error?.results) {
        const allResults = [
          ...(error.results.devices || []),
          ...(error.results.caps || [])
        ];
        summarizeOperationResults(allResults, 'Custom tree creation failed');
      } else {
      enqueueSnackbar(error?.message || 'Failed to create custom tree', { variant: 'error' });
      }
      
      // Close dialog after showing error
      setTimeout(() => {
        clearSelection();
      }, 1500);
    } finally {
      setCreatingCustomTree(false);
    }
  }, [customTreeName, selectedNodes, selectedCaps, enqueueSnackbar, fetchCustomTrees, clearSelection, summarizeOperationResults]);

  // Check if any connectivity parameter is set
  const hasConnectivityChanges = React.useMemo(() => {
    return !!(
      connectivityParams.txPowerDown.trim() ||
      connectivityParams.txPowerUp.trim() ||
      connectivityParams.band.trim() ||
      connectivityParams.maxChildren.trim() ||
      connectivityParams.htmode.trim() ||
      connectivityParams.channels.trim()
    );
  }, [connectivityParams]);

  const prefillConnectivityChannels = useCallback((band, htmode) => {
    if (!['5GHz', '6GHz'].includes(band)) {
      return '';
    }
    return formatTreemgmtChannelList(defaultChannelsForBandAndHtmode(band, htmode || 'HT80'));
  }, []);

  const handleConnectivityChange = useCallback(async () => {
    const allSerials = getSelectedSerials();
    if (allSerials.length === 0) {
      enqueueSnackbar('Select at least one device to update connectivity', { variant: 'warning' });
      return;
    }

    const parseNumericValue = (value, label, allowZero = false) => {
      const parsed = parseInt(value, 10);
      if (Number.isNaN(parsed)) {
        enqueueSnackbar(`${label} must be a valid number`, { variant: 'warning' });
        return null;
      }
      if ((!allowZero && parsed <= 0) || (allowZero && parsed < 0)) {
        enqueueSnackbar(`${label} must be ${allowZero ? '0 or greater' : 'greater than 0'}`, { variant: 'warning' });
        return null;
      }
      return parsed;
    };

    // Parse all values up-front so validation errors abort before any request.
    const universalFields = {};
    const backhaulOnlyFields = {};
    let hasParam = false;

    if (connectivityParams.txPowerUp !== '') {
      const parsed = parseNumericValue(connectivityParams.txPowerUp, 'TX Power Up');
      if (parsed === null) return;
      universalFields.tx_power_up = parsed;
      hasParam = true;
    }
    if (connectivityParams.maxChildren !== '') {
      const parsed = parseNumericValue(connectivityParams.maxChildren, 'Max Children', true);
      if (parsed === null) return;
      backhaulOnlyFields.max_children = parsed;
      hasParam = true;
    }
    if (connectivityParams.channels.trim() !== '') {
      const bandForChannels = connectivityParams.band.trim();
      const channelResult = buildConnectivityChannelGroups(bandForChannels, connectivityParams.channels);
      if (channelResult.error) {
        enqueueSnackbar(channelResult.error, { variant: 'warning' });
        return;
      }
      universalFields.channel_groups = channelResult.channelGroups;
      hasParam = true;
    }

    // Backhaul-only fields: tx_power_down, band, htmode, max_children.
    // These must NOT be applied to devices whose 5 GHz radio is
    // configured for Public WiFi (ap on 5GHz slot + max_children=0).
    if (connectivityParams.txPowerDown !== '') {
      const parsed = parseNumericValue(connectivityParams.txPowerDown, 'TX Power Down');
      if (parsed === null) return;
      backhaulOnlyFields.tx_power_down = parsed;
      hasParam = true;
    }
    if (connectivityParams.band.trim() !== '') {
      const bandValue = connectivityParams.band.trim();
      if (!['5GHz', '6GHz'].includes(bandValue)) {
        enqueueSnackbar('Band must be one of: 5GHz, 6GHz', { variant: 'warning' });
        return;
      }
      backhaulOnlyFields.band = bandValue;
      hasParam = true;
    }
    if (connectivityParams.htmode.trim() !== '') {
      const htValue = connectivityParams.htmode.trim().toUpperCase();
      if (!['HT20', 'HT40', 'HT80', 'HT160'].includes(htValue)) {
        enqueueSnackbar('HT mode must be one of: HT20, HT40, HT80, HT160', { variant: 'warning' });
        return;
      }
      backhaulOnlyFields.htmode = htValue;
      hasParam = true;
    }

    if (!hasParam) {
      enqueueSnackbar('Provide at least one connectivity parameter to update', { variant: 'warning' });
      return;
    }

    const hasBackhaulOnly = Object.keys(backhaulOnlyFields).length > 0;
    const hasUniversal = Object.keys(universalFields).length > 0;

    // Partition devices: exclude public-wifi-on-5GHz devices from
    // backhaul-only params (tx_power_down, band, htmode, max_children).
    let backhaulSerials = allSerials;
    let skippedCount = 0;
    if (hasBackhaulOnly) {
      const allSet = new Set(allSerials);
      const publicWifiSerials = new Set();
      selectedDevices.forEach((d) => {
        const serial = d.serial_number?.toLowerCase();
        if (serial && allSet.has(serial) && device5GHzRadioUsedForPublicWifi(d)) {
          publicWifiSerials.add(serial);
        }
      });
      skippedCount = publicWifiSerials.size;
      backhaulSerials = allSerials.filter((s) => !publicWifiSerials.has(s));
    }

    setConnectivityLoading(true);
    try {
      const calls = [];

      // 1. Universal fields (tx_power_up, channels) → all devices.
      if (hasUniversal) {
        calls.push(api.updateDevicesConnectivity({ devices: allSerials, ...universalFields }));
      }

      // 2. Backhaul-only fields → only non-public-wifi devices.
      if (hasBackhaulOnly && backhaulSerials.length > 0) {
        calls.push(api.updateDevicesConnectivity({ devices: backhaulSerials, ...backhaulOnlyFields }));
      }

      if (calls.length === 0) {
        enqueueSnackbar('No devices eligible for the selected changes', { variant: 'info' });
        return;
      }

      const settled = await Promise.allSettled(calls);
      const allResults = [];
      settled.forEach((outcome) => {
        if (outcome.status === 'fulfilled') {
          allResults.push(...(outcome.value?.results || []));
        } else {
          const err = outcome.reason;
          if (err?.results) {
            allResults.push(...err.results);
          }
        }
      });

      if (allResults.length > 0) {
        const summary = formatConfigPushSummaryMessage(classifyConfigPushResults(allResults));
        enqueueSnackbar(summary.text, { variant: summary.variant, autoHideDuration: 7000 });
      }
      if (skippedCount > 0) {
        enqueueSnackbar(
          `${skippedCount} device${skippedCount > 1 ? 's' : ''} skipped for TX Power Down / Band / HT Mode / Max Children (5 GHz radio assigned to Public WiFi)`,
          { variant: 'info', autoHideDuration: 6000 },
        );
      }
    } catch (error) {
      if (error?.results) {
        const summary = formatConfigPushSummaryMessage(classifyConfigPushResults(error.results));
        enqueueSnackbar(summary.text, { variant: summary.variant, autoHideDuration: 7000 });
      } else {
        enqueueSnackbar(error?.message || 'Failed to update connectivity', { variant: 'error' });
      }
    } finally {
      setConnectivityLoading(false);
    }
  }, [connectivityParams, getSelectedSerials, selectedDevices, enqueueSnackbar]);

  const selectedNodeDevices = React.useMemo(
    () => selectedDevices.filter((d) => getDeviceType(d.capabilities) === 'Node'),
    [selectedDevices, getDeviceType],
  );

  const selectedCapCount = React.useMemo(
    () => selectedDevices.filter((d) => getDeviceType(d.capabilities) === 'CAP').length,
    [selectedDevices, getDeviceType],
  );

  const handleBgScanBulkApply = useCallback(async () => {
    const nodeSerials = selectedNodeDevices
      .map((d) => (d.serial_number || '').toLowerCase().trim())
      .filter(Boolean);
    if (nodeSerials.length === 0) {
      enqueueSnackbar('Select at least one Node device to update background scan', { variant: 'warning' });
      return;
    }

    const { patch, errors } = buildBackgroundStaScanBulkPatch(bgScanForm);
    if (errors.length > 0) {
      enqueueSnackbar(errors[0], { variant: 'warning' });
      return;
    }
    if (!patch) {
      enqueueSnackbar('Provide at least one background scan field to update', { variant: 'warning' });
      return;
    }

    setBgScanLoading(true);
    try {
      const response = await api.bulkPatchTreeMgmtConfig(nodeSerials, patch);
      summarizeOperationResults(response?.results || [], 'Background scan updated');
    } catch (error) {
      if (error?.results) {
        summarizeOperationResults(error.results, 'Background scan update failed');
      } else {
        enqueueSnackbar(error?.message || 'Failed to update background scan', { variant: 'error' });
      }
    } finally {
      setBgScanLoading(false);
    }
  }, [bgScanForm, selectedNodeDevices, enqueueSnackbar, summarizeOperationResults]);

  // Strip `segment_id_ap` and `segment_id_dev` from every treemgmt entry of
  // each selected device, then push the updated configuration back via the
  // batch configure endpoint.
  const handleDeleteSegments = useCallback(async () => {
    const devicesList = getSelectedSerials();
    if (devicesList.length === 0) {
      enqueueSnackbar('Select at least one device to delete segments', { variant: 'warning' });
      return;
    }

    setDeleteSegmentsLoading(true);
    try {
      const queryResp = await api.queryDeviceConfigBatch(devicesList);
      const configBySerial = new Map();
      (queryResp?.results || []).forEach((row) => {
        const serial = row.serial?.toLowerCase();
        if (serial && row.success && row.configuration && typeof row.configuration === 'object') {
          configBySerial.set(serial, row.configuration);
        }
      });

      const results = [];
      const updates = [];

      devicesList.forEach((serial) => {
        const key = serial.toLowerCase();
        const configuration = configBySerial.get(key);
        if (!configuration) {
          results.push({
            serial,
            success: false,
            message: 'No configuration available on device',
          });
          return;
        }

        const treemgmt = Array.isArray(configuration.treemgmt) ? configuration.treemgmt : null;
        if (!treemgmt || treemgmt.length === 0) {
          results.push({
            serial,
            success: true,
            message: 'No treemgmt section — nothing to clear',
            noChange: true,
          });
          return;
        }

        let removedAny = false;
        const updatedTreemgmt = treemgmt.map((entry) => {
          if (!entry || typeof entry !== 'object') return entry;
          const next = { ...entry };
          if (Object.prototype.hasOwnProperty.call(next, 'segment_id_ap')) {
            delete next.segment_id_ap;
            removedAny = true;
          }
          if (Object.prototype.hasOwnProperty.call(next, 'segment_id_dev')) {
            delete next.segment_id_dev;
            removedAny = true;
          }
          return next;
        });

        if (!removedAny) {
          results.push({
            serial,
            success: true,
            message: 'No segment_id fields present',
            noChange: true,
          });
          return;
        }

        updates.push({
          serial: key,
          configuration: { ...configuration, treemgmt: updatedTreemgmt },
        });
      });

      if (updates.length > 0) {
        const applyResp = await api.applyDeviceConfigBatch({ updates });
        const applyBySerial = new Map(
          (applyResp?.results || []).map((row) => [row.serial?.toLowerCase(), row]),
        );
        updates.forEach(({ serial }) => {
          const row = applyBySerial.get(serial);
          if (!row) {
            results.push({ serial, success: false, message: 'No apply result returned' });
            return;
          }
          if (!row.success) {
            results.push({
              serial,
              success: false,
              message: row.error || row.message || 'Failed to clear segments',
            });
            return;
          }
          results.push({
            serial,
            success: true,
            message: 'Segments cleared',
            command_uuid: row.command_uuid,
            command_id: row.command_id,
          });
        });
      }

      const changedCount = results.filter((r) => r.success && !r.noChange).length;
      const skippedCount = results.filter((r) => r.success && r.noChange).length;
      let summary = `Segments deleted on ${changedCount} device(s)`;
      if (skippedCount > 0) {
        summary += `; ${skippedCount} had nothing to clear`;
      }
      summarizeOperationResults(results, summary);
    } finally {
      setDeleteSegmentsLoading(false);
      setDeleteSegmentsConfirmOpen(false);
    }
  }, [getSelectedSerials, enqueueSnackbar, summarizeOperationResults]);

  const handleSaveSyslogConfig = useCallback(async () => {
    if (!syslogConfig.ip.trim()) {
      enqueueSnackbar('Syslog server IP is required', { variant: 'warning' });
      return;
    }
    const devicesList = getSelectedSerials();
    if (devicesList.length === 0) {
      enqueueSnackbar('No devices selected', { variant: 'warning' });
      return;
    }
    setSyslogSaving(true);
    try {
      const response = await api.applySyslogConfigBatch(devicesList, syslogConfig);
      const results = api.mapBatchOperationResults(response);
      const ok = results.filter((r) => r.success).length;
      const fail = results.filter((r) => !r.success).length;
      if (fail === 0) {
        enqueueSnackbar(`Syslog config applied to ${ok} device(s)`, { variant: 'success' });
      } else {
        enqueueSnackbar(`Syslog config: ${ok} succeeded, ${fail} failed`, { variant: 'warning' });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to apply syslog config', { variant: 'error' });
    } finally {
      setSyslogSaving(false);
    }
  }, [syslogConfig, getSelectedSerials, enqueueSnackbar]);

  const handleSaveReportsConfig = useCallback(async () => {
    const devicesList = getSelectedSerials();
    if (devicesList.length === 0) {
      enqueueSnackbar('No devices selected', { variant: 'warning' });
      return;
    }
    setReportsSaving(true);
    try {
      const response = await api.applyReportsConfigBatch(devicesList, reportsConfig);
      const results = api.mapBatchOperationResults(response);
      const ok = results.filter((r) => r.success).length;
      const fail = results.filter((r) => !r.success).length;
      if (fail === 0) {
        enqueueSnackbar(`Reports config applied to ${ok} device(s)`, { variant: 'success' });
      } else {
        enqueueSnackbar(`Reports config: ${ok} succeeded, ${fail} failed`, { variant: 'warning' });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to apply reports config', { variant: 'error' });
    } finally {
      setReportsSaving(false);
    }
  }, [reportsConfig, getSelectedSerials, enqueueSnackbar]);

  const handleSendCommand = useCallback(async () => {
    if (!commandText.trim()) {
      enqueueSnackbar('Please enter a command', { variant: 'warning' });
      return;
    }

    const devicesList = getSelectedSerials();
    if (devicesList.length === 0) {
      enqueueSnackbar('No devices selected', { variant: 'warning' });
      return;
    }

    setCommandLoading(true);
    try {
      const response = await api.sendCommandBatch(devicesList, commandText.trim());
      const commandResults = api.mapBatchOperationResults(response);
      const successful = commandResults.filter(r => r.success);
      const failed = commandResults.filter(r => !r.success);

      const failedDeviceNames = failed.map(f => {
        const device = selectedDevices.find(d => d.serial_number === f.serial);
        return device?.name || f.serial;
      });

      if (failed.length === 0) {
        enqueueSnackbar(`Command sent successfully to all ${devicesList.length} device(s)`, { variant: 'success' });
      } else if (successful.length === 0) {
        enqueueSnackbar(`Failed to send command to all ${devicesList.length} device(s)`, { variant: 'error' });
      } else {
        const failedList = failedDeviceNames.join(', ');
        enqueueSnackbar(
          `Command sent to ${successful.length} out of ${devicesList.length} device(s). Failed: ${failedList} (${failed.length} failed)`,
          { 
            variant: 'warning',
            autoHideDuration: 8000
          }
        );
      }
    } catch (error) {
      console.error('Error sending command:', error);
      enqueueSnackbar('Failed to send command', { variant: 'error' });
    } finally {
      setCommandLoading(false);
    }
  }, [commandText, getSelectedSerials, selectedDevices, enqueueSnackbar]);

  const handleApplyStaBandSwitch = useCallback(async () => {
    const serials = Array.from(staBandSwitchSelected).filter(Boolean);
    if (serials.length === 0) {
      enqueueSnackbar('Select at least one device', { variant: 'warning' });
      return;
    }
    if (!staBandTarget) {
      enqueueSnackbar('Choose a target band', { variant: 'warning' });
      return;
    }

    setStaBandSwitchLoading(true);
    try {
      const response = await api.setStaBandBatch(serials, staBandTarget);
      const rows = api.mapBatchOperationResults(response);
      summarizeOperationResults(
        rows,
        `Queued set_sta_band (${staBandTarget}) for ${rows.filter((r) => r.success).length} device(s)`,
      );
    } catch (error) {
      console.error('STA band switch:', error);
      enqueueSnackbar('Failed to apply STA band switch', { variant: 'error' });
    } finally {
      setStaBandSwitchLoading(false);
    }
  }, [staBandSwitchSelected, staBandTarget, enqueueSnackbar, summarizeOperationResults]);

  const handleDeleteCustomTree = useCallback(async (tree) => {
    if (!tree) return;

    if (!window.confirm(`Are you sure you want to delete segment "${tree.name}"? This will remove the segment ID from all associated devices and cannot be undone.`)) {
      return;
    }

    setDeleteCustomTreeLoading(true);
    try {
      const response = await api.deleteCustomTree(tree.id);
      
      // Summarize results from device operations
      const allResults = [
        ...(response?.results?.devices || []),
        ...(response?.results?.caps || [])
      ];
      summarizeOperationResults(allResults, response?.message || 'Segment deleted successfully');
      
      // Clear selected tree if it was the one deleted
      if (selectedTree?.id === tree.id) {
        setSelectedTree(null);
      }
      
      // Refresh the trees list
      fetchCustomTrees();
    } catch (error) {
      // Check if error contains detailed results from backend
      if (error?.results) {
        const allResults = [
          ...(error.results.devices || []),
          ...(error.results.caps || [])
        ];
        summarizeOperationResults(allResults, 'Segment deletion failed');
      } else {
        enqueueSnackbar(error?.message || 'Failed to delete segment', { variant: 'error' });
      }
    } finally {
      setDeleteCustomTreeLoading(false);
    }
  }, [selectedTree, enqueueSnackbar, fetchCustomTrees, summarizeOperationResults]);

  const handleUpdateSegmentDevices = useCallback(async (tree) => {
    if (!tree) return;

    const nodeSerials = segmentEditNodes.map(d => d.serial_number);
    const capSerials = segmentEditCaps.map(d => d.serial_number);

    // Determine which devices to add and which to remove
    const currentNodes = tree.devices || [];
    const currentCaps = tree.caps || [];

    const nodesToAdd = nodeSerials.filter(s => !currentNodes.includes(s));
    const nodesToRemove = currentNodes.filter(s => !nodeSerials.includes(s));
    const capsToAdd = capSerials.filter(s => !currentCaps.includes(s));
    const capsToRemove = currentCaps.filter(s => !capSerials.includes(s));

    if (nodesToAdd.length === 0 && nodesToRemove.length === 0 && capsToAdd.length === 0 && capsToRemove.length === 0) {
      enqueueSnackbar('No changes to apply', { variant: 'info' });
      return;
    }

    setAddToCustomTreeLoading(true);
    setRemoveFromCustomTreeLoading(true);

    try {
      let updatedTree = { ...tree };

      // Removes run first (synchronous): they must complete before an add
      // starts a plan, because the add flips the tree to "provisioning"
      // and a concurrent remove would be rejected by the plan-conflict
      // guard while the rollout is in flight.
      if (nodesToRemove.length > 0 || capsToRemove.length > 0) {
        const removeResponse = await api.removeDevicesFromCustomTree(tree.id, {
          devices: nodesToRemove,
          caps: capsToRemove
        });
        updatedTree = {
          ...updatedTree,
          devices: removeResponse.devices || updatedTree.devices,
          caps: removeResponse.caps || updatedTree.caps
        };
        const removeResults = [
          ...(removeResponse?.results?.devices || []),
          ...(removeResponse?.results?.caps || []),
        ];
        summarizeOperationResults(removeResults, 'Removed devices from segment');
      }

      // Adds start a tracked, offline-capable segment rollout (202 plan).
      if (nodesToAdd.length > 0 || capsToAdd.length > 0) {
        const addResponse = await api.addDevicesToCustomTree(tree.id, {
          devices: nodesToAdd,
          caps: capsToAdd
        });
        updatedTree = {
          ...updatedTree,
          devices: addResponse.devices || updatedTree.devices,
          caps: addResponse.caps || updatedTree.caps,
          status: 'provisioning',
          plan_id: addResponse?.plan_id || updatedTree.plan_id,
        };
        notifySegmentAddPlan(addResponse, tree.name);
      }

      // Update the selected tree
      setSelectedTree(updatedTree);

      // Refresh the trees list
      fetchCustomTrees();
    } catch (error) {
      if (error?.conflicts?.length) {
        enqueueSnackbar(
          'Some devices are already in an in-flight segment plan — wait for it to finish, then retry.',
          { variant: 'error' },
        );
      } else if (error?.results) {
        const allResults = [
          ...(error.results.devices || []),
          ...(error.results.caps || [])
        ];
        summarizeOperationResults(allResults, 'Segment update failed');
      } else {
        enqueueSnackbar(error?.message || 'Failed to update segment', { variant: 'error' });
      }
    } finally {
      setAddToCustomTreeLoading(false);
      setRemoveFromCustomTreeLoading(false);
    }
  }, [segmentEditNodes, segmentEditCaps, enqueueSnackbar, fetchCustomTrees, summarizeOperationResults, notifySegmentAddPlan]);



  // Handle segment selection for highlighting
  const unassignedSegmentSerialSet = React.useMemo(
    () => collectUnassignedSegmentSerials(devices, customTrees),
    [devices, customTrees],
  );

  const highlightSegment = useCallback((segment) => {
    setSelectedSegmentForHighlight(segment);
    setPolygons([]);

    const targetSerials = segment.id === NO_SEGMENT_OVERLAY_ID
      ? unassignedSegmentSerialSet
      : new Set(
        [...(segment.devices || []), ...(segment.caps || [])]
          .filter(Boolean)
          .map((s) => String(s).toLowerCase()),
      );
    if (targetSerials.size === 0 || !devices) return;

    const firstDevice = devices.find((d) => {
      const serial = d.serial_number?.toLowerCase();
      return serial
        && targetSerials.has(serial)
        && d.gps_location?.latitude
        && d.gps_location?.longitude;
    });
    if (!firstDevice) return;

    const lat = parseFloat(firstDevice.gps_location.latitude);
    const lng = parseFloat(firstDevice.gps_location.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      setMapCenter({ lat, lng });
      setMapZoom(16);
    }
  }, [devices, unassignedSegmentSerialSet]);

  const handleSegmentClick = useCallback((segment) => {
    if (selectedSegmentForHighlight?.id === segment.id) {
      setSelectedSegmentForHighlight(null);
    } else {
      highlightSegment(segment);
    }
  }, [highlightSegment, selectedSegmentForHighlight]);

  const handleShowSegmentInLayer = useCallback((event, tree) => {
    event.preventDefault();
    event.stopPropagation();
    setShowSegmentsOnMap(true);
    setSegmentsDialogOpen(false);
    setSelectedTree(null);
    highlightSegment(tree);
  }, [highlightSegment]);

  const selectedSegmentSerialSet = React.useMemo(() => {
    if (!selectedSegmentForHighlight) return null;
    if (selectedSegmentForHighlight.id === NO_SEGMENT_OVERLAY_ID) {
      return unassignedSegmentSerialSet.size > 0 ? unassignedSegmentSerialSet : null;
    }
    const set = new Set();
    [...(selectedSegmentForHighlight.devices || []), ...(selectedSegmentForHighlight.caps || [])]
      .forEach((serial) => {
        if (serial) set.add(String(serial).toLowerCase());
      });
    return set.size > 0 ? set : null;
  }, [selectedSegmentForHighlight, unassignedSegmentSerialSet]);

  const isDeviceInSelectedSegment = useCallback((device) => {
    if (!selectedSegmentSerialSet || !device?.serial_number) return false;
    return selectedSegmentSerialSet.has(device.serial_number.toLowerCase());
  }, [selectedSegmentSerialSet]);

  const segmentMapOverlayActive = Boolean(
    selectedSegmentSerialSet &&
    (showAllConnections || showChannelMode || showWhitelistOverlay || showBlacklistOverlay)
  );

  const segmentColorById = React.useMemo(() => {
    const byId = new Map();
    (customTrees || []).forEach((tree, index) => {
      if (tree?.id != null) byId.set(tree.id, segmentColorAt(index));
    });
    return byId;
  }, [customTrees]);

  const getMarkerIcon = (device, customColor = null, borderColor = null, borderWidth = 2, opacity = 1) => {
    // Return null if Google Maps is not loaded yet
    if (!window.google?.maps) {
      return null;
    }

    const deviceType = getDeviceType(device.capabilities);
    
    const color = customColor || (device.connected ? MAP_UI.deviceOnline : MAP_UI.deviceOffline);

    if (deviceType === 'CAP') {
      // Explicit CAP badge, centered on the CAP's position where its
      // direction wedge (CapDirectionWedges) starts. Small, so it only
      // takes clicks on itself.
      const svg = buildCapBadgeSvg({
        fill: color,
        border: customColor ? color : '#000000',
        highlightColor: borderColor,
        highlightWidth: borderWidth,
        notesBadgeColor: deviceHasNotes(device) ? MAP_UI.notesBadge : null,
      });
      const capImg = document.createElement('img');
      capImg.src = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
      capImg.width = CAP_BADGE_SIZE;
      capImg.height = CAP_BADGE_SIZE;
      capImg.style.cursor = 'pointer';
      capImg.style.transform = CAP_MARKER_CENTER_TRANSFORM;
      if (opacity < 1) {
        capImg.style.opacity = String(opacity);
      }
      return capImg;
    }

    const iconBorderColor = customColor
      ? color
      : (deviceType === 'CAP'
          ? '#000000'
          : (device.connected ? MAP_UI.deviceOnlineBorder : MAP_UI.deviceOfflineBorder)
        );
    
    // CAP uses thicker border (2.5px), others use 1.2px
    const iconBorderWidth = deviceType === 'CAP' ? 2.5 : 1.2;
    
    // Compact sizes for dense map views
    let size = 24;
    let wifiArcs = '';
    let baseShape = '';
    let baseShapeType = 'circle';  // 'circle' for lamp/AI, 'square' for CAP

    // Glyph is centered on the marker (12, 12). One inner circle plus the outer arc.
    const nodeOuterArc = '<path d="M12,6.5 Q7,9.5 7,12 Q7,14.5 12,17.5 Q17,14.5 17,12 Q17,9.5 12,6.5 Z" fill="none" stroke="white" stroke-width="0.9" opacity="0.65"/>';
    const innerCircleStroke = 2.5;
    const nodeInnerCircle = `<circle cx="12" cy="12" r="4" fill="none" stroke="white" stroke-width="${innerCircleStroke}" opacity=""/>`;
    const aiInnerCircle = `<circle cx="12" cy="12" r="4" fill="none" stroke="#424242" stroke-width="${innerCircleStroke}" opacity="1"/>`;
    
    if (deviceType === 'AI') {
      size = 24;
      baseShapeType = 'circle';
      baseShape = aiInnerCircle;
      wifiArcs = nodeOuterArc;
    } else {
      // Network devices (Node/Lamp): Wi-Fi communication node in street light
      size = 24;
      baseShapeType = 'circle';
      // Subtle Wi-Fi arcs - represents embedded Wi-Fi unit in lamp
      // Compact, minimal arcs for dense map view
      baseShape = nodeInnerCircle;
      wifiArcs = nodeOuterArc;
    }
    
    // Create SVG icon with appropriate base shape
    // If borderColor specified, make marker slightly larger
    const viewBoxSize = 24;
    const actualSize = borderColor ? size * 1.15 : size;
    
    // Highlight ring. Selection sits outside the marker circle and glows.
    const isSelectionOutline = borderColor === MAP_UI.selectedMarkerOutline;
    const outerBorder = borderColor && !isSelectionOutline ? `
      <circle cx="${viewBoxSize/2}" cy="${viewBoxSize/2}" r="${(viewBoxSize/2) - 0.5}" fill="none" stroke="${borderColor}" stroke-width="1.5" opacity="0.7"/>
    ` : '';

    const selectionPad = 8;
    const selectionView = viewBoxSize + selectionPad * 2;
    const selectionRing = isSelectionOutline ? `
      <defs>
        <filter id="selectionGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="1.4" result="blur"/>
          <feMerge>
            <feMergeNode in="blur"/>
            <feMergeNode in="blur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <circle cx="12" cy="12" r="14.6" fill="none" stroke="${MAP_UI.selectedMarkerOutline}" stroke-width="2.4" opacity="0.9" filter="url(#selectionGlow)"/>
      <circle cx="12" cy="12" r="13.6" fill="none" stroke="${MAP_UI.selectedMarkerOutline}" stroke-width="1.5"/>
    ` : '';

    // Amber corner dot marks devices that carry operator notes.
    const notesBadge = deviceHasNotes(device)
      ? `<circle cx="19.5" cy="4.5" r="3.6" fill="${MAP_UI.notesBadge}" stroke="white" stroke-width="1.2"/>`
      : '';
    
    // Base shape: circle for lamp/AI/CAP, rectangle/square for others
    // CAP uses 2px dark neutral border, others use 1px darker border
    const baseShapeSvg = baseShapeType === 'rectangle'
      ? `<rect x="4" y="6" width="16" height="12" rx="1.5" fill="${color}" stroke="${iconBorderColor}" stroke-width="${iconBorderWidth}" opacity="0.95"/>`
      : baseShapeType === 'square'
      ? `<rect x="2" y="2" width="20" height="20" rx="2" fill="${color}" stroke="${iconBorderColor}" stroke-width="${iconBorderWidth}" opacity="0.95"/>`
      : `<circle cx="${viewBoxSize/2}" cy="${viewBoxSize/2}" r="${viewBoxSize/2 - 1}" fill="${color}" stroke="${iconBorderColor}" stroke-width="${iconBorderWidth}" opacity="0.95"/>`;
    
    const svgViewBox = isSelectionOutline
      ? `${-selectionPad} ${-selectionPad} ${selectionView} ${selectionView}`
      : `0 0 ${viewBoxSize} ${viewBoxSize}`;
    const drawSize = isSelectionOutline
      ? Math.round(size * selectionView / viewBoxSize)
      : actualSize;

    // Create industrial Wi-Fi infrastructure icon
    const svg = `
      <svg width="${drawSize}" height="${drawSize}" viewBox="${svgViewBox}" xmlns="http://www.w3.org/2000/svg">
        ${baseShapeSvg}
        <g transform="translate(0, 0)">
          ${baseShape}
          ${wifiArcs}
        </g>
        ${outerBorder}
        ${notesBadge}
        ${selectionRing}
      </svg>
    `;
    
    const iconUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    
    // Create a DOM element for AdvancedMarkerElement
    const img = document.createElement('img');
    img.src = iconUrl;
    img.width = drawSize;
    img.height = drawSize;
    img.style.cursor = 'pointer';
    if (opacity < 1) {
      img.style.opacity = String(opacity);
    }

    if (!isSelectionOutline) {
      return img;
    }

    // Keep the marker anchor on the original circle. The ring and glow draw outside it.
    const wrap = document.createElement('div');
    wrap.style.position = 'relative';
    wrap.style.width = `${size}px`;
    wrap.style.height = `${size}px`;
    wrap.style.overflow = 'visible';
    wrap.style.cursor = 'pointer';
    img.style.position = 'absolute';
    img.style.left = `${(size - drawSize) / 2}px`;
    img.style.top = `${(size - drawSize) / 2}px`;
    wrap.appendChild(img);
    return wrap;
  };

  const mapLayersActive = showAllConnections
    || showGroupsOnMap
    || showChannelMode
    || showWhitelistOverlay
    || showBlacklistOverlay
    || showSegmentsOnMap
    || showPublicWifiLampsLayer
    || showDataTrafficLayer
    || Boolean(iperfViewRun);

  const mapToolbarBtnSx = (active) => ({
    width: '100%',
    justifyContent: 'center',
    backgroundColor: active ? MAP_UI.accentTint : 'rgba(255, 255, 255, 0.96)',
    borderColor: active ? MAP_UI.accent : MAP_UI.accentSoft,
    color: MAP_UI.accentDark,
    '&:hover': {
      backgroundColor: active ? MAP_UI.accentTintHover : 'rgba(236, 239, 241, 0.98)',
      borderColor: MAP_UI.accent,
    },
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.75rem',
    px: 1.5,
    py: 0.5,
    minWidth: 0,
  });

  useEffect(() => {
    const syncFullscreen = () => {
      setMapFullscreen(document.fullscreenElement === mapShellRef.current);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps?.event) return;
    const t = window.setTimeout(() => {
      window.google.maps.event.trigger(mapRef.current, 'resize');
    }, 100);
    return () => window.clearTimeout(t);
  }, [mapFullscreen]);

  const toggleMapFullscreen = useCallback(async () => {
    const shell = mapShellRef.current;
    if (!shell) return;
    try {
      if (document.fullscreenElement === shell) {
        await document.exitFullscreen();
      } else {
        await shell.requestFullscreen();
      }
    } catch (err) {
      console.error('Map fullscreen failed:', err);
      enqueueSnackbar('Could not enter fullscreen mode', { variant: 'warning' });
    }
  }, [enqueueSnackbar]);

  const activeMapContainerStyle = mapFullscreen
    ? { width: '100%', height: '100vh', minHeight: '100vh' }
    : mapContainerStyle;

  const mapDialogContainer = mapFullscreen ? mapShellRef.current : undefined;

  // MUI Tooltip portals to document.body by default; in fullscreen that sits
  // outside the map shell and tooltips never paint. Portal into the shell.
  const mapTooltipPopperProps = mapDialogContainer
    ? { container: mapDialogContainer, sx: { zIndex: 1500 } }
    : undefined;

  const mapToolbarSx = {
    position: 'absolute',
    top: 10,
    right: 10,
    left: 'auto',
    transform: 'none',
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: 'max-content',
    gap: 1,
    pointerEvents: 'none',
  };

  const mapToolbarBtnPointerSx = { pointerEvents: 'auto' };

  if (loadError) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Alert severity="error">
          Error loading Google Maps. Please check your internet connection and try again.
        </Alert>
      </Paper>
    );
  }

  if (!isLoaded) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading map...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!devices) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  // When no devices have GPS but there are devices without GPS, show the map with sidebar
  // to allow manual placement. Only return early if there are truly no devices at all.
  if (devicesWithLocation.length === 0 && devicesWithoutGPS.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Alert severity="info">
          No devices available. Add devices to see them on the map.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">
          Device Map
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {devicesWithLocation.length > 0 ? (
            <>
          <Chip 
                label={showPublicWifiLampsLayer
                  ? `${mapDisplayedDevices.length} on map (Public WiFi layer)`
                  : `${devicesWithLocation.length} on map`}
            color="primary" 
            size="small"
          />
          <Chip 
            label={`${devicesWithLocation.filter(d => getDeviceType(d.capabilities) !== 'AI').length} network`} 
            color="default" 
            size="small"
            variant="outlined"
          />
          <Chip 
            label={`${devicesWithLocation.filter(d => getDeviceType(d.capabilities) === 'AI').length} AI`} 
            color="info" 
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${devicesWithLocation.filter(d => d.connected).length} online`}
            size="small"
            sx={{
              bgcolor: MAP_UI.onlineChipBg,
              color: MAP_UI.onlineChipText,
              border: `1px solid ${MAP_UI.onlineChipBorder}`,
              fontWeight: 600,
            }}
          />
          <Chip
            label={`${devicesWithLocation.filter(d => !d.connected).length} offline`}
            size="small"
            sx={{
              bgcolor: MAP_UI.offlineChipBg,
              color: MAP_UI.offlineChipText,
              border: `1px solid ${MAP_UI.offlineChipBorder}`,
              fontWeight: 600,
            }}
          />
          {devicesWithLocation.filter(d => d.isInheritedGPS).length > 0 && (
            <Chip 
              label={`${devicesWithLocation.filter(d => d.isInheritedGPS).length} inherited GPS`} 
              color="info" 
              size="small"
              variant="outlined"
                />
              )}
            </>
          ) : (
            <Chip 
              label={`${devicesWithoutGPS.length} devices without GPS`} 
              color="warning" 
              size="small"
              icon={<MapIcon />}
            />
          )}
        </Box>
      </Box>
      
      {/* TM candidates focus banner */}
      {showTmCandidatesForDevice && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={handleClearTmCandidates}>
              Clear
            </Button>
          }
        >
          Showing TM candidates for <strong>{showTmCandidatesForDevice.name || showTmCandidatesForDevice.serial_number}</strong>
          {tmCandidatesData?.candidates ? ` — ${tmCandidatesData.candidates.length} candidate${tmCandidatesData.candidates.length !== 1 ? 's' : ''}` : ''}
          . Other devices are dimmed.
        </Alert>
      )}

      {/* Show alert when no devices have GPS location */}
      {devicesWithLocation.length === 0 && devicesWithoutGPS.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No devices have GPS coordinates yet. Click on a device in the sidebar, then click on the map to place it.
        </Alert>
      )}

      <Box
        ref={mapShellRef}
        sx={{
          position: 'relative',
          ...(mapFullscreen ? {
            width: '100vw',
            height: '100vh',
            bgcolor: 'background.default',
          } : {}),
        }}
      >
          <GoogleMap
            key={`device-map-${theme.palette.mode}-${placesPreset.mapId}`}
            mapContainerStyle={{
              ...activeMapContainerStyle,
              cursor: (selectedDeviceForGPS || capAim) ? 'crosshair' : (drawingMode ? 'crosshair' : (parentSelectionMode ? 'crosshair' : (mapSelectionMode ? 'crosshair' : 'default')))
            }}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={handleMapLoad}
            onClick={handleMapClick}
            options={{
              streetViewControl: true,
              mapTypeControl: true,
              fullscreenControl: false,
              draggable: !drawingMode,
              gestureHandling: drawingMode ? 'none' : 'auto',
              draggableCursor: drawingMode ? 'crosshair' : 'grab',
              disableDoubleClickZoom: drawingMode, // Disable double-click zoom when drawing
              mapId: placesPreset.mapId, // Required for AdvancedMarkerElement; also selects the POI style
              colorScheme: mapColorScheme,
            }}
          >
            <MapPlacesControl preset={placesPreset} onChange={changePlacesPreset} dark={theme.palette.mode === 'dark'} />
            {isLoading && (
              <Box sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                zIndex: 1000
              }}>
                <CircularProgress />
              </Box>
            )}

            {/* GPS Assignment Mode Overlay */}
            {selectedDeviceForGPS && (
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                backgroundColor: MAP_UI.accentBg,
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  📍 Click on the map to set GPS location for: {getDeviceDisplayName(selectedDeviceForGPS)}
                </Typography>
                {canLockCurrentDeviceGps(selectedDeviceForGPS) && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={metadataSaving}
                    onClick={() => handleLockCurrentDeviceGps(selectedDeviceForGPS)}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' },
                    }}
                  >
                    Lock current GPS
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSelectedDeviceForGPS(null)}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {/* Parent Selection Mode Overlay */}
            {parentSelectionMode && deviceSelectingParent && (
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                backgroundColor: 'rgba(33, 150, 243, 0.95)',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  👆 Click on another device to set as parent for: {deviceSelectingParent.name || deviceSelectingParent.serial_number}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setParentSelectionMode(false);
                    setDeviceSelectingParent(null);
                  }}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {/* Map Selection Mode Overlay for Access List */}
            {mapSelectionMode && deviceForAccessList && (
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                backgroundColor: mapSelectionMode === 'whitelist' ? 'rgba(76, 175, 80, 0.95)' : 'rgba(244, 67, 54, 0.95)',
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {mapSelectionMode === 'whitelist' ? '✅' : '🚫'} Click devices on map to add to {mapSelectionMode === 'whitelist' ? 'whitelist' : 'blacklist'} for: {deviceForAccessList.name || deviceForAccessList.serial_number}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setMapSelectionMode(null)}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Done
                </Button>
              </Box>
            )}

            {/* iPerf Selection Mode Overlay */}
            {iperfSelectionMode && !iperfConfirmDialogOpen && (
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 2000,
                backgroundColor: MAP_UI.accentBg,
                color: 'white',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {!iperfFromDevice 
                    ? '📍 Click on the source device (From)' 
                    : !iperfToDevice 
                    ? '📍 Click on the destination device (To)' 
                    : '✓ Both devices selected'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setIperfSelectionMode(false);
                    setIperfFromDevice(null);
                    setIperfToDevice(null);
                    setIperfConfirmDialogOpen(false);
                    enqueueSnackbar('iPerf selection cancelled', { variant: 'info' });
                  }}
                  sx={{
                    color: 'white',
                    borderColor: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    },
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {isLoadingWifiDetails && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 60,
                  right: 20,
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: 20,
                  padding: '4px 12px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
                }}
              >
                <CircularProgress size={16} sx={{ color: '#9c27b0' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                  Loading WiFi details...
                </Typography>
              </Box>
            )}

            {/* Heard-by overlay active banner */}
            {heardByOverlayActive && (
              <Box
                sx={{
                  position: 'absolute',
                  top: iperfViewRun ? 96 : 60,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                }}
              >
                <Chip
                  icon={<HearingIcon sx={{ color: 'white !important' }} />}
                  label={`Heard by ${heardByLinks.length} · STA ≥ ${heardByMinRssi} dBm`}
                  onClick={() => setHeardByDialogOpen(true)}
                  onDelete={handleClearHeardBy}
                  sx={{
                    backgroundColor: 'rgba(0, 131, 143, 0.92)',
                    color: 'white',
                    fontWeight: 700,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.85)' },
                  }}
                />
              </Box>
            )}

            {/* iPerf overlay active banner */}
            {iperfViewRun && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 60,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: MAP_UI.accentBg,
                  color: 'white',
                  borderRadius: 20,
                  padding: '4px 14px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                  cursor: 'pointer',
                }}
                onClick={() => setIperfViewRun(null)}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  iPerf overlay: {iperfViewRun.name || iperfViewRun.id?.slice(0, 8)} — click to clear
                </Typography>
              </Box>
            )}

            {/* CAP aiming: the popup is hidden until the user clicks the target */}
            {capAim && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1100,
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  maxWidth: 'calc(100% - 24px)',
                }}
              >
                <Typography variant="body2">
                  Click the spot or lamp the antenna of CAP {capAim.serial} points at (Esc to cancel)
                </Typography>
                <Button size="small" onClick={() => setCapAim(null)} sx={{ textTransform: 'none', flexShrink: 0 }}>
                  Cancel
                </Button>
              </Paper>
            )}

            {/* iPerf overlay color legend */}
            {iperfViewRun && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 32,
                  left: 12,
                  zIndex: 1000,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: 2,
                  padding: '8px 12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  minWidth: 160,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: '#424242' }}>
                  iPerf Throughput
                </Typography>
                {[
                  { color: MAP_UI.throughputExcellent, label: '> 300 Mbps', sub: 'Excellent' },
                  { color: MAP_UI.moderate, label: '100 – 300 Mbps', sub: 'Moderate' },
                  { color: MAP_UI.throughputPoor, label: '< 100 Mbps', sub: 'Poor' },
                  { color: '#9e9e9e', label: 'No data', sub: '' },
                ].map(({ color, label, sub }) => (
                  <Box key={color} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                    <Box sx={{ width: 28, height: 6, borderRadius: 1, backgroundColor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#212121', lineHeight: 1.3 }}>
                      {label}
                      {sub && (
                        <Box component="span" sx={{ color: '#757575', ml: 0.5 }}>({sub})</Box>
                      )}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.4, color: '#424242' }}>
                    Arrow types
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                    <Box sx={{ width: 28, height: 3, borderRadius: 1, backgroundColor: '#757575', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#212121' }}>Hop (child → parent)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Simulate the white-outlined E2E line */}
                    <Box sx={{ position: 'relative', width: 28, height: 10, flexShrink: 0 }}>
                      <Box sx={{ position: 'absolute', top: 2, left: 0, width: 28, height: 6, borderRadius: 1, bgcolor: 'white' }} />
                      <Box sx={{ position: 'absolute', top: 4, left: 0, width: 28, height: 2, bgcolor: '#757575' }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#212121', fontWeight: 700 }}>
                      E2E (leaf → CAP) — hover for breakdown
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Data-traffic layer color legend */}
            {showDataTrafficLayer && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 32,
                  left: 12,
                  zIndex: 1000,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: 2,
                  padding: '8px 12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  minWidth: 160,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75, color: '#424242' }}>
                  Data Traffic — {getTrafficLayerCategoryLabel(dataTrafficCategory)} (RX+TX)
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {TRAFFIC_LAYER_CATEGORIES.map(({ id, label }) => (
                    <Chip
                      key={id}
                      label={label}
                      size="small"
                      clickable
                      color={dataTrafficCategory === id ? 'primary' : 'default'}
                      variant={dataTrafficCategory === id ? 'filled' : 'outlined'}
                      onClick={() => setDataTrafficCategory(id)}
                      sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                  ))}
                </Stack>
                {TRAFFIC_RATE_LEGEND.map(({ color, label }) => (
                  <Box key={color} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: '#212121', lineHeight: 1.3 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #e0e0e0' }}>
                  <Typography variant="caption" sx={{ color: '#757575', lineHeight: 1.3 }}>
                    Rates are averaged over the last 2 minutes
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Drawing mode instruction overlay */}
            {drawingMode && (
              <Box sx={{ 
                position: 'absolute', 
                top: 60, 
                left: '50%', 
                transform: 'translateX(-50%)',
                zIndex: 1000,
                backgroundColor: 'rgba(33, 150, 243, 0.9)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                fontSize: '13px',
                pointerEvents: 'none'
              }}>
                Click to add points, click near start point to close polygon
              </Box>
            )}

            {/* Estimated CAP antenna directions (ground-anchored, rotate with the map) */}
            <CapDirectionWedges caps={capDirectionWedges} />

            {/* Render stored polygons */}
            {polygons.map((poly) => (
              <Polygon
                key={poly.id}
                paths={poly.coordinates}
                options={{
                  fillColor: '#2196f3',
                  fillOpacity: 0.2,
                  strokeWeight: 2,
                  strokeColor: '#1976d2',
                  clickable: false
                }}
              />
            ))}

            {/* Render connection lines between devices and their parents */}
            {mapDisplayedDevices.map((device) => {
              if (selectedSegmentSerialSet && !isDeviceInSelectedSegment(device)) return null;
              if (!device.parent_serial) return null;
              if (showPublicWifiLampsLayer && !mapDisplayedSerialSet.has(device.parent_serial?.toLowerCase())) return null;
              
              const parent = mapDisplayedDevices.find(d => 
                d.serial_number && 
                d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
              );
              
              if (!parent) return null;
              
              const deviceSerial = device.serial_number?.toLowerCase();
              const parentSerial = parent.serial_number?.toLowerCase();
              if (!deviceSerial || !parentSerial || deviceSerial === parentSerial) return null;
              
              // Calculate distance
              const distance = calculateDistance(device.lat, device.lng, parent.lat, parent.lng);
              
              if (distance === 0) return null;
              
              // Check if this connection is part of the highlighted tree
              const isHighlighted = highlightedTree && 
                highlightedTree.has(deviceSerial) &&
                highlightedTree.has(parentSerial);
              
              // Only render if showing all connections, channel mode, or if this connection is highlighted
              const shouldShow = showAllConnections || showChannelMode || isHighlighted;
              if (!shouldShow) return null;

              // Dim connections that don't involve relevant TM-candidate devices
              const tmCandidateSerials = showTmCandidatesForDevice && tmCandidatesData?.candidates
                ? (() => {
                    const s = new Set();
                    s.add(showTmCandidatesForDevice.serial_number?.toLowerCase());
                    tmCandidatesData.candidates.forEach((c) => {
                      const apNodeId = c.AP_NodeID || c.ap_node_id || '';
                      s.add(apNodeId.toLowerCase().replace(/:/g, ''));
                    });
                    return s;
                  })()
                : null;
              const isConnectionDimmed = tmCandidateSerials &&
                !tmCandidateSerials.has(deviceSerial) &&
                !tmCandidateSerials.has(parentSerial);
              
              const { apRssi, staRssi } = getBackhaulConnectionRssi(device, parent);
              const isGoodSignal = apRssi !== null && apRssi >= -85;
              const isPoorSignal = apRssi !== null && apRssi < -85;
              
              // Calculate midpoint for label
              const midLat = (device.lat + parent.lat) / 2;
              const midLng = (device.lng + parent.lng) / 2;
              
              const distanceText = distance < 1000 
                ? `${Math.round(distance)}m` 
                : `${(distance / 1000).toFixed(2)}km`;

              const uplinkChannelInfo = showChannelMode ? getDeviceUplinkChannelInfo(device) : null;
              
              let arrowColor;
              let labelText;
              let tooltipContent;

              if (showChannelMode) {
                arrowColor = channelModeArrowColor(uplinkChannelInfo?.band);
                labelText = uplinkChannelInfo?.channel ? String(uplinkChannelInfo.channel) : '?';
                tooltipContent = (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Channel: {labelText}
                      {uplinkChannelInfo?.band ? ` (${uplinkChannelInfo.band})` : ''}
                    </Typography>
                    <Typography variant="body2">
                      {device.name || device.serial_number} → {parent.name || parent.serial_number}
                    </Typography>
                    {apRssi !== null && (
                      <Typography variant="body2">AP RSSI: {apRssi} dBm</Typography>
                    )}
                    {staRssi !== null && (
                      <Typography variant="body2">STA RSSI: {staRssi} dBm</Typography>
                    )}
                    <Typography variant="body2">Distance: {distanceText}</Typography>
                  </Box>
                );
              } else {
                if (isPoorSignal) {
                  arrowColor = '#ff0000';
                } else if (isGoodSignal) {
                  arrowColor = '#00ff00';
                } else {
                  arrowColor = '#2196f3';
                }
                labelText = apRssi !== null ? `${apRssi} dBm` : distanceText;
                tooltipContent = (
                  <Box sx={{ textAlign: 'center' }}>
                    {apRssi !== null && (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>AP RSSI: {apRssi} dBm</Typography>
                    )}
                    {staRssi !== null && (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>STA RSSI: {staRssi} dBm</Typography>
                    )}
                    <Typography variant="body2">Distance: {distanceText}</Typography>
                  </Box>
                );
              }
              
              return (
                <React.Fragment key={`connection-${device.serial_number}`}>
                  <Polyline
                    path={[
                      { lat: device.lat, lng: device.lng },
                      { lat: parent.lat, lng: parent.lng }
                    ]}
                    options={{
                      strokeColor: isConnectionDimmed ? '#888888' : arrowColor,
                      strokeOpacity: isConnectionDimmed ? 0.12 : (isHighlighted ? 1.0 : 0.7),
                      strokeWeight: isHighlighted ? 3 : 2,
                      icons: [{
                        icon: {
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          scale: isHighlighted ? 4 : 3,
                          strokeColor: isConnectionDimmed ? '#888888' : arrowColor,
                          fillColor: isConnectionDimmed ? '#888888' : arrowColor,
                          fillOpacity: 1
                        },
                        offset: '50%'
                      }]
                    }}
                  />
                  <OverlayView
                      position={{ lat: midLat, lng: midLng }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Tooltip 
                        title={tooltipContent}
                        arrow
                        placement="top"
                        enterDelay={200}
                        leaveDelay={0}
                        PopperProps={mapTooltipPopperProps}
                    >
                      <Box sx={{ 
                          position: 'relative',
                          transform: showChannelMode
                            ? 'translate(-50%, -50%)'
                            : 'translate(-50%, calc(-50% - 24px))',
                          backgroundColor: arrowColor,
                        color: 'white',
                        fontWeight: 'bold',
                          fontSize: showChannelMode ? '12px' : '11px',
                          borderRadius: showChannelMode ? '50%' : '3px',
                          padding: showChannelMode ? '5px 7px' : '3px 7px',
                        textAlign: 'center',
                          minWidth: showChannelMode ? '28px' : '50px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          pointerEvents: 'auto',
                          cursor: 'help',
                          opacity: isConnectionDimmed ? 0 : 1,
                          lineHeight: 1,
                        }}>
                          {labelText}
                      </Box>
                      </Tooltip>
                  </OverlayView>
                </React.Fragment>
              );
            })}

            {/* Render whitelist overlay connections */}
            {showWhitelistOverlay && devicesWithLocation.map((device) => {
              if (selectedSegmentSerialSet && !isDeviceInSelectedSegment(device)) return null;
              if (!device.parent_serial) return null;

              const parent = devicesWithLocation.find(d =>
                d.serial_number &&
                d.serial_number.toLowerCase() === device.parent_serial.toLowerCase()
              );

              if (!parent) return null;

              const deviceSerial = device.serial_number?.toLowerCase();
              const parentSerial = parent.serial_number?.toLowerCase();
              if (!deviceSerial || !parentSerial || deviceSerial === parentSerial) return null;

              const distance = calculateDistance(device.lat, device.lng, parent.lat, parent.lng);
              if (distance === 0) return null;

              const whitelist = device.tm_config?.white_list
                ? device.tm_config.white_list.split(',').map(s => s.trim()).filter(Boolean)
                : [];

              const hasWhitelist = whitelist.length > 0;
              if (!hasWhitelist) return null;

              const parentInWhitelist = whitelist.some(wl =>
                wl.toLowerCase() === device.parent_serial.toLowerCase()
              );

              const arrowColor = parentInWhitelist ? '#4caf50' : '#f44336';
              const parentSerialLower = device.parent_serial?.toLowerCase();

              const midLat = (device.lat + parent.lat) / 2;
              const midLng = (device.lng + parent.lng) / 2;

              const tooltipContent = (
                <Box sx={{ textAlign: 'left', maxWidth: 300 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {device.name || device.serial_number}
                  </Typography>
                  <Typography variant="body2">
                    Parent: {parent.name || parent.serial_number}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Whitelist: {whitelist.join(', ')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      fontWeight: 600,
                      color: parentInWhitelist ? '#4caf50' : '#f44336',
                    }}
                  >
                    {parentInWhitelist ? 'Parent matches whitelist' : 'Parent NOT in whitelist'}
                  </Typography>
                </Box>
              );

              return (
                <React.Fragment key={`whitelist-${device.serial_number}`}>
                  <Polyline
                    path={[
                      { lat: device.lat, lng: device.lng },
                      { lat: parent.lat, lng: parent.lng }
                    ]}
                    options={{
                      strokeColor: arrowColor,
                      strokeOpacity: 0.9,
                      strokeWeight: 3,
                      icons: [{
                        icon: {
                          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                          scale: 4,
                          strokeColor: arrowColor,
                          fillColor: arrowColor,
                          fillOpacity: 1
                        },
                        offset: '50%'
                      }]
                    }}
                  />
                  <OverlayView
                    position={{ lat: midLat, lng: midLng }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <Tooltip
                      title={tooltipContent}
                      arrow
                      placement="top"
                      enterDelay={200}
                      leaveDelay={0}
                      PopperProps={mapTooltipPopperProps}
                    >
                      <Box sx={{
                        position: 'relative',
                        transform: 'translate(-50%, calc(-50% - 24px))',
                        backgroundColor: arrowColor,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '10px',
                        borderRadius: '3px',
                        padding: '3px 7px',
                        textAlign: 'center',
                        minWidth: '50px',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        pointerEvents: 'auto',
                        cursor: 'help',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.25,
                      }}>
                        {whitelist.length === 1 ? (
                          whitelist[0].slice(-4)
                        ) : (
                          whitelist.map((serial, idx) => {
                            const isParent = Boolean(
                              parentSerialLower
                              && serial.toLowerCase() === parentSerialLower
                            );
                            return (
                              <React.Fragment key={serial}>
                                {idx > 0 && (
                                  <Box component="span" sx={{ opacity: 0.85, px: 0.1 }}>
                                    ,
                                  </Box>
                                )}
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.45)',
                                    borderRadius: '2px',
                                    px: 0.45,
                                    py: 0.15,
                                    fontWeight: isParent ? 800 : 700,
                                    lineHeight: 1,
                                    boxShadow: isParent
                                      ? '0 0 0 1px rgba(255, 255, 255, 0.85)'
                                      : 'none',
                                  }}
                                >
                                  {serial.slice(-4)}
                                </Box>
                              </React.Fragment>
                            );
                          })
                        )}
                      </Box>
                    </Tooltip>
                  </OverlayView>
                </React.Fragment>
              );
            })}

            {/* Render blacklist overlay connections */}
            {showBlacklistOverlay && devicesWithLocation.flatMap((device) => {
              if (selectedSegmentSerialSet && !isDeviceInSelectedSegment(device)) return [];
              const blacklist = device.tm_config?.black_list
                ? device.tm_config.black_list.split(',').map(s => s.trim()).filter(Boolean)
                : [];

              if (blacklist.length === 0) return [];

              return blacklist.map(blSerial => {
                const target = devicesWithLocation.find(d =>
                  d.serial_number &&
                  d.serial_number.toLowerCase() === blSerial.toLowerCase()
                );

                if (!target) return null;

                const deviceSerial = device.serial_number?.toLowerCase();
                const targetSerial = target.serial_number?.toLowerCase();
                if (!deviceSerial || !targetSerial || deviceSerial === targetSerial) return null;

                const distance = calculateDistance(device.lat, device.lng, target.lat, target.lng);
                if (distance === 0) return null;

                const isConnectedToBlacklisted = device.parent_serial &&
                  device.parent_serial.toLowerCase() === blSerial.toLowerCase();

                const arrowColor = isConnectedToBlacklisted ? '#f44336' : MAP_UI.accentMuted;

                const midLat = (device.lat + target.lat) / 2;
                const midLng = (device.lng + target.lng) / 2;

                const tooltipContent = (
                  <Box sx={{ textAlign: 'left', maxWidth: 300 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {device.name || device.serial_number}
                    </Typography>
                    <Typography variant="body2">
                      Blacklisted: {target.name || target.serial_number}
                    </Typography>
                    {isConnectedToBlacklisted && (
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: '#f44336' }}>
                        Currently connected to this blacklisted parent!
                      </Typography>
                    )}
                  </Box>
                );

                return (
                  <React.Fragment key={`blacklist-${device.serial_number}-${blSerial}`}>
                    <Polyline
                      path={[
                        { lat: device.lat, lng: device.lng },
                        { lat: target.lat, lng: target.lng }
                      ]}
                      options={{
                        strokeColor: arrowColor,
                        strokeOpacity: 0.9,
                        strokeWeight: isConnectedToBlacklisted ? 4 : 2,
                        icons: [{
                          icon: {
                            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: isConnectedToBlacklisted ? 5 : 3,
                            strokeColor: arrowColor,
                            fillColor: arrowColor,
                            fillOpacity: 1
                          },
                          offset: '50%'
                        }]
                      }}
                    />
                    <OverlayView
                      position={{ lat: midLat, lng: midLng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Tooltip
                        title={tooltipContent}
                        arrow
                        placement="top"
                        enterDelay={200}
                        leaveDelay={0}
                        PopperProps={mapTooltipPopperProps}
                      >
                        <Box sx={{
                          position: 'relative',
                          transform: 'translate(-50%, calc(-50% - 24px))',
                          backgroundColor: arrowColor,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '10px',
                          borderRadius: '3px',
                          padding: '3px 7px',
                          textAlign: 'center',
                          minWidth: '50px',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          pointerEvents: 'auto',
                          cursor: 'help',
                        }}>
                          {isConnectedToBlacklisted ? `BL: ${blSerial.slice(-4)}` : blSerial.slice(-4)}
                        </Box>
                      </Tooltip>
                    </OverlayView>
                  </React.Fragment>
                );
              }).filter(Boolean);
            })}

            {/* Don't render segment boundaries/polygons when Show Segments is active */}

            {(() => {
              // Pre-compute the set of serials relevant in TM-candidates mode:
              // the focused device + every candidate device. Used to dim others.
              const tmRelevantSerials = showTmCandidatesForDevice && tmCandidatesData?.candidates
                ? (() => {
                    const s = new Set();
                    s.add(showTmCandidatesForDevice.serial_number?.toLowerCase());
                    tmCandidatesData.candidates.forEach((c) => {
                      const apNodeId = c.AP_NodeID || c.ap_node_id || '';
                      const serial = apNodeId.toLowerCase().replace(/:/g, '');
                      if (serial) s.add(serial);
                    });
                    return s;
                  })()
                : null;
              const heardByRelevantSerials = heardByOverlayActive
                ? (() => {
                    const s = new Set(heardByHearerSerials);
                    s.add(heardByTarget.serial_number?.toLowerCase());
                    return s;
                  })()
                : null;
              return mapDisplayedDevices.map((device) => {
              // Check if this device is part of the highlighted tree
              const isHighlighted = highlightedTree && 
                highlightedTree.has(device.serial_number.toLowerCase());
              
              // Check if this device is part of the selected segment
              const isInSelectedSegment = isDeviceInSelectedSegment(device);
              
              // Check group highlight (from Groups dialog or parent prop)
              const deviceSerialLower = device.serial_number.toLowerCase();
              const isInGroupDialogHighlight = selectedGroupForHighlight &&
                (selectedGroupForHighlight.devices || []).some(s => s.toLowerCase() === deviceSerialLower);
              const isInPropGroupHighlight = Array.isArray(highlightedGroupSerials) &&
                highlightedGroupSerials.some(s => s.toLowerCase() === deviceSerialLower);
              const isInGroupHighlight = isInGroupDialogHighlight || isInPropGroupHighlight;
              
              // Get segment color if device is in selected segment
              const segmentColor = isInSelectedSegment
                ? (selectedSegmentForHighlight?.id === NO_SEGMENT_OVERLAY_ID
                  ? NO_SEGMENT_OVERLAY_COLOR
                  : segmentColorById.get(selectedSegmentForHighlight.id) || '#9c27b0')
                : null;
              
              // Check if this device is selected for iPerf
              const isIperfFrom = iperfSelectionMode && iperfFromDevice?.serial_number === device.serial_number;
              const isIperfTo = iperfSelectionMode && iperfToDevice?.serial_number === device.serial_number;
              const isIperfSelected = isIperfFrom || isIperfTo;
              
              // Check if this device is in whitelist or blacklist (case-insensitive comparison)
              const deviceSerial = device.serial_number.toLowerCase();
              const isInWhitelist = highlightedAccessList.whitelist.some(serial => serial.toLowerCase() === deviceSerial);
              const isInBlacklist = highlightedAccessList.blacklist.some(serial => serial.toLowerCase() === deviceSerial);
              const isInBothLists = isInWhitelist && isInBlacklist;
              
              // Debug logging for ALL devices being rendered (first time only)
              if (!window._markerRenderLogged) {
                window._markerRenderLogged = true;
              }
              
              // Debug logging for devices with access list membership
              if ((isInWhitelist || isInBlacklist) && !window._accessListDevices) {
                window._accessListDevices = new Set();
              }
              if ((isInWhitelist || isInBlacklist) && !window._accessListDevices.has(deviceSerial)) {
                window._accessListDevices.add(deviceSerial);
              }
              
              // Determine border color based on priority (access list > iPerf)
              let borderColor = null;
              let borderWidth = 3;
              
              // Priority 1: Access list status
              if (isInBothLists) {
                borderColor = MAP_UI.conflict; // Muted highlight for WL+BL conflict
                borderWidth = 4;
              } else if (isInWhitelist) {
                borderColor = '#4caf50'; // Green for whitelist
                borderWidth = 4;
              } else if (isInBlacklist) {
                borderColor = '#f44336'; // Red for blacklist
                borderWidth = 4;
              }
              // Priority 2: iPerf selection (only if not in access list)
              else if (isIperfFrom) {
                borderColor = '#2196f3'; // Blue for iPerf "from"
                borderWidth = 3;
              } else if (isIperfTo) {
                borderColor = MAP_UI.accent; // iPerf "to"
                borderWidth = 3;
              }
              
              // Group highlight border (lower priority than access list / iPerf)
              if (!borderColor && isInGroupHighlight) {
                borderColor = '#7c3aed'; // Purple for group highlight
                borderWidth = 4;
              }

              const isOutlined = outlinedSerial
                && device.serial_number
                && outlinedSerial.toLowerCase() === device.serial_number.toLowerCase();
              if (isOutlined) {
                borderColor = MAP_UI.selectedMarkerOutline;
                borderWidth = 3;
              }

              const isHeardByTarget = heardByTarget?.serial_number?.toLowerCase() === deviceSerialLower;
              const isHeardByHearer = heardByHearerSerials.has(deviceSerialLower);

              // Dim devices that are not relevant in TM-candidates mode or segment overlay modes
              const isTmDimmed = tmRelevantSerials &&
                !tmRelevantSerials.has(device.serial_number?.toLowerCase());
              const isSegmentOverlayDimmed = segmentMapOverlayActive && !isInSelectedSegment;
              const isHeardByDimmed = heardByRelevantSerials &&
                !heardByRelevantSerials.has(device.serial_number?.toLowerCase());
              const markerOpacity = isTmDimmed || isHeardByDimmed
                ? 0.15
                : (isSegmentOverlayDimmed ? 0.2 : 1);

              // Data-traffic layer colors the marker fill by total throughput.
              const trafficColor = showDataTrafficLayer
                ? getDeviceTrafficColor(device, dataTrafficCategory)
                : null;

              // Get custom icon - use segment color for fill if device is in selected segment, and add border
              let markerIcon;
              if (isHeardByTarget) {
                markerIcon = getMarkerIcon(device, MAP_UI.heardByFill, borderColor || MAP_UI.heardByBorder, borderColor ? borderWidth : 4, markerOpacity);
              } else if (isHeardByHearer) {
                markerIcon = getMarkerIcon(device, '#00838f', borderColor || '#006064', borderColor ? borderWidth : 4, markerOpacity);
              } else if (isInSelectedSegment && segmentColor) {
                markerIcon = getMarkerIcon(device, segmentColor, borderColor, borderWidth, markerOpacity);
              } else if (isInGroupHighlight && !isInSelectedSegment) {
                markerIcon = getMarkerIcon(device, '#7c3aed', borderColor, borderWidth, markerOpacity);
              } else if (trafficColor) {
                markerIcon = getMarkerIcon(device, trafficColor, borderColor, borderWidth, markerOpacity);
              } else {
                markerIcon = getMarkerIcon(device, null, borderColor, borderWidth, markerOpacity);
              }
              
              // Calculate zIndex - access list markers should be highly visible
              const markerZIndex = isOutlined ? 2200 :
                                   ((isInWhitelist || isInBlacklist) ? 1800 :
                                   (isIperfSelected ? 2000 : 
                                   (isInSelectedSegment ? 1500 : 
                                   (isInGroupHighlight ? 1200 :
                                   (isHighlighted ? 1000 : 100)))));
              
              return (
                <AdvancedMarker
                  key={device.serial_number}
                  position={{ lat: device.lat, lng: device.lng }}
                  onClick={() => handleMarkerClick(device)}
                  onDblClick={() => handleMarkerDoubleClick(device)}
                  onRightClick={() => handleMarkerRightClick(device)}
                  content={markerIcon}
                  title={[
                    buildMapMarkerTitle(
                      device,
                      showDataTrafficLayer,
                      dataTrafficCategory,
                      treeChildCounts.get(device.serial_number?.toLowerCase()),
                    ),
                    capAzimuthTitle(capAzimuthBySerial.get(device.serial_number?.toLowerCase())),
                  ].filter(Boolean).join('\n')}
                  zIndex={isTmDimmed ? 10 : markerZIndex}
                />
              );
            });
            })()}

          {/* iPerf test run overlay – draw one arrow per completed hop/e2e test */}
          {iperfViewRun && iperfViewRun.tests && (() => {
            const devicesBySerial = {};
            devicesWithLocation.forEach((d) => {
              if (d.serial_number) devicesBySerial[d.serial_number.toLowerCase()] = d;
            });

            // Parse bandwidth from a raw_result (handles both firmware formats)
            const parseBw = (test) => {
              if (!test.raw_result) return null;
              const r = test.raw_result;
              const bw = r.perf?.bw ?? r.status?.perf?.bw ?? null;
              const unit = r.perf?.bw_unit ?? r.status?.perf?.bw_unit ?? 'Mbits/sec';
              if (bw == null) return null;
              const mbps = unit && unit.toLowerCase().startsWith('g') ? bw * 1000 : bw;
              return { mbps, raw: bw, unit };
            };

            const parseLoss = (test) => {
              const l = test.raw_result?.perf?.data_loss ?? test.raw_result?.status?.perf?.data_loss ?? null;
              return l != null ? String(l) : null;
            };

            const parseRfStats = (test) => {
              const perf = test.raw_result?.perf ?? test.raw_result?.status?.perf ?? {};
              const quality = perf.Quality ?? perf['Max Quality'] ?? null;
              const stats = perf.stats ?? {};
              const getDominant = (obj) => {
                if (!obj || typeof obj !== 'object') return null;
                let best = null; let bestVal = -1;
                Object.entries(obj).forEach(([k, v]) => { if (v > bestVal) { bestVal = v; best = k; } });
                return best;
              };
              return {
                quality,
                bw: getDominant(stats.bw_counts),
                mcs: getDominant(stats.mcs_counts),
                nss: getDominant(stats.nss_counts),
              };
            };

            // Color arrow by throughput (green > 300, yellow 100-300, red < 100, grey = no data)
            const bwColor = (mbps) => {
              if (mbps == null) return '#9e9e9e';
              if (mbps > 300) return MAP_UI.throughputExcellent;
              if (mbps >= 100) return MAP_UI.moderate;
              return MAP_UI.throughputPoor;
            };

            // Build a per-e2e hop-chain lookup:
            // key = "leafSerial→capSerial", value = sorted array of completed hop tests
            // along that path (matched by from_serial / to_serial chains ending at capSerial).
            const hopTests = iperfViewRun.tests.filter((t) => t.type === 'hop' && t.status === 'completed');
            // Index hops by their from_serial so we can walk the chain from leaf → cap.
            const hopsByFrom = {};
            hopTests.forEach((t) => {
              const k = t.from_serial?.toLowerCase();
              if (k) {
                if (!hopsByFrom[k]) hopsByFrom[k] = [];
                hopsByFrom[k].push(t);
              }
            });

            // For a given e2e test walk from leaf → cap collecting the hop chain.
            const resolveHopChain = (e2eTest) => {
              const capSerial = e2eTest.to_serial?.toLowerCase();
              const chain = [];
              let cursor = e2eTest.from_serial?.toLowerCase();
              const visited = new Set();
              while (cursor && cursor !== capSerial && !visited.has(cursor)) {
                visited.add(cursor);
                const candidates = (hopsByFrom[cursor] || []).filter(
                  (h) => h.to_serial?.toLowerCase() === capSerial ||
                         !visited.has(h.to_serial?.toLowerCase()),
                );
                // Prefer the hop whose to_serial is the parent in the chain (lowest hop_index).
                const next = candidates.sort((a, b) => (a.hop_index ?? 99) - (b.hop_index ?? 99))[0];
                if (!next) break;
                chain.push(next);
                cursor = next.to_serial?.toLowerCase();
              }
              // Sort leaf→cap by ascending hop_index (leaf has highest index, cap = 0 or 1)
              chain.sort((a, b) => (b.hop_index ?? 0) - (a.hop_index ?? 0));
              return chain;
            };

            return iperfViewRun.tests.map((test) => {
              if (test.status !== 'completed') return null;
              const from = devicesBySerial[test.from_serial?.toLowerCase()];
              const to   = devicesBySerial[test.to_serial?.toLowerCase()];
              if (!from || !to) return null;
              if (!from.lat || !from.lng || !to.lat || !to.lng) return null;

              const bwInfo = parseBw(test);
              const loss   = parseLoss(test);
              const rf     = parseRfStats(test);
              const color  = bwColor(bwInfo?.mbps);
              const label  = bwInfo ? `${Math.round(bwInfo.mbps)} Mbps` : 'No data';
              const isE2E  = test.type === 'end_to_end';

              const midLat = (from.lat + to.lat) / 2;
              const midLng = (from.lng + to.lng) / 2;

              // For e2e arrows build the hop-breakdown chain for the tooltip.
              const hopChain = isE2E ? resolveHopChain(test) : [];

              const tooltipContent = isE2E ? (
                <Box sx={{ minWidth: 200, maxWidth: 320 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5, fontSize: '0.78rem' }}>
                    E2E: …{test.from_serial?.slice(-6)} → …{test.to_serial?.slice(-6)}
                    {test.path_hops != null ? ` (${test.path_hops} hops)` : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 0.75 }}>
                    Total BW: <strong style={{ color: color === '#9e9e9e' ? 'inherit' : color }}>{label}</strong>
                    {loss != null && <span style={{ marginLeft: 6 }}>· Loss: <strong>{loss}</strong></span>}
                  </Typography>
                  {hopChain.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
                        Hop-by-hop breakdown:
                      </Typography>
                      {hopChain.map((hop, idx) => {
                        const hBw = parseBw(hop);
                        const hLoss = parseLoss(hop);
                        const hColor = bwColor(hBw?.mbps);
                        return (
                          <Box key={hop.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: hColor, flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ flexGrow: 1 }}>
                              Hop {idx + 1}: …{hop.from_serial?.slice(-6)} → …{hop.to_serial?.slice(-6)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: hColor, ml: 0.5 }}>
                              {hBw ? `${Math.round(hBw.mbps)} Mbps` : '—'}
                            </Typography>
                            {hLoss != null && (
                              <Typography variant="caption" sx={{ ml: 0.25, color: 'rgba(255,255,255,0.7)' }}>
                                ({hLoss} loss)
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </>
                  )}
                  {rf.quality != null && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
                      Quality: <strong>{rf.quality}</strong>
                      {rf.mcs && <span style={{ marginLeft: 6 }}>· MCS: <strong>{rf.mcs}</strong></span>}
                      {rf.nss && <span style={{ marginLeft: 6 }}>· NSS: <strong>{rf.nss}</strong></span>}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ minWidth: 160 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Hop: …{test.from_serial?.slice(-6)} → …{test.to_serial?.slice(-6)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    BW: <strong>{label}</strong>
                  </Typography>
                  {loss != null && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Loss: <strong>{loss}</strong>
                    </Typography>
                  )}
                  {rf.quality != null && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Quality: <strong>{rf.quality}</strong>
                    </Typography>
                  )}
                  {rf.bw && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      Channel BW: <strong>{rf.bw}</strong>
                    </Typography>
                  )}
                  {rf.mcs && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      MCS: <strong>{rf.mcs}</strong>
                    </Typography>
                  )}
                  {rf.nss && (
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      NSS: <strong>{rf.nss}</strong>
                    </Typography>
                  )}
                </Box>
              );

              const path = [{ lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }];
              const arrowIcon = {
                icon: {
                  path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: isE2E ? 5 : 4,
                  strokeColor: color,
                  strokeWeight: 1,
                  fillColor: color,
                  fillOpacity: 1,
                },
                offset: '60%',
              };

              return (
                <React.Fragment key={`iperf-overlay-${test.id}`}>
                  {/* White outline polyline behind the colored line — E2E only */}
                  {isE2E && (
                    <Polyline
                      path={path}
                      options={{
                        strokeColor: 'white',
                        strokeOpacity: 1,
                        strokeWeight: 10,
                        zIndex: 18,
                        icons: [],
                      }}
                    />
                  )}
                  {/* Main colored polyline */}
                  <Polyline
                    path={path}
                    options={{
                      strokeColor: color,
                      strokeOpacity: 0.9,
                      strokeWeight: isE2E ? 6 : 3,
                      zIndex: isE2E ? 20 : 10,
                      icons: [arrowIcon],
                    }}
                  />
                  <OverlayView
                    position={{ lat: midLat, lng: midLng }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  >
                    <Tooltip title={tooltipContent} arrow placement="top" enterDelay={100}
                      PopperProps={mapTooltipPopperProps}
                      componentsProps={isE2E ? {
                        tooltip: { sx: { bgcolor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.15)', maxWidth: 360, p: 1.25 } },
                        arrow: { sx: { color: '#1a1a2e' } },
                      } : {}}>
                      <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        position: 'relative',
                        transform: 'translate(-50%, calc(-50% - 22px))',
                        backgroundColor: color,
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: isE2E ? '12px' : '11px',
                        borderRadius: '3px',
                        padding: '3px 7px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'auto',
                        cursor: 'help',
                        boxShadow: isE2E
                          ? '0 0 0 2px white, 0 3px 8px rgba(0,0,0,0.45)'
                          : '0 2px 4px rgba(0,0,0,0.35)',
                      }}>
                        {isE2E && (
                          <Box component="span" sx={{
                            fontSize: '9px',
                            fontWeight: 900,
                            bgcolor: 'rgba(0,0,0,0.28)',
                            borderRadius: '2px',
                            px: '3px',
                            py: '1px',
                            letterSpacing: '0.05em',
                          }}>
                            E2E
                          </Box>
                        )}
                        {label}
                      </Box>
                    </Tooltip>
                  </OverlayView>
                </React.Fragment>
              );
            });
          })()}

          {/* Render neighbor connections */}
          {showNeighborsForDevice && neighborsData && neighborsData.Neighbors && (() => {
            // Pre-process neighbors to count duplicates per destination
            const destinationCounts = {};
            const destinationIndices = {};
            
            neighborsData.Neighbors.forEach((neighbor) => {
              const apNodeId = neighbor.AP_NodeID || neighbor.ap_node_id || '';
              const neighborSerial = apNodeId.toLowerCase().replace(/:/g, '');
              const neighborDevice = devicesWithLocation.find(d => 
                d.serial_number && d.serial_number.toLowerCase() === neighborSerial
              );
              
              if (neighborDevice && neighborDevice.lat && neighborDevice.lng) {
                destinationCounts[neighborSerial] = (destinationCounts[neighborSerial] || 0) + 1;
              }
            });
            
            // Initialize indices for tracking curve offsets
            Object.keys(destinationCounts).forEach(serial => {
              destinationIndices[serial] = 0;
            });

            return (
            <>
              {neighborsData.Neighbors.map((neighbor, index) => {
                const apNodeId = neighbor.AP_NodeID || neighbor.ap_node_id || '';
                const neighborSerial = apNodeId.toLowerCase().replace(/:/g, '');
                const neighborDevice = devicesWithLocation.find(d => 
                  d.serial_number && d.serial_number.toLowerCase() === neighborSerial
                );

                if (!neighborDevice || !neighborDevice.lat || !neighborDevice.lng) {
                  return null;
                }

                const apSNR = neighbor.AP_SNR ?? neighbor.ap_snr ?? 0;
                const staSNR = neighbor.STA_SNR ?? neighbor.sta_snr ?? 0;
                const apNF = neighbor.AP_NF ?? neighbor.ap_nf ?? 0;
                const staNF = neighbor.STA_NF ?? neighbor.sta_nf ?? 0;
                const channel = neighbor.Channel ?? neighbor.channel ?? 0;
                const apRSSI = apSNR + apNF;  // How neighbor hears this device
                const staRSSI = staSNR + staNF;  // How this device hears the neighbor

                // Color based on RSSI signal quality (typical values: -30 to -90 dBm)
                const getColorFromRSSI = (rssi) => {
                  if (rssi >= -50) return '#4caf50'; // green (excellent)
                  if (rssi >= -70) return MAP_UI.moderate; // good
                  return '#f44336'; // red (poor)
                };

                const lineColor = getColorFromRSSI(apRSSI);

                  // Calculate curve for duplicate destinations
                  const duplicateCount = destinationCounts[neighborSerial];
                  const currentIndex = destinationIndices[neighborSerial]++;
                  
                  // Determine curve direction and amount
                  let curveDirection = 0;
                  let curveAmount = 0;
                  if (duplicateCount > 1) {
                    // For 2 arrows: one curves left (-1), one curves right (+1)
                    // For more: distribute evenly
                    const offset = currentIndex - (duplicateCount - 1) / 2;
                    curveDirection = offset >= 0 ? 1 : -1;
                    curveAmount = 0.15 + Math.abs(offset) * 0.1; // Base curve + additional for each
                  }

                  const start = { lat: showNeighborsForDevice.lat, lng: showNeighborsForDevice.lng };
                  const end = { lat: neighborDevice.lat, lng: neighborDevice.lng };
                  
                  // Create path (curved if duplicate, straight if single)
                  let path;
                  let labelPosition;
                  
                  if (duplicateCount > 1) {
                    const curved = createCurvedPath(start, end, curveDirection, curveAmount);
                    path = curved.points;
                    // Place label at mid-point along the curve for better visual alignment
                    const midIdx = Math.floor(curved.points.length / 2);
                    labelPosition = curved.points[midIdx] || { lat: curved.controlLat, lng: curved.controlLng };
                  } else {
                    path = [start, end];
                    labelPosition = { lat: (start.lat + end.lat) / 2, lng: (start.lng + end.lng) / 2 };
                  }

                return (
                  <React.Fragment key={`neighbor-${index}-${neighborSerial}`}>
                    <Polyline
                        path={path}
                      options={{
                        strokeColor: lineColor,
                        strokeOpacity: 0.8,
                        strokeWeight: 3,
                        icons: [{
                          icon: {
                            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                            scale: 3,
                            strokeColor: lineColor,
                            fillColor: lineColor,
                            fillOpacity: 1
                          },
                          offset: '100%'
                        }]
                      }}
                    />
                    {/* Label overlay for neighbor info */}
                    <OverlayView
                        position={labelPosition}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <Box
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.92)',
                          border: `1px solid ${lineColor}`,
                          borderRadius: '4px',
                          padding: '3px 6px',
                          fontSize: '9px',
                          fontWeight: 500,
                          lineHeight: '1.3',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                          transform: 'translate(-50%, -50%)',
                          display: 'inline-block'
                        }}
                      >
                        <div style={{ color: '#1976d2', marginBottom: '1px' }}>Ch {channel}</div>
                        <div style={{ color: getColorFromRSSI(apRSSI), marginBottom: '1px' }}>AP {apRSSI}dBm</div>
                        <div style={{ color: getColorFromRSSI(staRSSI) }}>STA {staRSSI}dBm</div>
                      </Box>
                    </OverlayView>
                  </React.Fragment>
                );
              }).filter(Boolean)}
            </>
            );
          })()}

          {/* Devices that heard the selected target (STA RSSI filter) */}
          {heardByOverlayActive && heardByTarget && heardByLinks.map((link) => {
            const hearer = link.scannerDevice;
            if (!hearer?.lat || !hearer?.lng || !heardByTarget?.lat || !heardByTarget?.lng) {
              return null;
            }

            const start = { lat: hearer.lat, lng: hearer.lng };
            const end = { lat: heardByTarget.lat, lng: heardByTarget.lng };
            const path = [start, end];
            const labelPosition = { lat: (start.lat + end.lat) / 2, lng: (start.lng + end.lng) / 2 };
            const lineColor = rssiSignalColor(link.staRssi);

            return (
              <React.Fragment key={`heard-by-${link.id}`}>
                <Polyline
                  path={path}
                  options={{
                    strokeColor: lineColor,
                    strokeOpacity: 0.85,
                    strokeWeight: 3,
                    icons: [{
                      icon: {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 3,
                        strokeColor: lineColor,
                        fillColor: lineColor,
                        fillOpacity: 1,
                      },
                      offset: '100%',
                    }],
                  }}
                />
                <OverlayView
                  position={labelPosition}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <Box
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.92)',
                      border: `1px solid ${lineColor}`,
                      borderRadius: '4px',
                      padding: '3px 6px',
                      fontSize: '9px',
                      fontWeight: 500,
                      lineHeight: '1.3',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      transform: 'translate(-50%, -50%)',
                      display: 'inline-block',
                    }}
                  >
                    <div style={{ color: rssiSignalColor(link.staRssi), marginBottom: '1px' }}>
                      STA {link.staRssi}dBm
                    </div>
                    <div style={{ color: rssiSignalColor(link.apRssi) }}>
                      AP {Number.isFinite(link.apRssi) ? `${link.apRssi}dBm` : 'N/A'}
                    </div>
                  </Box>
                </OverlayView>
              </React.Fragment>
            );
          })}

          {/* TM Candidates visualization */}
          {showTmCandidatesForDevice && tmCandidatesData && tmCandidatesData.candidates && (() => {
            // Pre-process candidates to count duplicates per destination
            const destinationCounts = {};
            const destinationIndices = {};
            
            tmCandidatesData.candidates.forEach((candidate) => {
              const apNodeId = candidate.AP_NodeID || candidate.ap_node_id || '';
              const candidateSerial = apNodeId.toLowerCase().replace(/:/g, '');
              destinationCounts[candidateSerial] = (destinationCounts[candidateSerial] || 0) + 1;
              if (!destinationIndices[candidateSerial]) {
                destinationIndices[candidateSerial] = 0;
              }
            });

            return (
              <>
                {tmCandidatesData.candidates.map((candidate, index) => {
                  const apNodeId = candidate.AP_NodeID || candidate.ap_node_id || '';
                  const candidateSerial = apNodeId.toLowerCase().replace(/:/g, '');
                  const candidateDevice = devicesWithLocation.find(d => 
                    d.serial_number && d.serial_number.toLowerCase() === candidateSerial
                  );

                  if (!candidateDevice || !candidateDevice.lat || !candidateDevice.lng) {
                    return null;
                  }

                  const apSNR = candidate.AP_SNR ?? candidate.ap_snr ?? 0;
                  const staSNR = candidate.STA_SNR ?? candidate.sta_snr ?? 0;
                  const apNF = candidate.AP_NF ?? candidate.ap_nf ?? 0;
                  const staNF = candidate.STA_NF ?? candidate.sta_nf ?? 0;
                  const channel = candidate.Channel ?? candidate.channel ?? 0;
                  const hopDistance = candidate.HOP_Distance ?? candidate.hop_distance ?? 0;
                  // Works for both firmware formats: an unranked (-1)
                  // candidate is never the selected one.
                  const isSelected = isTmCandidateSelected(candidate, tmSelectedRankId(tmCandidatesData));
                  
                  // Calculate RSSI values
                  const apRSSI = apSNR + apNF;
                  const staRSSI = staSNR + staNF;

                  // Count children for this candidate device
                  const numChildren = devicesWithLocation.filter(d => 
                    d.parent_serial && 
                    d.parent_serial.toLowerCase() === candidateSerial
                  ).length;

                  // Color: green for selected, red for others
                  const lineColor = isSelected ? '#4caf50' : '#f44336';

                  // Calculate curve for duplicate destinations
                  const duplicateCount = destinationCounts[candidateSerial];
                  const currentIndex = destinationIndices[candidateSerial]++;
                  
                  // Determine curve direction and amount
                  let curveDirection = 0;
                  let curveAmount = 0;
                  if (duplicateCount > 1) {
                    // For 2 arrows: one curves left (-1), one curves right (+1)
                    // For more: distribute evenly
                    const offset = currentIndex - (duplicateCount - 1) / 2;
                    curveDirection = offset >= 0 ? 1 : -1;
                    curveAmount = 0.15 + Math.abs(offset) * 0.1; // Base curve + additional for each
                  }

                  const start = { lat: showTmCandidatesForDevice.lat, lng: showTmCandidatesForDevice.lng };
                  const end = { lat: candidateDevice.lat, lng: candidateDevice.lng };
                  
                  // Create path (curved if duplicate, straight if single)
                  let path;
                  let labelPosition;
                  
                  if (duplicateCount > 1) {
                    const curved = createCurvedPath(start, end, curveDirection, curveAmount);
                    path = curved.points;
                    // Place label at mid-point along the curve for better visual alignment
                    const midIdx = Math.floor(curved.points.length / 2);
                    labelPosition = curved.points[midIdx] || { lat: curved.controlLat, lng: curved.controlLng };
                  } else {
                    path = [start, end];
                    labelPosition = { lat: (start.lat + end.lat) / 2, lng: (start.lng + end.lng) / 2 };
                  }

                  return (
                    <React.Fragment key={`tm-candidate-${index}-${candidateSerial}-${currentIndex}`}>
                      <Polyline
                        path={path}
                        options={{
                          strokeColor: lineColor,
                          strokeOpacity: 0.9,
                          strokeWeight: isSelected ? 4 : 3,
                          icons: [{
                            icon: {
                              path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                              scale: isSelected ? 4 : 3,
                              strokeColor: lineColor,
                              fillColor: lineColor,
                              fillOpacity: 1
                            },
                            offset: '100%'
                          }]
                        }}
                      />
                      {/* Label overlay for TM candidate info */}
                      <OverlayView
                        position={labelPosition}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                      >
                        <Box
                          sx={{
                            backgroundColor: isSelected ? 'rgba(232, 245, 233, 0.95)' : 'rgba(255, 235, 238, 0.95)',
                            border: `2px solid ${lineColor}`,
                            borderRadius: '6px',
                            padding: '4px 8px',
                            fontSize: '9px',
                            fontWeight: 500,
                            lineHeight: '1.4',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                            transform: 'translate(-50%, -50%)',
                            display: 'inline-block'
                          }}
                        >
                          {isSelected && (
                            <div style={{ color: '#2e7d32', fontWeight: 700, marginBottom: '2px', textAlign: 'center' }}>
                              ★ SELECTED
                            </div>
                          )}
                          <div style={{ color: '#1976d2', marginBottom: '1px' }}>Ch {channel}</div>
                          <div style={{ color: apRSSI >= -70 ? '#4caf50' : apRSSI >= -80 ? MAP_UI.moderate : '#f44336', marginBottom: '1px' }}>
                            AP {apRSSI}dBm
                          </div>
                          <div style={{ color: staRSSI >= -70 ? '#4caf50' : staRSSI >= -80 ? MAP_UI.moderate : '#f44336', marginBottom: '1px' }}>
                            STA {staRSSI}dBm
                          </div>
                          <div style={{ color: '#666', marginBottom: '1px' }}>Hop: {hopDistance}</div>
                          <div style={{ color: '#666' }}>Children: {numChildren}</div>
                        </Box>
                      </OverlayView>
                    </React.Fragment>
                  );
                }).filter(Boolean)}
              </>
            );
          })()}

          {selectedDevice && !iperfSelectionMode && !capAim && (
            <InfoWindow
              position={{ lat: selectedDevice.lat, lng: selectedDevice.lng }}
              onCloseClick={() => setSelectedDevice(null)}
              options={{
                disableAutoPan: false,
                // Our header band carries the close button; with Google's
                // header and padding off (App.css) the card is flush on all
                // four sides.
                headerDisabled: true,
                maxWidth: 520,
                pixelOffset: new window.google.maps.Size(0, -10)
              }}
            >
              <Box
                className="nm-device-popup"
                ref={devicePopupRef}
                sx={{
                  width: 'min(440px, calc(100vw - 48px))',
                  boxSizing: 'border-box',
                  maxHeight: devicePopupMaxHeight,
                  pb: 1,
                  overflowX: 'hidden',
                  // Wide bar only when the content overflows, so the card
                  // stays evenly aligned when it fits.
                  overflowY: 'auto',
                  scrollbarWidth: 'auto',
                  scrollbarColor: '#757575 #eeeeee',
                  '&::-webkit-scrollbar': { width: 32 },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#eeeeee' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#757575',
                    borderRadius: 8,
                    border: '3px solid #eeeeee',
                  },
                  color: infoWindowTextColor,
                  '& .MuiTypography-root': infoWindowTextColor ? { color: infoWindowTextColor } : undefined,
                  '& .MuiTypography-root.MuiTypography-colorTextSecondary': infoWindowTextColor
                    ? { color: infoWindowTextColor }
                    : undefined,
                }}
              >
                {/* Header band (each section below draws its own divider) */}
                <Box sx={{ px: 1.5, pt: 1.5, pb: 1.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', lineHeight: 1.2 }}>
                      {getDeviceDisplayName(selectedDevice)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                      <Chip
                        label={selectedDevice.connected ? 'Online' : 'Offline'}
                        color={selectedDevice.connected ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                      />
                      {selectedDevice.capabilities?.model && (
                        <Chip
                          label={selectedDevice.capabilities.model}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                      <IconButton
                        size="small"
                        aria-label="Close"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDevice(null);
                        }}
                        sx={{ ml: 0.25, p: 0.25 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    component="a"
                    href={`/device/${selectedDevice.serial_number}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="caption"
                    sx={{ fontWeight: 700, color: '#1976d2', textDecoration: 'none', fontFamily: 'monospace', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {selectedDevice.serial_number}
                  </Typography>
                  {selectedDevice.last_seen && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, fontStyle: 'italic' }}>
                      Last seen: {new Date(selectedDevice.last_seen).toLocaleString()}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Stack spacing={1.25}>

                  {isLastKnownGoodEligible(selectedDevice) && (
                    <PopupSection
                      title="Name & location"
                      icon={<PlaceOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />}
                      action={(selectedDevice.gps_source || getEffectiveGps(selectedDevice)?.source) ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={gpsSourceLabel(selectedDevice.gps_source || getEffectiveGps(selectedDevice)?.source)}
                          sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { whiteSpace: 'nowrap' } }}
                        />
                      ) : null}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Display name"
                          placeholder="e.g. Pole 12 East"
                          value={metadataEditName}
                          onChange={(e) => setMetadataEditName(e.target.value)}
                          disabled={metadataSaving}
                          onClick={(e) => e.stopPropagation()}
                          inputProps={{ maxLength: 128 }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          disabled={
                            metadataSaving
                            || metadataEditName.trim() === (selectedDevice.display_name || selectedDevice.name || '').trim()
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveDisplayName();
                          }}
                          sx={popupInlineButtonSx}
                        >
                          Save
                        </Button>
                      </Box>
                      <PopupButtonGrid sx={{ mt: 0.75 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={metadataSaving}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectDeviceForGPS(selectedDevice);
                          }}
                          sx={popupButtonSx}
                        >
                          Set location
                        </Button>
                        {canLockCurrentDeviceGps(selectedDevice) && (
                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={metadataSaving}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLockCurrentDeviceGps(selectedDevice);
                            }}
                            sx={popupButtonSx}
                          >
                            Lock current GPS
                          </Button>
                        )}
                        {hasManualGpsOverride(selectedDevice) && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            fullWidth
                            disabled={metadataSaving}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearManualGps();
                            }}
                            sx={popupButtonSx}
                          >
                            Clear manual GPS
                          </Button>
                        )}
                      </PopupButtonGrid>
                    </PopupSection>
                  )}

                  {getDeviceType(selectedDevice.capabilities) === 'CAP' && (
                    <CapAzimuthEditor
                      key={selectedDevice.serial_number}
                      azimuth={capAzimuthBySerial.get(selectedDevice.serial_number?.toLowerCase()) || null}
                      canEdit={auth.canWriteDeviceMetadata()}
                      saving={capAzimuthSaving}
                      onSave={(deg) => handleSaveCapAzimuth(selectedDevice.serial_number, deg)}
                      onReset={() => handleResetCapAzimuth(selectedDevice.serial_number)}
                      onToggleAim={() => handleToggleCapAim(selectedDevice)}
                    />
                  )}

                  {(auth.canWriteDeviceMetadata() || deviceHasNotes(selectedDevice)) && (
                    <PopupSection
                      title="Notes"
                      icon={<StickyNote2Icon sx={{ fontSize: 15, color: deviceHasNotes(selectedDevice) ? MAP_UI.notesBadge : 'text.disabled' }} />}
                    >
                      <DeviceNotesEditor
                        device={selectedDevice}
                        stopPropagation
                        buttonSx={popupInlineButtonSx}
                        onSaved={(fields) => handleSelectedDeviceFieldsSaved(selectedDevice.serial_number, fields)}
                      />
                    </PopupSection>
                  )}

                  {(() => {
                    const popupDeviceType = getDeviceType(selectedDevice.capabilities);
                    const assignedSsids = popupDeviceType === 'Node' ? ssidsAssignedToDevice(ssidProfiles, selectedDevice) : [];
                    const ssidSummaries = assignedSsids.length > 0 ? assignedSsidClientSummaries(selectedDevice, assignedSsids) : [];
                    const provRows = provisioningRows(selectedDevice.provisioning);
                    const hasParent = (selectedDevice.isInheritedGPS && selectedDevice.parentDevice) || selectedDevice.parent_serial;
                    const backhaulRatio = selectedDevice.backhaul_tx_ratio;
                    const rows = [
                      selectedDevice.firmware && ['Firmware', selectedDevice.firmware],
                      selectedDevice.peer_address && ['IP address', selectedDevice.peer_address],
                      hasParent && ['Parent', (
                        <Box component="span" sx={{ color: selectedDevice.isInheritedGPS ? '#0288d1' : 'inherit' }}>
                          {selectedDevice.isInheritedGPS && selectedDevice.parentDevice
                            ? (selectedDevice.parentDevice.name || selectedDevice.parentDevice.serial_number)
                            : selectedDevice.parent_serial}
                          {selectedDevice.isInheritedGPS && (
                            <Chip label="GPS Inherited" size="small" color="info" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      )],
                      selectedDevice.ai_device_mac && ['AI device MAC', (
                        <Typography
                          component="a"
                          href={`/device/${selectedDevice.ai_device_mac.toLowerCase()}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ fontWeight: 500, wordBreak: 'break-all', color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {selectedDevice.ai_device_mac}
                        </Typography>
                      )],
                      backhaulRatio !== undefined && backhaulRatio > 0 && ['Backhaul health', (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <SignalCellularAltIcon sx={{ fontSize: 15, color: '#4caf50' }} />
                          {`${(backhaulRatio * 100).toFixed(1)}%`}
                        </Box>
                      )],
                      provRows.length > 0 && ['Provisioning', <PopupInfoRows rows={provRows} />],
                      ssidSummaries.length > 0 && ['WiFi clients', (
                        <Tooltip
                          title={(
                            <Box>
                              {ssidSummaries.map(({ profile, label, count }) => (
                                <Typography key={profile.id} variant="body2" display="block">
                                  <strong>{label}</strong>: {count} client{count === 1 ? '' : 's'}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          placement="top"
                          arrow
                          PopperProps={mapTooltipPopperProps}
                        >
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ cursor: 'help' }}>
                            {ssidSummaries.map(({ profile, label, count }) => (
                              <Chip
                                key={profile.id}
                                size="small"
                                variant="outlined"
                                color="primary"
                                icon={<WifiIcon sx={{ fontSize: '0.85rem !important' }} />}
                                label={`${label}: ${count}`}
                                sx={{ maxWidth: '100%', height: 22, '& .MuiChip-label': { fontSize: '0.7rem' } }}
                              />
                            ))}
                          </Stack>
                        </Tooltip>
                      )],
                    ];
                    if (!rows.some(Boolean)) return null;
                    return (
                      <PopupSection title="Device info" icon={<InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />}>
                        <PopupInfoRows rows={rows} />
                      </PopupSection>
                    );
                  })()}

                  <PopupSection title="Actions">
                    <PopupButtonGrid>
                      {(() => {
                        const selectedSerial = selectedDevice.serial_number?.toLowerCase();
                        const isDeviceTreeHighlighted = Boolean(
                          highlightedTree?.size > 0
                          && highlightedTreeRootSerial === selectedSerial,
                        );
                        return (
                          <>
                            <Button
                              variant={isDeviceTreeHighlighted ? 'contained' : 'outlined'}
                              color="secondary"
                              size="small"
                              fullWidth
                              endIcon={<ArrowDropDownIcon sx={{ fontSize: '1rem !important' }} />}
                              onClick={(e) => setTreeHighlightMenuAnchor(e.currentTarget)}
                              sx={popupButtonSx}
                            >
                              {isDeviceTreeHighlighted
                                ? (highlightedTreeMode === 'children' ? 'Children' : 'Sub-tree')
                                : 'Sub-Tree'}
                            </Button>
                            <Menu
                              anchorEl={treeHighlightMenuAnchor}
                              open={Boolean(treeHighlightMenuAnchor)}
                              onClose={() => setTreeHighlightMenuAnchor(null)}
                            >
                              <MenuItem
                                selected={isDeviceTreeHighlighted && highlightedTreeMode === 'children'}
                                onClick={() => {
                                  setTreeHighlightMenuAnchor(null);
                                  handleShowDeviceTree(selectedDevice, 'children');
                                }}
                              >
                                Direct children
                              </MenuItem>
                              <MenuItem
                                selected={isDeviceTreeHighlighted && highlightedTreeMode === 'subtree'}
                                onClick={() => {
                                  setTreeHighlightMenuAnchor(null);
                                  handleShowDeviceTree(selectedDevice, 'subtree');
                                }}
                              >
                                Sub-tree
                              </MenuItem>
                            </Menu>
                          </>
                        );
                      })()}
                      {allowBackhaulWrite && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          startIcon={<TerminalIcon sx={{ fontSize: '1rem' }} />}
                          onClick={() => window.open(`/rtty/${selectedDevice.serial_number}`, '_blank')}
                          sx={popupButtonSx}
                        >
                          Terminal
                        </Button>
                      )}
                      <Button
                        variant={showNeighborsForDevice?.serial_number === selectedDevice.serial_number ? 'contained' : 'outlined'}
                        color="success"
                        size="small"
                        fullWidth
                        startIcon={<HubIcon sx={{ fontSize: '0.9rem' }} />}
                        onClick={() => {
                          if (showNeighborsForDevice?.serial_number === selectedDevice.serial_number) {
                            handleClearNeighbors();
                          } else {
                            handleShowNeighbors(selectedDevice);
                          }
                        }}
                        disabled={loadingNeighbors}
                        sx={popupButtonSx}
                      >
                        Neighbors
                      </Button>
                      {allowBackhaulWrite && (
                        <Button
                          variant="outlined"
                          color="info"
                          size="small"
                          fullWidth
                          startIcon={<DevicesIcon sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => window.open(`/neighbors/${selectedDevice.serial_number}`, '_blank')}
                          sx={popupButtonSx}
                        >
                          Neighbors Report
                        </Button>
                      )}
                      <Button
                        variant={heardByOverlayActive && heardByTarget?.serial_number === selectedDevice.serial_number ? 'contained' : 'outlined'}
                        color="secondary"
                        size="small"
                        fullWidth
                        startIcon={<HearingIcon sx={{ fontSize: '0.9rem' }} />}
                        onClick={() => {
                          if (heardByOverlayActive && heardByTarget?.serial_number === selectedDevice.serial_number) {
                            handleClearHeardBy();
                          } else {
                            handleOpenHeardByDialog(selectedDevice);
                          }
                        }}
                        sx={popupButtonSx}
                      >
                        Heard By
                      </Button>
                      {allowBackhaulWrite && (
                        <Button
                          variant={showTmCandidatesForDevice?.serial_number === selectedDevice.serial_number ? 'contained' : 'outlined'}
                          color="secondary"
                          size="small"
                          fullWidth
                          startIcon={<AccountTreeIcon sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => {
                            if (showTmCandidatesForDevice?.serial_number === selectedDevice.serial_number) {
                              handleClearTmCandidates();
                            } else {
                              handleShowTmCandidates(selectedDevice);
                            }
                          }}
                          disabled={loadingTmCandidates}
                          sx={popupButtonSx}
                        >
                          TM Candidates
                        </Button>
                      )}
                      {allowBackhaulWrite && selectedDeviceHasConnectedChild && (
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          fullWidth
                          startIcon={<AccountTreeIcon sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => handleOpenSubtreeSegmentDialog(selectedDevice)}
                          sx={popupButtonSx}
                        >
                          Create segment
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        startIcon={<GroupAddIcon sx={{ fontSize: '0.9rem' }} />}
                        onClick={() => handleOpenGroupDialog(selectedDevice)}
                        sx={popupButtonSx}
                      >
                        {selectedDeviceMemberGroups.length > 0 ? 'Manage groups' : 'Add to group'}
                      </Button>
                      {(getDeviceType(selectedDevice.capabilities) !== 'CAP'
                        || deviceIsJnet2Cap(selectedDevice, getDeviceType)) && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          fullWidth
                          startIcon={<WifiIcon sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => handleOpenAssignSsidDialog(selectedDevice)}
                          sx={popupButtonSx}
                        >
                          {ssidsAssignedToDevice(ssidProfiles, selectedDevice).length > 0
                            ? 'Manage SSID'
                            : 'Assign SSID'}
                        </Button>
                      )}
                      {allowBackhaulWrite && (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            startIcon={<PersonAddIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={() => handleStartParentSelection(selectedDevice)}
                            sx={popupButtonSx}
                          >
                            Set Parent
                          </Button>
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            fullWidth
                            startIcon={<ListAltIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={() => handleOpenAccessListDialog(selectedDevice)}
                            sx={popupButtonSx}
                          >
                            Access List
                          </Button>
                        </>
                      )}
                    {auth.isAdmin() && deviceSupportsApFailsafe(selectedDevice) && (
                      <>
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          fullWidth
                          disabled={acceptAllSaving}
                          startIcon={acceptAllSaving
                            ? <CircularProgress size={12} color="inherit" />
                            : <AllInclusiveIcon sx={{ fontSize: '0.9rem' }} />}
                          endIcon={<ArrowDropDownIcon sx={{ fontSize: '1rem !important' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAcceptAllMenuAnchor(e.currentTarget);
                          }}
                          sx={popupButtonSx}
                        >
                          Accept all clients
                        </Button>
                        <Menu
                          anchorEl={acceptAllMenuAnchor}
                          open={Boolean(acceptAllMenuAnchor)}
                          onClose={() => setAcceptAllMenuAnchor(null)}
                        >
                          {(() => {
                            const radios = apFailsafeRadioChoices(selectedDevice);
                            const bothOn = radios.length > 1 && radios.every((radio) => radio.enabled);
                            return (
                              <>
                                {radios.map((radio) => (
                                  <MenuItem
                                    key={radio.treeId}
                                    disabled={acceptAllSaving}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAcceptAllMenuAnchor(null);
                                      setAcceptAllConfirm({
                                        device: selectedDevice,
                                        treeId: radio.treeId,
                                        enabled: radio.enabled,
                                        targetLabel: `${radio.treeId} radio`,
                                      });
                                    }}
                                  >
                                    {`${radio.treeId} radio - ${radio.enabled ? 'Turn Off' : 'Turn On'}`}
                                  </MenuItem>
                                ))}
                                {radios.length > 1 && (
                                  <MenuItem
                                    disabled={acceptAllSaving}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAcceptAllMenuAnchor(null);
                                      setAcceptAllConfirm({
                                        device: selectedDevice,
                                        treeId: '*',
                                        enabled: bothOn,
                                        targetLabel: 'both AP radios',
                                      });
                                    }}
                                  >
                                    {`Both - ${bothOn ? 'Turn Off' : 'Turn On'}`}
                                  </MenuItem>
                                )}
                              </>
                            );
                          })()}
                        </Menu>
                      </>
                    )}
                    </PopupButtonGrid>
                  </PopupSection>

                  {/* Hint */}
                  <Typography variant="caption" color="primary" sx={{ pt: 0.5, display: 'block', fontStyle: 'italic', textAlign: 'center' }}>
                    Double-click for details
                  </Typography>
                </Stack>
                </Box>
              </Box>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Map toolbar — outside GoogleMap so clicks and menus work above the map canvas */}
        <Box sx={mapToolbarSx}>
          {allowBackhaulWrite && (
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggleDrawingMode();
            }}
            sx={{ ...mapToolbarBtnPointerSx, ...mapToolbarBtnSx(drawingMode) }}
          >
            {drawingMode ? 'Cancel' : 'Select Devices'}
          </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<LayersIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              setLayersMenuAnchor(e.currentTarget);
            }}
            endIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', ml: -0.5 }} />}
            sx={{ ...mapToolbarBtnPointerSx, ...mapToolbarBtnSx(mapLayersActive) }}
          >
            Layers
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SpeedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              setIperfDialogOpen(true);
            }}
            sx={{ ...mapToolbarBtnPointerSx, ...mapToolbarBtnSx(iperfSelectionMode) }}
          >
            Run iPerf
          </Button>
          {allowBackhaulWrite && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AccountTreeIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={(e) => {
                e.stopPropagation();
                setSegmentsDialogOpen(true);
              }}
              sx={{ ...mapToolbarBtnPointerSx, ...mapToolbarBtnSx(segmentsDialogOpen) }}
            >
              Manage Segments
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={
              mapFullscreen
                ? <FullscreenExitIcon sx={{ fontSize: '1rem !important' }} />
                : <FullscreenIcon sx={{ fontSize: '1rem !important' }} />
            }
            onClick={(e) => {
              e.stopPropagation();
              toggleMapFullscreen();
            }}
            sx={{ ...mapToolbarBtnPointerSx, ...mapToolbarBtnSx(mapFullscreen) }}
          >
            {mapFullscreen ? 'Exit full screen' : 'Full screen'}
          </Button>
        </Box>
        <Menu
          anchorEl={layersMenuAnchor}
          open={Boolean(layersMenuAnchor)}
          onClose={() => setLayersMenuAnchor(null)}
          container={mapDialogContainer}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          MenuListProps={{ sx: { p: 0 }, onMouseDown: (e) => e.preventDefault() }}
          PaperProps={{
            sx: {
              minWidth: 220,
              maxWidth: 220,
              overflow: 'hidden',
              backgroundColor: 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
            },
          }}
          sx={{ zIndex: 1200 }}
        >
          <Box
            sx={{
              p: 1.5,
              borderBottom: '2px solid #e0e0e0',
              backgroundColor: 'rgba(245, 245, 245, 0.9)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
              Map overlays
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Click to toggle
            </Typography>
          </Box>
          <List sx={{ p: 0 }}>
            <MapOverlayListRow
              label="Connections"
              color="#1976d2"
              selected={showAllConnections}
              disabled={isLoadingWifiDetails && !showAllConnections}
              onToggle={handleToggleShowConnections}
            />
            <MapOverlayListRow
              label="Groups"
              color="#7c3aed"
              selected={showGroupsOnMap}
              onToggle={() => {
                setShowGroupsOnMap((prev) => !prev);
                setSelectedGroupForHighlight(null);
              }}
            />
            <MapOverlayListRow
              label="Channel"
              color="#00897b"
              selected={showChannelMode}
              disabled={isLoadingWifiDetails && !showChannelMode}
              onToggle={handleToggleChannelMode}
            />
            <MapOverlayListRow
              label="Whitelist"
              color="#43a047"
              selected={showWhitelistOverlay}
              onToggle={() => setShowWhitelistOverlay((prev) => !prev)}
            />
            <MapOverlayListRow
              label="Blacklist"
              color="#e53935"
              selected={showBlacklistOverlay}
              onToggle={() => setShowBlacklistOverlay((prev) => !prev)}
            />
            <MapOverlayListRow
              label="Segments"
              color="#0288d1"
              selected={showSegmentsOnMap}
              onToggle={() => {
                setShowSegmentsOnMap((prev) => !prev);
                setSelectedSegmentForHighlight(null);
              }}
            />
            <MapOverlayListRow
              label="Public WiFi lamps"
              color="#90caf9"
              selected={showPublicWifiLampsLayer}
              onToggle={() => setShowPublicWifiLampsLayer((prev) => !prev)}
            />
            <MapOverlayListRow
              label="Data traffic"
              color="#1565c0"
              selected={showDataTrafficLayer}
              onToggle={() => setShowDataTrafficLayer((prev) => !prev)}
            />
            <MapOverlayListRow
              label="iPerf overlay"
              color={MAP_UI.accent}
              selected={Boolean(iperfViewRun)}
              showBottomBorder={!iperfViewRun}
              onToggle={() => {
                if (iperfViewRun) {
                  setIperfViewRun(null);
                } else {
                  setLayersMenuAnchor(null);
                  openIperfViewPicker();
                }
              }}
            />
          </List>
          {iperfViewRun && (
            <List sx={{ p: 0, borderTop: '1px solid #e0e0e0' }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    setIperfViewRun(null);
                    setLayersMenuAnchor(null);
                  }}
                  sx={{ py: 1, justifyContent: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'error.main', fontWeight: 600 }}>
                    Clear iPerf overlay
                  </Typography>
                </ListItemButton>
              </ListItem>
            </List>
          )}
          <Divider />
          <List sx={{ p: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setLayersMenuAnchor(null)}
                sx={{ py: 1, justifyContent: 'center' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  Done
                </Typography>
              </ListItemButton>
            </ListItem>
          </List>
        </Menu>

        {/* Segments Sidebar Overlay */}
        {showSegmentsOnMap && (customTrees.length > 0 || unassignedSegmentSerialSet.size > 0) && (
          <Paper 
            elevation={6} 
        sx={{
          position: 'absolute',
              top: 60,
              left: 10,
              width: 220,
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2
            }}
          >
            <Box sx={{ 
              p: 1.5, 
              borderBottom: '2px solid #e0e0e0',
              backgroundColor: 'rgba(245, 245, 245, 0.9)',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              borderRadius: '8px 8px 0 0'
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                Segments
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Click to highlight
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {customTrees.map((tree, index) => {
                const isSelected = selectedSegmentForHighlight?.id === tree.id;
                const segmentColor = segmentColorById.get(tree.id) || segmentColorAt(index);
                
                return (
                  <ListItem
                    key={tree.id}
                    disablePadding
                    sx={{
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => handleSegmentClick(tree)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        '&.Mui-selected': {
                          backgroundColor: `${segmentColor}25`,
                          borderLeft: `3px solid ${segmentColor}`,
                          '&:hover': {
                            backgroundColor: `${segmentColor}35`,
                          }
                        },
                        '&:hover': {
                          backgroundColor: `${segmentColor}15`,
                        }
                      }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                          <Box 
                            sx={{ 
                              width: 10, 
                              height: 10, 
                              borderRadius: '50%',
                              backgroundColor: segmentColor,
                              flexShrink: 0,
                              boxShadow: `0 0 4px ${segmentColor}`
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: isSelected ? 700 : 600,
                              fontSize: '0.8rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {tree.name}
                          </Typography>
                        </Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: 'block',
                            pl: 2,
                            fontSize: '0.7rem'
                          }}
                        >
                          {(tree.devices?.length || 0) + (tree.caps?.length || 0)} devices
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {unassignedSegmentSerialSet.size > 0 && (
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedSegmentForHighlight?.id === NO_SEGMENT_OVERLAY_ID}
                    onClick={() => handleSegmentClick(NO_SEGMENT_OVERLAY)}
                    sx={{
                      py: 1,
                      px: 1.5,
                      '&.Mui-selected': {
                        backgroundColor: `${NO_SEGMENT_OVERLAY_COLOR}25`,
                        borderLeft: `3px solid ${NO_SEGMENT_OVERLAY_COLOR}`,
                        '&:hover': {
                          backgroundColor: `${NO_SEGMENT_OVERLAY_COLOR}35`,
                        },
                      },
                      '&:hover': {
                        backgroundColor: `${NO_SEGMENT_OVERLAY_COLOR}15`,
                      },
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: NO_SEGMENT_OVERLAY_COLOR,
                            flexShrink: 0,
                            boxShadow: `0 0 4px ${NO_SEGMENT_OVERLAY_COLOR}`,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: selectedSegmentForHighlight?.id === NO_SEGMENT_OVERLAY_ID ? 700 : 600,
                            fontSize: '0.8rem',
                          }}
                        >
                          No segment
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', pl: 2, fontSize: '0.7rem' }}
                      >
                        {unassignedSegmentSerialSet.size} device{unassignedSegmentSerialSet.size === 1 ? '' : 's'}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Paper>
        )}

        {/* Groups Panel (floating overlay, same style as Segments panel) */}
        {showGroupsOnMap && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              top: 60,
              // Sit to the right of the segments panel when both are visible, otherwise left-aligned
              left: (showSegmentsOnMap && customTrees.length > 0) ? 240 : 10,
              width: 220,
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
            }}
          >
            <Box sx={{
              p: 1.5,
              borderBottom: '2px solid #e0e0e0',
              backgroundColor: 'rgba(237, 233, 254, 0.9)',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              borderRadius: '8px 8px 0 0',
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#7c3aed' }}>
                Groups
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Click to highlight devices
              </Typography>
            </Box>

            {mapGroupsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#7c3aed' }} />
              </Box>
            ) : mapGroups.length === 0 ? (
              <Box sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  No groups yet. Use "Create Group" to draw a polygon.
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {mapGroups.map((group, index) => {
                  const isSelected = selectedGroupForHighlight?.id === group.id;
                  return (
                    <ListItem
                      key={group.id}
                      disablePadding
                      sx={{ borderBottom: index < mapGroups.length - 1 ? '1px solid #e0e0e0' : 'none' }}
                    >
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => setSelectedGroupForHighlight(isSelected ? null : group)}
                        sx={{
                          py: 1,
                          px: 1.5,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(124,58,237,0.12)',
                            borderLeft: '3px solid #7c3aed',
                            '&:hover': { backgroundColor: 'rgba(124,58,237,0.18)' },
                          },
                          '&:hover': { backgroundColor: 'rgba(124,58,237,0.06)' },
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                            <Box sx={{
                              width: 10, height: 10, borderRadius: '50%',
                              backgroundColor: '#7c3aed', flexShrink: 0,
                              boxShadow: isSelected ? '0 0 6px #7c3aed' : 'none',
                            }} />
                            <Typography variant="body2" sx={{
                              fontWeight: isSelected ? 700 : 600,
                              fontSize: '0.8rem',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {group.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2, fontSize: '0.7rem' }}>
                            {(group.devices || []).length} device(s)
                          </Typography>
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        )}

        {/* Devices Without GPS Sidebar */}
        {devicesWithoutGPS.length > 0 && (
          <Paper 
            elevation={6} 
            sx={{ 
              position: 'absolute',
              top: 240,
              right: 10,
              width: devicesWithoutGPSExpanded ? 300 : 'auto',
              maxHeight: devicesWithoutGPSExpanded ? 'calc(100vh - 300px)' : 'auto',
              overflowY: devicesWithoutGPSExpanded ? 'auto' : 'visible',
              zIndex: 1000,
              backgroundColor: 'rgba(255, 255, 255, 0.97)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2
            }}
          >
            <Box 
              onClick={() => setDevicesWithoutGPSExpanded(!devicesWithoutGPSExpanded)}
              sx={{ 
                p: 1.5, 
                borderBottom: devicesWithoutGPSExpanded ? '2px solid #e0e0e0' : 'none',
                backgroundColor: MAP_UI.accentBgLight,
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderRadius: devicesWithoutGPSExpanded ? '8px 8px 0 0' : '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '&:hover': {
                  backgroundColor: MAP_UI.accentBgLight,
                }
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem', color: MAP_UI.accentDark }}>
                  Devices Without GPS ({devicesWithoutGPS.length})
                </Typography>
                {devicesWithoutGPSExpanded && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Click device, then click map
                  </Typography>
                )}
              </Box>
              {devicesWithoutGPSExpanded ? <ExpandLessIcon sx={{ color: MAP_UI.accentDark }} /> : <ExpandMoreIcon sx={{ color: MAP_UI.accentDark }} />}
            </Box>
            {devicesWithoutGPSExpanded && (
              <List sx={{ p: 0 }}>
                {devicesWithoutGPS.map((device, index) => {
                const deviceType = getDeviceType(device.capabilities);
                const isConnected = device.connected;
                const isSelected = selectedDeviceForGPS?.serial_number === device.serial_number;
                
                const serial = device.serial_number || '';
                return (
                  <ListItem
                    key={device.serial_number}
                    disablePadding
                    secondaryAction={serial ? (
                      <Box component="span" sx={{ display: 'inline-flex', zIndex: 2 }}>
                        <Tooltip title="Open device details">
                          <Link
                            href={deviceDetailsHref(serial)}
                            underline="hover"
                            variant="caption"
                            onClick={(event) => {
                              event.stopPropagation();
                              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                                return;
                              }
                              event.preventDefault();
                              navigate(deviceDetailsHref(serial));
                            }}
                            sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                          >
                            Details
                          </Link>
                        </Tooltip>
                      </Box>
                    ) : null}
                    sx={{
                      borderBottom: index < devicesWithoutGPS.length - 1 ? '1px solid #e0e0e0' : 'none',
                      '& .MuiListItemSecondaryAction-root': {
                        right: 8,
                      },
                    }}
                  >
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => handleSelectDeviceForGPS(device)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        pr: serial ? 12 : 1.5,
                        '&.Mui-selected': {
                          backgroundColor: MAP_UI.accentTint,
                          borderLeft: `3px solid ${MAP_UI.accentBorder}`,
                          '&:hover': {
                            backgroundColor: MAP_UI.accentTintHover,
                          }
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(96, 125, 139, 0.06)',
                        }
                      }}
                    >
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
                          <Box 
                            sx={{ 
                              width: 10, 
                              height: 10, 
                              borderRadius: '50%',
                              backgroundColor: isConnected ? MAP_UI.deviceOnline : MAP_UI.deviceOffline,
                              flexShrink: 0,
                              boxShadow: `0 0 4px ${isConnected ? MAP_UI.deviceOnlineBorder : MAP_UI.deviceOfflineBorder}`
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.8rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {getDeviceDisplayName(device)}
                          </Typography>
                        </Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            display: 'block',
                            pl: 2,
                            fontSize: '0.7rem'
                          }}
                        >
                          {deviceType} • {device.serial_number}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
              </List>
            )}
          </Paper>
        )}
    </Box>

    {/* Device Selection Dialog */}
    <Dialog
      open={selectionDialogOpen}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      container={mapDialogContainer}
      PaperProps={mapSelectionDialogPaperProps}
    >
      <MapSelectionDialogTitle
        title="Map selection"
        subtitle={`${selectedDevices.length} device(s) selected — choose an action below`}
      />

      <MapSelectionDialogTabs
        value={dialogTab}
        onChange={(_, newValue) => setDialogTab(newValue)}
        tabs={SELECTION_DIALOG_TABS}
      />

      <MapSelectionDialogBody>
        <MapSelectionSummaryStrip
          selectedDevices={selectedDevices}
          getDeviceType={getDeviceType}
        />

        {/* Tab Content */}
        {dialogTab === 0 && (
          <Box>
            {/* Create New Custom Tree */}
            <Card variant="outlined" sx={{ mb: 3, boxShadow: 'none', border: 'none' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon color="primary" />
                  Create New Custom Tree
          </Typography>
          <TextField
            fullWidth
                  size="medium"
                  label="Tree Name"
            value={customTreeName}
            onChange={(e) => setCustomTreeName(e.target.value)}
                  placeholder="e.g., Building A Network"
                  sx={{ mt: 2, mb: 2 }}
                />
                
                {/* Node Devices Selection */}
                <Autocomplete
                  multiple
                  fullWidth
                  options={allNodeDevices}
                  value={selectedNodes}
                  onChange={(event, newValue) => setSelectedNodes(newValue)}
                  getOptionLabel={(option) => option.name || option.serial_number}
                  isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Node Devices *"
                      placeholder="Select Node devices"
                      helperText="At least one Node device is required"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || option.serial_number}
                        {...getTagProps({ index })}
                        size="small"
                        color="primary"
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {option.name || option.serial_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.serial_number}
                          {option.connected ? ' • Online' : ' • Offline'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
            sx={{ mb: 2 }}
          />

                {/* CAP Devices Selection */}
                <Autocomplete
                  multiple
                  fullWidth
                  options={allCapDevices}
                  value={selectedCaps}
                  onChange={(event, newValue) => setSelectedCaps(newValue)}
                  getOptionLabel={(option) => option.name || option.serial_number}
                  isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="CAP Devices *"
                      placeholder="Select CAP devices"
                      helperText="At least one CAP device is required"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || option.serial_number}
                        {...getTagProps({ index })}
                        size="small"
                        color="secondary"
                      />
                    ))
                  }
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">
                          {option.name || option.serial_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.serial_number}
                          {option.connected ? ' • Online' : ' • Offline'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  sx={{ mb: 2 }}
                />
                
                <Tooltip 
                  title={
                    selectedNodes.length === 0 && selectedCaps.length === 0 
                      ? "At least one Node and one CAP device must be selected" 
                      : selectedNodes.length === 0 
                        ? "At least one Node device must be selected"
                        : selectedCaps.length === 0 
                          ? "At least one CAP device must be selected"
                          : ""
                  } 
                  arrow
                  placement="top"
                >
                  <span>
          <Button
            variant="contained"
                      size="large"
                      fullWidth
            onClick={handleCreateCustomTree}
                      disabled={!customTreeName.trim() || selectedNodes.length === 0 || selectedCaps.length === 0 || creatingCustomTree}
                      sx={{ py: 1.5 }}
                      startIcon={creatingCustomTree ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {creatingCustomTree ? 'Creating Segment…' : 'Create Custom Tree'}
          </Button>
                  </span>
                </Tooltip>
              </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            <Card variant="outlined" sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountTreeIcon color="secondary" />
                  Add to Existing Segment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add the map selection to a segment that already exists. Devices already in the
                  segment are excluded automatically.
                </Typography>

                {customTreesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : customTrees.length === 0 ? (
                  <Alert severity="info">
                    No existing segments yet. Create one above first.
                  </Alert>
                ) : (
                  <>
                    <Autocomplete
                      fullWidth
                      options={customTrees}
                      value={addToExistingTree}
                      onChange={(event, newValue) => setAddToExistingTree(newValue)}
                      getOptionLabel={(option) => option.name || option.id}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Existing segment"
                          placeholder="Select a segment"
                          helperText={
                            addToExistingTree
                              ? `Segment ID ${addToExistingTree.segment_id} • ${(addToExistingTree.devices?.length || 0) + (addToExistingTree.caps?.length || 0)} current device(s)`
                              : 'Choose which segment should receive the selected devices'
                          }
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Segment ID {option.segment_id} • {(option.devices?.length || 0) + (option.caps?.length || 0)} devices
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      sx={{ mb: 2 }}
                    />

                    {addToExistingTree && (
                      <>
                        <Autocomplete
                          multiple
                          fullWidth
                          options={allNodeDevices}
                          value={addToExistingNodes}
                          onChange={(event, newValue) => setAddToExistingNodes(newValue)}
                          getOptionLabel={(option) => option.name || option.serial_number}
                          isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Node devices to add"
                              placeholder="Select Node devices"
                              helperText="Pre-filled from map selection; edit before adding"
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option.name || option.serial_number}
                                {...getTagProps({ index })}
                                size="small"
                                color="primary"
                              />
                            ))
                          }
                          sx={{ mb: 2 }}
                        />

                        <Autocomplete
                          multiple
                          fullWidth
                          options={allCapDevices}
                          value={addToExistingCaps}
                          onChange={(event, newValue) => setAddToExistingCaps(newValue)}
                          getOptionLabel={(option) => option.name || option.serial_number}
                          isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="CAP devices to add"
                              placeholder="Select CAP devices"
                              helperText="Optional — only needed when adding new anchor CAPs"
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                label={option.name || option.serial_number}
                                {...getTagProps({ index })}
                                size="small"
                                color="secondary"
                              />
                            ))
                          }
                          sx={{ mb: 2 }}
                        />

                        {addToExistingNodes.length === 0 && addToExistingCaps.length === 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            All selected devices are already in this segment.
                          </Alert>
                        )}

                        <Button
                          variant="contained"
                          color="secondary"
                          size="large"
                          fullWidth
                          onClick={handleAddSelectionToExistingTree}
                          disabled={
                            addToCustomTreeLoading
                            || !addToExistingTree
                            || (addToExistingNodes.length === 0 && addToExistingCaps.length === 0)
                          }
                          sx={{ py: 1.5 }}
                          startIcon={addToCustomTreeLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                        >
                          {addToCustomTreeLoading ? 'Adding to Segment…' : 'Add to Segment'}
                        </Button>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
        </Box>
        )}

        {dialogTab === 1 && (
          <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AutoFixHighIcon color="primary" />
                  Run RRM Optimize
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Open the RRM Optimize tab scoped to the {selectedDevices.length} selected device
                  {selectedDevices.length === 1 ? '' : 's'}. Plan actions will apply only to these devices.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleRunOptimizeOnSelection}
                  disabled={selectedDevices.length === 0}
                >
                  Run RRM Optimize on selection
                </Button>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HubIcon color="primary" />
                  Create Segments for Selection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Open RRM Optimize → Segment scoped to the {selectedDevices.length} selected device
                  {selectedDevices.length === 1 ? '' : 's'} and run the partitioning only on this cohort.
                  In-cohort CAPs become segment roots; in-cohort Nodes are the candidates for assignment.
                </Typography>
                {selectedDevices.length > 0 && !selectionHasCapDevice && (
                  <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    Selection has no CAP. Add at least one CAP — partitioning needs a CAP as a segment root.
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={<HubIcon />}
                  onClick={handleCreateSegmentsOnSelection}
                  disabled={selectedDevices.length === 0 || !selectionHasCapDevice}
                >
                  Create segments for selection
                </Button>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SpeedIcon color="primary" />
                  Run Tree-Wide Iperf Test
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Open the RRM Iperf Test tab with CAP trees from this selection pre-selected.
                  CAP devices are used directly; if none are selected, cap_serial values from
                  selected nodes are used. Iperf runs hop tests and path analysis per CAP tree.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<SpeedIcon />}
                  onClick={handleRunIperfOnSelection}
                  disabled={selectedDevices.length === 0}
                >
                  Open Iperf Test for selection
                </Button>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RadarIcon color="primary" />
                  Neighbors Scan
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Run a neighbors scan on the selected devices ({selectionNeighborsScanTargets.length} connected
                  jnet2 of {selectedDevices.length} selected). Offline or non-jnet2 devices are skipped.
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Scan interval (seconds)"
                      value={selectionNeighborsScanInterval}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '') {
                          setSelectionNeighborsScanInterval(value);
                          return;
                        }
                        const num = parseInt(value, 10);
                        if (!Number.isNaN(num)) {
                          setSelectionNeighborsScanInterval(num);
                        }
                      }}
                      onBlur={() => {
                        if (!selectionNeighborsScanInterval || selectionNeighborsScanInterval < 1) {
                          setSelectionNeighborsScanInterval(15);
                        }
                      }}
                      inputProps={{ min: 1, max: 60 }}
                      helperText="Time on each channel"
                      disabled={selectionNeighborsScanLoading}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Channels to scan</InputLabel>
                      <Select
                        value={selectionNeighborsScanAllFreq}
                        onChange={(e) => setSelectionNeighborsScanAllFreq(e.target.value)}
                        label="Channels to scan"
                        disabled={selectionNeighborsScanLoading}
                      >
                        <MenuItem value={true}>All channels (all bands)</MenuItem>
                        <MenuItem value={false}>Known channels only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <NeighborScanBatchField
                      value={selectionNeighborsScanBatch}
                      onChange={setSelectionNeighborsScanBatch}
                      size="medium"
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={
                    selectionNeighborsScanLoading
                      ? <CircularProgress size={16} color="inherit" />
                      : <RadarIcon />
                  }
                  onClick={handleOpenNeighborsScanOnSelection}
                  disabled={selectedDevices.length === 0 || selectionNeighborsScanLoading}
                >
                  {selectionNeighborsScanLoading ? 'Starting scan…' : 'Run neighbors scan on selection'}
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 2 && (
        <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backhaul Connectivity Configuration
          </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Update backhaul network configuration for selected devices.
                  TX Power Down, Band, HT Mode, and Max Children are backhaul-only — devices whose 5 GHz radio is assigned to Public WiFi will be skipped for those fields.
                  TX Power Up applies to all selected devices.
                </Typography>
                
                <Stack spacing={3}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="TX Power Down"
              type="number"
              value={connectivityParams.txPowerDown}
              onChange={(e) => setConnectivityParams(prev => ({ ...prev, txPowerDown: e.target.value }))}
              placeholder="e.g., 7"
                      fullWidth
            />
            <TextField
              label="TX Power Up"
              type="number"
              value={connectivityParams.txPowerUp}
              onChange={(e) => setConnectivityParams(prev => ({ ...prev, txPowerUp: e.target.value }))}
              placeholder="e.g., 5"
                      fullWidth
            />
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <FormControl fullWidth>
              <InputLabel>Band</InputLabel>
              <Select
                value={connectivityParams.band}
                label="Band"
                onChange={(e) => {
                  const band = e.target.value;
                  connectivityChannelsTouchedRef.current = false;
                  setConnectivityParams((prev) => ({
                    ...prev,
                    band,
                    channels: prefillConnectivityChannels(band, prev.htmode),
                  }));
                }}
              >
                {BAND_OPTIONS.map((option) => (
                  <MenuItem key={option || 'none'} value={option}>
                            {option === '' ? 'Not Set' : option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
                    
                    <FormControl fullWidth>
              <InputLabel>HT Mode</InputLabel>
              <Select
                value={connectivityParams.htmode}
                label="HT Mode"
                onChange={(e) => {
                  const htmode = e.target.value;
                  setConnectivityParams((prev) => {
                    const next = { ...prev, htmode };
                    if (!connectivityChannelsTouchedRef.current && ['5GHz', '6GHz'].includes(prev.band)) {
                      next.channels = prefillConnectivityChannels(prev.band, htmode);
                    }
                    return next;
                  });
                }}
              >
                {HTMODE_OPTIONS.map((option) => (
                  <MenuItem key={option || 'none'} value={option}>
                            {option === '' ? 'Not Set' : option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>

          <TextField
                    label="Max Children"
                    type="number"
                    value={connectivityParams.maxChildren}
                    onChange={(e) => setConnectivityParams(prev => ({ ...prev, maxChildren: e.target.value }))}
                    placeholder="e.g., 16"
            fullWidth
                  />

                  {['5GHz', '6GHz'].includes(connectivityParams.band) && (
                    <Box>
                      <TextField
                        label={`${connectivityParams.band} channels`}
                        value={connectivityParams.channels}
                        onChange={(e) => {
                          connectivityChannelsTouchedRef.current = true;
                          setConnectivityParams((prev) => ({ ...prev, channels: e.target.value }));
                        }}
                        placeholder={connectivityParams.band === '5GHz' ? 'e.g. 36, 40, 44' : 'e.g. 5, 13, 21'}
                        fullWidth
                        multiline
                        minRows={2}
                        helperText="Comma-separated channel list for treemgmt ChannelGroups. Saving replaces channels for this band and removes the other band from the config."
                      />
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          connectivityChannelsTouchedRef.current = false;
                          setConnectivityParams((prev) => ({
                            ...prev,
                            channels: prefillConnectivityChannels(prev.band, prev.htmode),
                          }));
                        }}
                      >
                        Reset to all channels for band
                      </Button>
                    </Box>
                  )}

            <Button
              variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
              onClick={handleConnectivityChange}
                    disabled={connectivityLoading || selectedDevices.length === 0 || !hasConnectivityChanges}
                    sx={{ py: 1.5, mt: 2 }}
            >
                    {connectivityLoading ? 'Applying Changes…' : 'Apply Configuration'}
            </Button>
          </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Syslog Server
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure the remote syslog server for all selected devices.
                  Only one syslog server is supported per device.
                </Typography>

                <Stack spacing={2}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      label="IP Address"
                      value={syslogConfig.ip}
                      onChange={(e) => setSyslogConfig(prev => ({ ...prev, ip: e.target.value }))}
                      placeholder="e.g. 192.168.1.100"
                      fullWidth
                    />
                    <TextField
                      label="Port"
                      value={syslogConfig.port}
                      onChange={(e) => setSyslogConfig(prev => ({ ...prev, port: e.target.value }))}
                      placeholder="e.g. 514"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Protocol</InputLabel>
                      <Select
                        value={syslogConfig.proto || 'udp'}
                        label="Protocol"
                        onChange={(e) => setSyslogConfig(prev => ({ ...prev, proto: e.target.value }))}
                      >
                        <MenuItem value="udp">UDP</MenuItem>
                        <MenuItem value="tcp">TCP</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Remote Logging</InputLabel>
                      <Select
                        value={syslogConfig.log_remote || ''}
                        label="Remote Logging"
                        onChange={(e) => setSyslogConfig(prev => ({ ...prev, log_remote: e.target.value }))}
                      >
                        <MenuItem value="">Off</MenuItem>
                        <MenuItem value="1">On</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={handleSaveSyslogConfig}
                    disabled={syslogSaving || selectedDevices.length === 0 || !syslogConfig.ip.trim()}
                    sx={{ py: 1.5, mt: 1 }}
                    startIcon={syslogSaving ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {syslogSaving
                      ? 'Applying Syslog Config…'
                      : `Apply Syslog Config to ${selectedDevices.length} Device(s)`}
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Client Reports
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enable periodic association snapshots for backhaul and public WiFi clients
                  on all selected devices.
                </Typography>

                <ReportsConfigFields
                  cfg={reportsConfig}
                  onChange={(field, value) => setReportsConfig((prev) => ({ ...prev, [field]: value }))}
                  disabled={reportsSaving}
                />

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleSaveReportsConfig}
                  disabled={reportsSaving || selectedDevices.length === 0}
                  sx={{ py: 1.5, mt: 2 }}
                  startIcon={reportsSaving ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {reportsSaving
                    ? 'Applying Reports Config…'
                    : `Apply Reports Config to ${selectedDevices.length} Device(s)`}
                </Button>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Segment IDs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Remove <code>segment_id_ap</code> and <code>segment_id_dev</code> from every
                  <code> treemgmt</code> entry on each selected device, then push the updated
                  configuration. Devices with no <code>treemgmt</code> section or no segment IDs
                  set are skipped automatically.
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="large"
                  fullWidth
                  startIcon={deleteSegmentsLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                  disabled={deleteSegmentsLoading || selectedDevices.length === 0}
                  onClick={() => setDeleteSegmentsConfirmOpen(true)}
                  sx={{ py: 1.25 }}
                >
                  {deleteSegmentsLoading
                    ? 'Deleting Segments…'
                    : `Delete Segments (${selectedDevices.length} device${selectedDevices.length === 1 ? '' : 's'})`}
                </Button>
              </CardContent>
            </Card>
        </Box>
        )}

        {dialogTab === 3 && (
        <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Send Command to Selected Devices
          </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Execute a command on all selected devices in the polygon
                </Typography>
                
                <Stack spacing={3}>
          <TextField
                    label="Command"
                    value={commandText}
                    onChange={(e) => setCommandText(e.target.value)}
                    placeholder="e.g., reboot, ping, upgrade"
            fullWidth
                    multiline
                    rows={3}
                    helperText="Enter the command to send to all selected devices"
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={handleSendCommand}
                    disabled={commandLoading || !commandText.trim() || selectedDevices.length === 0}
                    sx={{ py: 1.5, mt: 2 }}
                    startIcon={commandLoading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {commandLoading ? 'Sending Command…' : `Send Command to ${selectedDevices.length} Device(s)`}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 4 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ color: '#7c3aed' }} />
                  Create Group
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Save the {selectedDevices.length} selected device(s) as a named group for SSID and VLAN assignment.
                </Typography>

                <Stack spacing={3}>
                  <TextField
                    label="Group Name *"
                    value={groupCreationName}
                    onChange={e => setGroupCreationName(e.target.value)}
                    fullWidth
                    autoFocus
                    placeholder="e.g., Floor 2, Building A, Zone North"
                  />

                  {/* Preview of devices to be added */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Devices in group ({selectedDevices.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selectedDevices.map(d => (
                        <Chip
                          key={d.serial_number}
                          label={d.name || d.serial_number}
                          size="small"
                          color={d.connected ? 'success' : 'default'}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleCreateGroupFromMap}
                    disabled={!groupCreationName.trim() || creatingGroup || selectedDevices.length === 0}
                    sx={{
                      py: 1.5,
                      bgcolor: '#7c3aed',
                      '&:hover': { bgcolor: '#6d28d9' },
                      '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                    }}
                    startIcon={creatingGroup ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {creatingGroup ? 'Creating Group…' : `Create Group with ${selectedDevices.length} Device(s)`}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 5 && (
          <DeviceLocationSelectionPanel
            devices={selectedDevices}
            saving={metadataSaving}
            onNotify={({ type, message }) => {
              enqueueSnackbar(message, { variant: type === 'success' ? 'success' : type === 'warning' ? 'warning' : 'error' });
            }}
            onRefreshDevice={refreshDevicesAfterMetadataChange}
            onSetOnMap={(device) => {
              setSelectionDialogOpen(false);
              handleSelectDeviceForGPS(device);
            }}
          />
        )}

        {dialogTab === 6 && (
          <DeviceNicknameSelectionPanel
            devices={selectedDevices}
            saving={metadataSaving}
            onNotify={({ type, message }) => {
              enqueueSnackbar(message, { variant: type === 'success' ? 'success' : type === 'warning' ? 'warning' : 'error' });
            }}
            onRefreshDevice={refreshDevicesAfterMetadataChange}
          />
        )}

        {dialogTab === 7 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ListAltIcon sx={{ color: '#2e7d32' }} />
                  Device Whitelists
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  View and manage whitelists for the {selectedDevices.length} selected device(s).
                </Typography>

                {selectionWhitelistLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {selectedDevices.map(device => {
                      const data = selectionWhitelistData[device.serial_number] || {};
                      const whitelist = data.whitelist || [];
                      const isClearing = clearingWhitelistSerials.has(device.serial_number);

                      return (
                        <Card key={device.serial_number} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: whitelist.length > 0 ? 1 : 0 }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                                  {device.name || device.serial_number}
                                </Typography>
                                {device.name && (
                                  <Typography variant="caption" color="text.secondary">
                                    {device.serial_number}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={isClearing || whitelist.length === 0}
                                onClick={() => handleClearWhitelist(device.serial_number)}
                                startIcon={isClearing ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                {isClearing ? 'Clearing…' : 'Clear'}
                              </Button>
                            </Box>
                            {whitelist.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {whitelist.map(serial => (
                                  <Chip key={serial} label={serial} size="small" variant="outlined" color="success" />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No whitelist configured
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}

                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      onClick={handleClearAllWhitelists}
                      disabled={clearingWhitelistSerials.size > 0 || !Object.values(selectionWhitelistData).some(d => d.whitelist?.length > 0)}
                      startIcon={clearingWhitelistSerials.size > 0 ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                      sx={{ py: 1.5, mt: 1 }}
                    >
                      {clearingWhitelistSerials.size > 0
                        ? 'Clearing All…'
                        : `Clear All Whitelists (${selectedDevices.length} Devices)`}
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {dialogTab === 8 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ListAltIcon sx={{ color: '#c62828' }} />
                  Device Blacklists
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  View and manage blacklists for the {selectedDevices.length} selected device(s).
                </Typography>

                {selectionWhitelistLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {selectedDevices.map(device => {
                      const data = selectionWhitelistData[device.serial_number] || {};
                      const blacklist = data.blacklist || [];
                      const isClearing = clearingBlacklistSerials.has(device.serial_number);

                      return (
                        <Card key={device.serial_number} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: blacklist.length > 0 ? 1 : 0 }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                                  {device.name || device.serial_number}
                                </Typography>
                                {device.name && (
                                  <Typography variant="caption" color="text.secondary">
                                    {device.serial_number}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={isClearing || blacklist.length === 0}
                                onClick={() => handleClearBlacklist(device.serial_number)}
                                startIcon={isClearing ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                              >
                                {isClearing ? 'Clearing…' : 'Clear'}
                              </Button>
                            </Box>
                            {blacklist.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {blacklist.map(serial => (
                                  <Chip key={serial} label={serial} size="small" variant="outlined" color="error" />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No blacklist configured
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}

                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      onClick={handleClearAllBlacklists}
                      disabled={clearingBlacklistSerials.size > 0 || !Object.values(selectionWhitelistData).some(d => d.blacklist?.length > 0)}
                      startIcon={clearingBlacklistSerials.size > 0 ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                      sx={{ py: 1.5, mt: 1 }}
                    >
                      {clearingBlacklistSerials.size > 0
                        ? 'Clearing All…'
                        : `Clear All Blacklists (${selectedDevices.length} Devices)`}
                    </Button>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
        {dialogTab === 9 && (
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WifiIcon sx={{ color: '#1976d2' }} />
                  Band switch (STA)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Filter selected devices by reported STA band (uplink / private station interface), then send{' '}
                  <code>set_sta_band</code> to move radios between 5 GHz and 6 GHz. Devices may drop and rejoin
                  their parent.
                </Typography>

                {isLoadingWifiDetails && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Loading WiFi interface details for accurate band detection…
                  </Alert>
                )}

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={staBandFilter5G}
                          onChange={(e) => setStaBandFilter5G(e.target.checked)}
                          size="small"
                        />
                      }
                      label="5 GHz STA"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={staBandFilter6G}
                          onChange={(e) => setStaBandFilter6G(e.target.checked)}
                          size="small"
                        />
                      }
                      label="6 GHz STA"
                    />
                  </Box>

                  {!staBandFilter5G && !staBandFilter6G && (
                    <Alert severity="warning">Select at least one band to list matching devices.</Alert>
                  )}

                  {staBandFilteredDevices.length === 0 && (staBandFilter5G || staBandFilter6G) && (
                    <Alert severity="info">
                      No selected devices with a detected 5 GHz or 6 GHz STA in this view. Ensure devices report{' '}
                      <code>ul_band</code> or refresh with WiFi interface fields.
                    </Alert>
                  )}

                  {staBandFilteredDevices.length > 0 && (
                    <>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setStaBandSwitchSelected(
                              new Set(
                                staBandFilteredDevices
                                  .map((d) => d.serial_number?.toLowerCase())
                                  .filter(Boolean),
                              ),
                            )
                          }
                        >
                          Select all ({staBandFilteredDevices.length})
                        </Button>
                        <Button size="small" variant="text" onClick={() => setStaBandSwitchSelected(new Set())}>
                          Clear selection
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          {staBandSwitchSelected.size} selected
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          maxHeight: 260,
                          overflow: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <List dense disablePadding>
                          {staBandFilteredDevices.map((device) => {
                            const serial = device.serial_number;
                            const key = serial?.toLowerCase();
                            const band = getDeviceStaBand(device);
                            const checked = key && staBandSwitchSelected.has(key);
                            return (
                              <ListItem key={serial} disablePadding>
                                <ListItemButton
                                  dense
                                  onClick={() => {
                                    if (!key) return;
                                    setStaBandSwitchSelected((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(key)) next.delete(key);
                                      else next.add(key);
                                      return next;
                                    });
                                  }}
                                >
                                  <Checkbox edge="start" checked={!!checked} tabIndex={-1} disableRipple />
                                  <ListItemText
                                    primary={device.name || serial}
                                    secondary={
                                      <>
                                        {serial}
                                        {band ? (
                                          <>
                                            {' · STA '}
                                            <strong>{band}</strong>
                                          </>
                                        ) : (
                                          ' · STA band unknown'
                                        )}
                                        {' · '}
                                        {device.connected ? 'Online' : 'Offline'}
                                      </>
                                    }
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Switch selected devices to
                        </Typography>
                        <RadioGroup
                          row
                          value={staBandTarget}
                          onChange={(e) => setStaBandTarget(e.target.value)}
                        >
                          <FormControlLabel value="5GHz" control={<Radio size="small" />} label="5 GHz" />
                          <FormControlLabel value="6GHz" control={<Radio size="small" />} label="6 GHz" />
                        </RadioGroup>
                      </Box>

                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={
                          staBandSwitchLoading || staBandSwitchSelected.size === 0 || !staBandTarget
                        }
                        onClick={handleApplyStaBandSwitch}
                        sx={{ py: 1.5 }}
                        startIcon={
                          staBandSwitchLoading ? <CircularProgress size={20} color="inherit" /> : null
                        }
                      >
                        {staBandSwitchLoading
                          ? 'Sending set_sta_band…'
                          : `Apply band switch (${staBandSwitchSelected.size} device${
                              staBandSwitchSelected.size === 1 ? '' : 's'
                            })`}
                      </Button>
                    </>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        )}
        {dialogTab === 10 && (
          <BackgroundStaScanBulkPanel
            nodeCount={selectedNodeDevices.length}
            capCount={selectedCapCount}
            selectedDevices={selectedDevices}
            form={bgScanForm}
            onChange={setBgScanForm}
            onApply={handleBgScanBulkApply}
            loading={bgScanLoading}
          />
        )}
        {dialogTab === 11 && (
          <LastKnownGoodSelectionPanel
            devices={selectedDevices}
            onNotify={({ type, message }) => {
              enqueueSnackbar(message, { variant: type === 'success' ? 'success' : type === 'warning' ? 'warning' : 'error' });
            }}
          />
        )}
      </MapSelectionDialogBody>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Button onClick={clearSelection} variant="outlined" color="inherit" fullWidth sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Segments Confirmation Dialog */}
    <Dialog
      open={deleteSegmentsConfirmOpen}
      onClose={() => !deleteSegmentsLoading && setDeleteSegmentsConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Delete Segments</DialogTitle>
      <DialogContent>
        <Typography variant="body2">
          This will remove <code>segment_id_ap</code> and <code>segment_id_dev</code> from the
          {' '}<code>treemgmt</code> entries on{' '}
          <strong>{selectedDevices.length}</strong> selected device
          {selectedDevices.length === 1 ? '' : 's'} and push the updated configuration. This
          action cannot be undone from the UI.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setDeleteSegmentsConfirmOpen(false)}
          disabled={deleteSegmentsLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDeleteSegments}
          color="error"
          variant="contained"
          disabled={deleteSegmentsLoading || selectedDevices.length === 0}
          startIcon={deleteSegmentsLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
        >
          {deleteSegmentsLoading ? 'Deleting…' : 'Delete Segments'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Segments Management Dialog */}
    <Dialog
      open={segmentsDialogOpen}
      onClose={() => {
        setSegmentsDialogOpen(false);
        setSelectedTree(null);
      }}
      maxWidth="md"
      fullWidth
      container={mapDialogContainer}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        Manage Segments
      </DialogTitle>
      
      <DialogContent>
        {customTreesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : customTrees.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No segments found. Draw a selection area on the map to create one.
          </Alert>
        ) : (
          <List sx={{ 
            border: '1px solid #e0e0e0', 
            borderRadius: 2, 
            overflow: 'hidden'
          }}>
            {customTrees.map((tree) => (
              <Box key={tree.id}>
                <ListItem 
                  id={`manage-segment-${tree.id}`}
                  disablePadding
                  sx={{
                    borderBottom: '1px solid #f0f0f0',
                    backgroundColor: selectedTree?.id === tree.id ? '#e3f2fd' : 'transparent'
                  }}
                >
                  <ListItemButton onClick={() => {
                    if (selectedTree?.id === tree.id) {
                      setSelectedTree(null);
                      setSegmentEditNodes([]);
                      setSegmentEditCaps([]);
                    } else {
                      setSelectedTree(tree);
                      // Initialize edit state with current devices
                      const treeNodes = (tree.devices || []).map(serial => 
                        allNodeDevices.find(d => d.serial_number === serial)
                      ).filter(Boolean);
                      const treeCaps = (tree.caps || []).map(serial => 
                        allCapDevices.find(d => d.serial_number === serial)
                      ).filter(Boolean);
                      setSegmentEditNodes(treeNodes);
                      setSegmentEditCaps(treeCaps);
                    }
                  }}>
                    <AccountTreeIcon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={(
                        <Link
                          component="span"
                          underline="hover"
                          color="primary"
                          title="Show in segments layer"
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => handleShowSegmentInLayer(event, tree)}
                          sx={{ fontWeight: 'inherit', cursor: 'pointer' }}
                        >
                          {tree.name}
                        </Link>
                      )}
                      secondary={`Segment ID: ${tree.segment_id} • ${(tree.devices?.length || 0) + (tree.caps?.length || 0)} devices`}
                      primaryTypographyProps={{ fontWeight: selectedTree?.id === tree.id ? 600 : 400 }}
                    />
                    {selectedTree?.id === tree.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={selectedTree?.id === tree.id} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                      Edit Segment Devices:
          </Typography>
                    
                    {/* Node Devices Selection */}
                    <Autocomplete
                      multiple
                      fullWidth
                      options={allNodeDevices}
                      value={segmentEditNodes}
                      onChange={(event, newValue) => setSegmentEditNodes(newValue)}
                      getOptionLabel={(option) => option.name || option.serial_number}
                      isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
                      renderInput={(params) => (
          <TextField
                          {...params}
                          label="Node Devices"
                          placeholder="Select Node devices"
            size="small"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={segmentDeviceChipLabel(option)}
                            {...getTagProps({ index })}
                            size="small"
                            color="primary"
                          />
                        ))
                      }
                      renderOption={(props, option) => renderSegmentDeviceOption(props, option)}
                      sx={{ mb: 2 }}
                    />

                    {/* CAP Devices Selection */}
                    <Autocomplete
                      multiple
            fullWidth
                      options={allCapDevices}
                      value={segmentEditCaps}
                      onChange={(event, newValue) => setSegmentEditCaps(newValue)}
                      getOptionLabel={(option) => option.name || option.serial_number}
                      isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="CAP Devices"
                          placeholder="Select CAP devices"
            size="small"
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={segmentDeviceChipLabel(option)}
                            {...getTagProps({ index })}
                            size="small"
                            color="secondary"
                          />
                        ))
                      }
                      renderOption={(props, option) => renderSegmentDeviceOption(props, option)}
                      sx={{ mb: 2 }}
                    />
                    
                    <Stack spacing={1.5}>
            <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        fullWidth
              startIcon={(addToCustomTreeLoading || removeFromCustomTreeLoading) ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                        onClick={() => handleUpdateSegmentDevices(tree)}
                        disabled={
                          addToCustomTreeLoading || 
                          removeFromCustomTreeLoading || 
                          segmentEditNodes.length === 0 || 
                          segmentEditCaps.length === 0 ||
                          (
                            JSON.stringify([...(tree.devices || [])].sort()) === JSON.stringify(segmentEditNodes.map(d => d.serial_number).sort()) &&
                            JSON.stringify([...(tree.caps || [])].sort()) === JSON.stringify(segmentEditCaps.map(d => d.serial_number).sort())
                          )
                        }
                        sx={{ py: 1 }}
                      >
                        {(addToCustomTreeLoading || removeFromCustomTreeLoading) ? 'Updating Segment…' : 'Update Segment'}
            </Button>
            <Button
                        variant="contained"
              color="error"
                        size="medium"
                        fullWidth
              startIcon={deleteCustomTreeLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                        onClick={() => handleDeleteCustomTree(tree)}
                        disabled={deleteCustomTreeLoading}
                        sx={{ py: 1 }}
            >
                        {deleteCustomTreeLoading ? 'Deleting Segment…' : 'Delete Segment'}
            </Button>
          </Stack>
        </Box>
                </Collapse>
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
          onClick={() => {
            setSegmentsDialogOpen(false);
            setSelectedTree(null);
          }} 
              variant="outlined"
              fullWidth
            >
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* iPerf Instructions Dialog */}
    <Dialog
      open={iperfDialogOpen}
      onClose={() => setIperfDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      container={mapDialogContainer}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        background: MAP_UI.headerGradient,
        color: 'white',
        fontWeight: 700
      }}>
        iPerf Network Test
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Run an iPerf network performance test between two devices.
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Instructions:
        </Typography>
          <Typography variant="body2" component="div">
            1. Click <strong>Start Selection</strong> below<br/>
            2. Click on the <strong>first device</strong> on the map (source/client)<br/>
            3. Click on the <strong>second device</strong> on the map (destination/server)<br/>
            4. Review and click <strong>Run Test</strong>
        </Typography>
        </Alert>

        <Typography variant="body2" color="text.secondary">
          The test will run UDP traffic at 2000 Mbps for 20 seconds. Selected devices will be highlighted on the map.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
          onClick={() => setIperfDialogOpen(false)}
              variant="outlined"
            >
          Cancel
            </Button>
        <Button
          onClick={() => {
            setIperfDialogOpen(false);
            setIperfSelectionMode(true);
            setIperfFromDevice(null);
            setIperfToDevice(null);
          }}
          variant="contained"
          sx={{
            background: MAP_UI.headerGradient,
            '&:hover': {
              background: MAP_UI.headerGradientHover,
            }
          }}
        >
          Start Selection
        </Button>
      </DialogActions>
    </Dialog>

    <AcceptAllClientsConfirmDialog
      open={Boolean(acceptAllConfirm)}
      enabling={acceptAllConfirm ? !acceptAllConfirm.enabled : false}
      targetLabel={acceptAllConfirm?.targetLabel || ''}
      onCancel={() => setAcceptAllConfirm(null)}
      onConfirm={() => {
        const pending = acceptAllConfirm;
        setAcceptAllConfirm(null);
        if (pending) {
          handleToggleApFailsafeRadio(pending.device, pending.treeId, pending.enabled);
        }
      }}
    />

    {/* Neighbors scan (map selection / RRM tab) */}
    <Dialog
      open={selectionNeighborsScanConfirmOpen}
      onClose={() => !selectionNeighborsScanLoading && setSelectionNeighborsScanConfirmOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Confirm Neighbors Scan</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Start neighbors scan on {selectionNeighborsScanTargets.length} connected jnet2 device
          {selectionNeighborsScanTargets.length === 1 ? '' : 's'} from your map selection
          ({selectionNeighborsScanInterval}s per channel,{' '}
          {selectionNeighborsScanAllFreq ? 'all channels / bands' : 'known channels from fleet config only'}).
        </DialogContentText>
        {selectedDevices.length > selectionNeighborsScanTargets.length && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {selectedDevices.length - selectionNeighborsScanTargets.length} selected device
            {selectedDevices.length - selectionNeighborsScanTargets.length === 1 ? ' is' : 's are'} offline
            or not jnet2 and will be skipped.
          </Alert>
        )}
        <DialogContentText variant="body2" color="text.secondary">
          {describeNeighborScanBatch(selectionNeighborsScanBatch)} A tracker opens when the scan starts; reports appear
          in Neighbors / map overlays as devices finish.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setSelectionNeighborsScanConfirmOpen(false)}
          color="inherit"
          disabled={selectionNeighborsScanLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmNeighborsScanOnSelection}
          disabled={selectionNeighborsScanLoading}
          startIcon={selectionNeighborsScanLoading ? <CircularProgress size={16} color="inherit" /> : <RadarIcon />}
        >
          Start scan
        </Button>
      </DialogActions>
    </Dialog>

    {/* iPerf Confirmation Dialog */}
    <Dialog
      open={iperfConfirmDialogOpen}
      onClose={() => {
        if (!iperfRunning) {
          setIperfConfirmDialogOpen(false);
        }
      }}
      maxWidth="sm"
      fullWidth
      container={mapDialogContainer}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        background: MAP_UI.headerGradient,
        color: 'white',
        fontWeight: 700
      }}>
        {iperfRunning ? 'Running iPerf Test...' : iperfResults ? 'iPerf Test Results' : 'Confirm iPerf Test'}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {!iperfRunning && !iperfResults && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ready to run iPerf test between the selected devices:
            </Typography>
            
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Card variant="outlined" sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    From (Client - Sender):
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {iperfFromDevice?.name || iperfFromDevice?.serial_number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {iperfFromDevice?.serial_number}
                  </Typography>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ bgcolor: MAP_UI.cardTint }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    To (Server - Receiver):
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {iperfToDevice?.name || iperfToDevice?.serial_number}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {iperfToDevice?.serial_number} • {iperfToDevice?.peer_address || 'No IP'}
                  </Typography>
                </CardContent>
              </Card>
            </Stack>

            <Alert severity="info">
              <Typography variant="body2">
                Test will run UDP traffic at 2000 Mbps for 20 seconds.
              </Typography>
            </Alert>
          </>
        )}

        {/* Progress Indicator */}
        {iperfRunning && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Running iPerf test... This may take up to 30 seconds
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Starting server and client, waiting for results...
            </Typography>
          </Box>
        )}

        {/* Results Display */}
        {iperfResults && !iperfRunning && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                From: {iperfResults.fromDevice.name || iperfResults.fromDevice.serial_number}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                To: {iperfResults.toDevice.name || iperfResults.toDevice.serial_number}
              </Typography>
      </Box>

            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Test Results:
              </Typography>
              <Box component="pre" sx={{ 
                fontSize: '0.75rem', 
                overflow: 'auto', 
                maxHeight: '300px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {JSON.stringify(iperfResults.client, null, 2)}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={() => {
            setIperfConfirmDialogOpen(false);
            setIperfSelectionMode(false);
            setIperfFromDevice(null);
            setIperfToDevice(null);
            setIperfResults(null);
          }}
          variant="outlined"
          disabled={iperfRunning}
        >
          {iperfResults ? 'Close' : 'Cancel'}
        </Button>
        {!iperfResults && (
          <Button
            onClick={handleRunIperf}
            variant="contained"
            disabled={iperfRunning}
            sx={{
              background: MAP_UI.headerGradient,
              '&:hover': {
                background: MAP_UI.headerGradientHover,
              }
            }}
          >
            {iperfRunning ? 'Running...' : 'Run Test'}
          </Button>
        )}
      </DialogActions>
    </Dialog>

    {/* iPerf View – select a historical test run to overlay on the map */}
    <Dialog
      open={iperfViewDialogOpen}
      onClose={() => setIperfViewDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      container={mapDialogContainer}
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
    >
      <DialogTitle sx={{
        pb: 2,
        background: MAP_UI.headerGradient,
        color: 'white',
        fontWeight: 700,
      }}>
        View iPerf Test Results
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {iperfTestRunsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: MAP_UI.accent }} />
          </Box>
        )}

        {!iperfTestRunsLoading && iperfTestRuns.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No iPerf test runs found. Run a tree-wide iPerf test from the Optimize → iPerf Test tab first.
          </Alert>
        )}

        {!iperfTestRunsLoading && iperfTestRuns.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Select a completed test run to overlay its results on the map.
            </Typography>
            <List dense disablePadding>
              {iperfTestRuns.map((run) => {
                const isSelected = iperfViewRun?.id === run.id;
                const statusColor = run.status === 'completed' ? 'success.main'
                  : run.status === 'failed' ? 'error.main'
                  : run.status === 'running' ? 'warning.main'
                  : 'text.secondary';
                return (
                  <ListItem
                    key={run.id}
                    disablePadding
                    divider
                    secondaryAction={
                      <Chip
                        label={run.status}
                        size="small"
                        sx={{ fontSize: '0.7rem', color: statusColor, borderColor: statusColor, bgcolor: 'transparent' }}
                        variant="outlined"
                      />
                    }
                  >
                    <ListItemButton
                      selected={isSelected}
                      disabled={run.status !== 'completed'}
                      onClick={async () => {
                        setIperfViewRunLoading(true);
                        try {
                          const detail = await api.getIperfTestRun(run.id);
                          setIperfViewRun(detail);
                          setIperfViewDialogOpen(false);
                        } catch (_) {
                          /* ignore */
                        } finally {
                          setIperfViewRunLoading(false);
                        }
                      }}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 500 }}>
                            {run.name || run.id.slice(0, 8)}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {run.cap_serial} · {run.created_at ? new Date(run.created_at).toLocaleString() : ''}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}

        {iperfViewRunLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={18} sx={{ color: MAP_UI.accent }} />
            <Typography variant="caption">Loading run details…</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {iperfViewRun && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => { setIperfViewRun(null); setIperfViewDialogOpen(false); }}
          >
            Clear Overlay
          </Button>
        )}
        <Button variant="outlined" onClick={() => setIperfViewDialogOpen(false)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Access List Management Dialog */}
    <Dialog open={accessListDialogOpen && !mapSelectionMode} onClose={handleCloseAccessListDialog} maxWidth="md" fullWidth>
      <DialogTitle>
        Manage Access Lists - {deviceForAccessList?.name || deviceForAccessList?.serial_number}
      </DialogTitle>
      <DialogContent>
        {accessListUpdateStatus.message && (
          <Alert severity={accessListUpdateStatus.type} sx={{ mb: 2 }}>
            {accessListUpdateStatus.message}
          </Alert>
        )}
        
        {accessListLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading access list...
            </Typography>
          </Box>
        )}
        
        {!accessListLoading && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Whitelist Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
              Whitelist (Allowed Devices)
            </Typography>
            <FormControl fullWidth size="medium">
              <InputLabel id="whitelist-label">Select Whitelisted Devices</InputLabel>
              <Select
                labelId="whitelist-label"
                multiple
                value={selectedWhitelist}
                onChange={handleWhitelistChange}
                label="Select Whitelisted Devices"
                sx={{
                  minHeight: '56px',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    padding: '8px 14px',
                  }
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No devices selected
                      </Typography>
                    ) : (
                      selected.map((value) => {
                        const device = devices.find(d => d.serial_number === value);
                        return (
                          <Chip
                            key={value}
                            label={device?.name || value}
                            color="success"
                            size="small"
                            onDelete={() => {
                              setSelectedWhitelist(prev => {
                                const newList = prev.filter(id => id !== value);
                                // Update visual markers on map
                                setHighlightedAccessList(current => ({
                                  ...current,
                                  whitelist: newList
                                }));
                                return newList;
                              });
                            }}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                          />
                        );
                      })
                    )}
                  </Box>
                )}
              >
                {devices.filter(d => {
                  if (!d || !d.serial_number) return false;
                  const isNotCurrentDevice = d.serial_number.toLowerCase() !== deviceForAccessList?.serial_number?.toLowerCase();
                  const isJnet2Compatible = d.compatible === 'jnet2';
                  return isNotCurrentDevice && isJnet2Compatible;
                }).map((device) => (
                  <MenuItem key={device.serial_number} value={device.serial_number}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          ...statusDotSx(device.connected),
                        }}
                      />
                      <Typography variant="body2" noWrap>
                        {device.name || device.serial_number}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="success"
              fullWidth
              sx={{ mt: 2 }}
              startIcon={<MapIcon />}
              onClick={() => {
                setMapSelectionMode('whitelist');
              }}
              disabled={mapSelectionMode === 'whitelist'}
            >
              {mapSelectionMode === 'whitelist' ? 'Selecting from Map...' : 'Select from Map'}
            </Button>
          </Grid>

          {/* Blacklist Section */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom sx={{ color: 'error.main', mb: 2 }}>
              Blacklist (Blocked Devices)
            </Typography>
            <FormControl fullWidth size="medium">
              <InputLabel id="blacklist-label">Select Blacklisted Devices</InputLabel>
              <Select
                labelId="blacklist-label"
                multiple
                value={selectedBlacklist}
                onChange={handleBlacklistChange}
                label="Select Blacklisted Devices"
                sx={{
                  minHeight: '56px',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    padding: '8px 14px',
                  }
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No devices selected
                      </Typography>
                    ) : (
                      selected.map((value) => {
                        const device = devices.find(d => d.serial_number === value);
                        return (
                          <Chip
                            key={value}
                            label={device?.name || value}
                            color="error"
                            size="small"
                            onDelete={() => {
                              setSelectedBlacklist(prev => {
                                const newList = prev.filter(id => id !== value);
                                // Update visual markers on map
                                setHighlightedAccessList(current => ({
                                  ...current,
                                  blacklist: newList
                                }));
                                return newList;
                              });
                            }}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                          />
                        );
                      })
                    )}
                  </Box>
                )}
              >
                {devices.filter(d => {
                  if (!d || !d.serial_number) return false;
                  const isNotCurrentDevice = d.serial_number.toLowerCase() !== deviceForAccessList?.serial_number?.toLowerCase();
                  const isJnet2Compatible = d.compatible === 'jnet2';
                  return isNotCurrentDevice && isJnet2Compatible;
                }).map((device) => (
                  <MenuItem key={device.serial_number} value={device.serial_number}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          ...statusDotSx(device.connected),
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" noWrap>
                        {device.name || device.serial_number}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              sx={{ mt: 2 }}
              startIcon={<MapIcon />}
              onClick={() => {
                setMapSelectionMode('blacklist');
              }}
              disabled={mapSelectionMode === 'blacklist'}
            >
              {mapSelectionMode === 'blacklist' ? 'Selecting from Map...' : 'Select from Map'}
            </Button>
          </Grid>
        </Grid>
        )}

        {(mapSelectionMode || (highlightedAccessList.whitelist.length > 0 || highlightedAccessList.blacklist.length > 0)) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {mapSelectionMode && (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {mapSelectionMode === 'whitelist' ? '✅ Whitelist Selection Mode' : '🚫 Blacklist Selection Mode'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Click on devices on the map to add them to the {mapSelectionMode}. Click "Done" in the banner above or click elsewhere on the map to exit selection mode.
                </Typography>
              </>
            )}
            <Typography variant="body2" sx={{ fontWeight: 600, mt: mapSelectionMode ? 1 : 0 }}>
              Map Legend:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: `3px solid ${MAP_UI.deviceOnlineBorder}` }} />
                <Typography variant="caption">Whitelist</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: `3px solid ${MAP_UI.deviceOfflineBorder}` }} />
                <Typography variant="caption">Blacklist</Typography>
              </Box>
            </Box>
          </Alert>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Whitelist:</strong> Only these devices can connect as children (empty = all allowed)
            <br />
            <strong>Blacklist:</strong> These devices are explicitly prevented from connecting as children
            <br />
            <strong>Tip:</strong> Use "Select from Map" buttons to visually pick devices from the map view
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseAccessListDialog}>Cancel</Button>
        <Button onClick={handleSetAccessList} variant="contained" color="primary">
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={heardByDialogOpen}
      onClose={() => setHeardByDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      container={mapDialogContainer}
    >
      <DialogTitle>
        Who heard {heardByTarget?.name || heardByTarget?.serial_number || 'device'}?
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Scans all device neighbor reports and highlights every device whose STA-side measurement
          of the selected device meets the minimum RSSI. Arrows point from hearer → selected device.
        </DialogContentText>
        <TextField
          fullWidth
          type="number"
          label="Minimum STA RSSI (dBm)"
          value={heardByMinRssi}
          onChange={(e) => setHeardByMinRssi(e.target.value)}
          helperText="How loudly each scanner device heard the selected device (e.g. -75)"
          sx={{ mb: 2 }}
          inputProps={{ step: 1 }}
        />
        {heardByOverlayActive && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing {heardByLinks.length} hearer(s) for{' '}
            {heardByTarget?.name || heardByTarget?.serial_number} at STA RSSI ≥ {heardByMinRssi} dBm.
            Gold marker = target, teal = hearers.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={handleClearHeardBy} color="inherit">
          Clear overlay
        </Button>
        <Button onClick={() => setHeardByDialogOpen(false)} color="inherit">
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={handleApplyHeardBy}
          disabled={loadingHeardBy || !heardByTarget}
          startIcon={loadingHeardBy ? <CircularProgress size={16} /> : <HearingIcon />}
        >
          {loadingHeardBy ? 'Scanning…' : 'Show on map'}
        </Button>
        <Button
          variant="contained"
          startIcon={<AccountTreeIcon />}
          onClick={handleOpenHeardBySegmentDialog}
          disabled={heardByLinks.length === 0}
        >
          Create segment
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={subtreeSegmentDialogOpen}
      onClose={() => {
        setSubtreeSegmentDialogOpen(false);
        setSubtreeSegmentConfigBySerial(new Map());
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Create segment from subtree
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Build a custom tree from{' '}
          <strong>{subtreeSegmentTarget?.name || subtreeSegmentTarget?.serial_number}</strong>
          {' '}and its descendants. The selected device is always included; filters apply to
          children only.
        </DialogContentText>
        <TextField
          fullWidth
          label="Segment name"
          value={subtreeSegmentName}
          onChange={(e) => setSubtreeSegmentName(e.target.value)}
          placeholder="e.g. Building A east wing"
          sx={{ mb: 2 }}
        />
        <FormControlLabel
          control={(
            <Checkbox
              checked={subtreeSegmentConnectedOnly}
              onChange={(e) => setSubtreeSegmentConnectedOnly(e.target.checked)}
            />
          )}
          label="Connected devices only (descendants)"
          sx={{ mb: 1, display: 'block' }}
        />
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>RSSI filter (descendants)</InputLabel>
          <Select
            value={subtreeSegmentSignalFilter}
            label="RSSI filter (descendants)"
            onChange={(e) => setSubtreeSegmentSignalFilter(e.target.value)}
          >
            <MenuItem value="all">None</MenuItem>
            <MenuItem value="ap">AP (parent hears child)</MenuItem>
            <MenuItem value="sta">STA (child hears parent)</MenuItem>
            <MenuItem value="both">Both AP and STA</MenuItem>
          </Select>
        </FormControl>
        {subtreeSegmentSignalFilter !== 'all' && (
          <TextField
            fullWidth
            label="Minimum RSSI (dBm)"
            value={subtreeSegmentMinRssi}
            onChange={(e) => setSubtreeSegmentMinRssi(e.target.value)}
            helperText="Descendants must meet this backhaul RSSI threshold on the selected direction(s)"
            sx={{ mb: 2 }}
            type="number"
            inputProps={{ max: 0, step: 1 }}
          />
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleApplySubtreeSegmentFilters}
            disabled={loadingSubtreeSegmentConfig}
            startIcon={loadingSubtreeSegmentConfig ? <CircularProgress size={14} /> : null}
          >
            {loadingSubtreeSegmentConfig ? 'Loading configs…' : 'Apply filters'}
          </Button>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Subtree has {subtreeSegmentPreview.descendantCount} descendant(s);{' '}
          {subtreeSegmentPreview.includedDescendantCount} pass the current filters
          (plus the selected parent). Click <strong>Apply filters</strong> after changing
          filters to refresh the device lists.
          {countBlockedAPSegmentSlots(subtreeSegmentNodes, subtreeSegmentConfigBySerial) > 0 && (
            <>
              {' '}
              {countBlockedAPSegmentSlots(subtreeSegmentNodes, subtreeSegmentConfigBySerial)} device(s)
              have at least one treemgmt entry with <code>max_children=0</code>; those entries
              receive <code>segment_id_dev</code> only (no <code>segment_id_ap</code> on that slot).
            </>
          )}
        </Alert>
        <Autocomplete
          multiple
          fullWidth
          options={allNodeDevices}
          value={subtreeSegmentNodes}
          onChange={(event, newValue) => setSubtreeSegmentNodes(newValue)}
          getOptionLabel={(option) => option.name || option.serial_number}
          isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Node devices"
              placeholder="Adjust node list"
              helperText="Updated when you click Apply filters; manual edits are kept until then"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const isRoot = subtreeSegmentTarget?.serial_number === option.serial_number;
              return (
                <Chip
                  label={isRoot ? `${option.name || option.serial_number} (subtree root)` : (option.name || option.serial_number)}
                  {...getTagProps({ index })}
                  size="small"
                  color={isRoot ? 'warning' : 'primary'}
                />
              );
            })
          }
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          fullWidth
          options={allCapDevices}
          value={subtreeSegmentCaps}
          onChange={(event, newValue) => setSubtreeSegmentCaps(newValue)}
          getOptionLabel={(option) => option.name || option.serial_number}
          isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
          renderInput={(params) => (
            <TextField
              {...params}
              label="CAP devices"
              placeholder="Adjust CAP list"
              helperText="Resolved from included nodes and subtree root"
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setSubtreeSegmentDialogOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={creatingSubtreeSegment ? <CircularProgress size={16} color="inherit" /> : <AccountTreeIcon />}
          onClick={handleCreateSubtreeSegment}
          disabled={
            creatingSubtreeSegment
            || !subtreeSegmentName.trim()
            || subtreeSegmentNodes.length === 0
            || subtreeSegmentCaps.length === 0
          }
        >
          {creatingSubtreeSegment ? 'Creating…' : 'Create segment'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={heardBySegmentDialogOpen}
      onClose={() => setHeardBySegmentDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Create segment from heard-by set
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Pre-filled from the heard-by overlay for{' '}
          <strong>{heardByTarget?.name || heardByTarget?.serial_number}</strong>
          {' '}and {heardByLinks.length} hearer(s). Edit the device lists before creating the segment.
        </DialogContentText>
        <TextField
          fullWidth
          label="Segment name"
          value={heardByTreeName}
          onChange={(e) => setHeardByTreeName(e.target.value)}
          placeholder="e.g. Building A heard cluster"
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          fullWidth
          options={allNodeDevices}
          value={heardBySegmentNodes}
          onChange={(event, newValue) => setHeardBySegmentNodes(newValue)}
          getOptionLabel={(option) => option.name || option.serial_number}
          isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Node devices"
              placeholder="Select Node devices"
              helperText="Includes hearers and the heard-by target when it is a Node"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const isTarget = heardByTarget?.serial_number === option.serial_number;
              return (
                <Chip
                  label={isTarget ? `${option.name || option.serial_number} (heard-by target)` : (option.name || option.serial_number)}
                  {...getTagProps({ index })}
                  size="small"
                  color={isTarget ? 'warning' : 'primary'}
                />
              );
            })
          }
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">
                  {option.name || option.serial_number}
                  {heardByTarget?.serial_number === option.serial_number ? ' (heard-by target)' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.serial_number}
                  {option.connected ? ' • Online' : ' • Offline'}
                </Typography>
              </Box>
            </Box>
          )}
          sx={{ mb: 2 }}
        />
        <Autocomplete
          multiple
          fullWidth
          options={allCapDevices}
          value={heardBySegmentCaps}
          onChange={(event, newValue) => setHeardBySegmentCaps(newValue)}
          getOptionLabel={(option) => option.name || option.serial_number}
          isOptionEqualToValue={(option, value) => option.serial_number === value.serial_number}
          renderInput={(params) => (
            <TextField
              {...params}
              label="CAP devices"
              placeholder="Select CAP devices"
              helperText="Resolved from selected nodes and included when the heard-by target is a CAP"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const isTarget = heardByTarget?.serial_number === option.serial_number;
              return (
                <Chip
                  label={isTarget ? `${option.name || option.serial_number} (heard-by target)` : (option.name || option.serial_number)}
                  {...getTagProps({ index })}
                  size="small"
                  color={isTarget ? 'warning' : 'secondary'}
                />
              );
            })
          }
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">
                  {option.name || option.serial_number}
                  {heardByTarget?.serial_number === option.serial_number ? ' (heard-by target)' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.serial_number}
                  {option.connected ? ' • Online' : ' • Offline'}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setHeardBySegmentDialogOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={creatingHeardBySegment ? <CircularProgress size={16} color="inherit" /> : <AccountTreeIcon />}
          onClick={handleCreateHeardBySegment}
          disabled={
            creatingHeardBySegment
            || !heardByTreeName.trim()
            || heardBySegmentNodes.length === 0
            || heardBySegmentCaps.length === 0
          }
        >
          {creatingHeardBySegment ? 'Creating…' : 'Create segment'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Assign public WiFi SSID profile (single device from map popup; CAP requires tree_id) */}
    <Dialog
      open={assignSsidDialogOpen}
      onClose={(_, reason) => {
        if (
          assignSsidDialogSuppressCloseRef.current
          || assignSsidSaving
          || ssidUnassignInProgress
        ) {
          return;
        }
        if (reason === 'escapeKeyDown') {
          setAssignSsidDialogOpen(false);
          setAssignSsidDevice(null);
          setAssignSsidId('');
          setAssignSsidTreeId('');
          setAssignSsidCapTreeIds([]);
        }
      }}
      disableBackdropClick
      disableEscapeKeyDown={assignSsidSaving || ssidUnassignInProgress}
      maxWidth="sm"
      fullWidth
      container={mapDialogContainer}
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
    >
      <DialogTitle sx={{
        pb: 2,
        background: MAP_UI.headerGradient,
        color: 'white',
        fontWeight: 700,
      }}>
        Public WiFi SSID
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign or change the public WiFi SSID profile for{' '}
          <strong>{assignSsidDevice?.name || assignSsidDevice?.serial_number || 'device'}</strong>.
        </Typography>
        {assignSsidDevice?.name && assignSsidDevice?.serial_number && (
          <Typography
            variant="caption"
            sx={{ display: 'block', fontFamily: 'monospace', mb: 2, color: 'text.secondary' }}
          >
            {assignSsidDevice.serial_number}
          </Typography>
        )}
        {assignSsidDevice?.compatible && assignSsidDevice.compatible !== 'jnet2' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Device type &quot;{assignSsidDevice.compatible}&quot; may not support public WiFi SSID apply.
          </Alert>
        )}
        {!ssidProfilesLoading && ssidsAssignedToDevice(ssidProfiles, assignSsidDevice).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Currently assigned
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {assignedSsidClientSummaries(
                assignSsidDevice,
                ssidsAssignedToDevice(ssidProfiles, assignSsidDevice),
              ).map(({ profile, label, count }) => (
                <Chip
                  key={profile.id}
                  icon={<WifiIcon sx={{ fontSize: '0.85rem !important' }} />}
                  label={`${label}: ${count}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={(e) => handleChipUnassignSsid(e, profile.id, assignSsidDevice)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    assignSsidDialogSuppressCloseRef.current = true;
                  }}
                  disabled={assignSsidSaving || ssidUnassignInProgress}
                />
              ))}
            </Stack>
          </Box>
        )}
        {ssidProfilesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} sx={{ color: MAP_UI.accent }} />
          </Box>
        ) : (
          <>
            <FormControl fullWidth>
              <InputLabel id="map-assign-ssid-label">SSID profile</InputLabel>
              <Select
                labelId="map-assign-ssid-label"
                label="SSID profile"
                value={assignSsidId}
                onChange={(e) => setAssignSsidId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Choose a profile…</em>
                </MenuItem>
                {ssidProfiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {formatSsidProfileOptionLabel(p, vlanProfiles)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {assignSsidNeedsCapTreeSelect && (
              <Box sx={{ mt: 2 }}>
                {assignSsidConfigLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={22} sx={{ color: MAP_UI.accent }} />
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel id="map-assign-cap-tree-label">CAP radio slot</InputLabel>
                    <Select
                      labelId="map-assign-cap-tree-label"
                      label="CAP radio slot"
                      value={assignSsidTreeId}
                      onChange={(e) => setAssignSsidTreeId(e.target.value)}
                    >
                      {assignSsidCapTreeIds.map((treeId) => (
                        <MenuItem key={treeId} value={treeId}>
                          {treeId}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Assigning Public WiFi on this slot sets treemgmt <code>max_children</code> to 0
                  on <strong>{assignSsidTreeId || 'the selected slot'}</strong>, blocking mesh children
                  on that radio.
                </Alert>
              </Box>
            )}
          </>
        )}
        {!ssidProfilesLoading && ssidProfiles.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No SSID profiles found. Create one under Groups &amp; SSIDs first.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          type="button"
          onClick={() => {
            setAssignSsidDialogOpen(false);
            setAssignSsidDevice(null);
            setAssignSsidId('');
            setAssignSsidTreeId('');
            setAssignSsidCapTreeIds([]);
          }}
          variant="outlined"
          disabled={assignSsidSaving || ssidUnassignInProgress}
        >
          Close
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={handleAssignSsidToDevice}
          disabled={
            assignSsidSaving
            || ssidUnassignInProgress
            || !assignSsidId
            || ssidProfilesLoading
            || assignSsidConfigLoading
            || (assignSsidNeedsCapTreeSelect && !assignSsidTreeId)
          }
          sx={{
            background: MAP_UI.headerGradient,
            '&:hover': { background: MAP_UI.headerGradientHover },
          }}
        >
          {assignSsidSaving ? 'Applying…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>

    <DeviceMapGroupDialog
      open={groupDialogOpen}
      onClose={handleCloseGroupDialog}
      device={groupDialogDevice}
      allDevices={deviceCatalog}
      onGroupsChanged={handleGroupDialogChanged}
    />

  </Paper>
  );
};

export default DeviceMapView;

